import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Lottie from 'lottie-react';
import { siteBuilderAPI } from '../lib/api';
import { SiteFlashBadge } from '../components/SiteFlashLogo';
import TemplateEditor from '../components/TemplateEditor';
import BookingModal from '../components/BookingModal';
import BookingModalV2 from '../components/BookingModalV2';
import ClientLoginModal from '../components/ClientLoginModal';
import PricingTabsSection from '../components/site/PricingTabsSection';
import PhotoboothPresentationPage from '../components/site/PhotoboothPresentationPage';
import VideoBooth360PresentationPage from '../components/site/VideoBooth360PresentationPage';
import LivreDorPresentationPage from '../components/site/LivreDorPresentationPage';
import ModelesPage from '../components/site/ModelesPage';
import TarifsPage from '../components/site/TarifsPage';
import RealisationsPage from '../components/site/RealisationsPage';
import FormulasPage from './site/FormulasPage';
import { 
  FiChevronDown, FiChevronRight, FiChevronLeft, FiMenu, FiX, FiUser, FiPhone, FiMail,
  FiVolume2, FiCalendar, FiStar, FiArrowRight, FiArrowLeft, FiInstagram,
  FiFacebook, FiArrowUp, FiShare2, FiHeart, FiMessageSquare, FiClock,
  FiUpload, FiImage, FiCheck, FiDownload, FiShield, FiInfo, FiAlertCircle,
  FiGrid, FiMaximize, FiEdit3, FiMapPin, FiPlay, FiCopy, FiExternalLink, FiGift, FiSettings, FiLock, FiZap, FiPackage, FiPlus, FiAward
} from 'react-icons/fi';
import { FaSnapchatGhost, FaTiktok, FaYoutube } from 'react-icons/fa';

// Helper function to get API URL - uses relative path in production, full URL in preview
const getApiUrl = () => {
  const hostname = window.location.hostname;
  // If we're on the production domain, use relative URLs
  if (hostname.includes('justasmile-events.com') || hostname.includes('app.justasmile')) {
    return '';  // Relative path - will use same domain
  }
  // Otherwise use the configured backend URL (for preview/development)
  return process.env.REACT_APP_BACKEND_URL || '';
};

const API_URL = getApiUrl();

// Helper component to render rich text HTML content safely
// Handles content that comes from the RichTextEditor (contains HTML like <strong>, <p>, etc.)
const RichTextContent = ({ content, className = '' }) => {
  if (!content) return null;
  
  // Check if content contains HTML tags
  const hasHtmlTags = /<[^>]+>/.test(content);
  
  if (hasHtmlTags) {
    return (
      <div 
        className={`rich-text-content ${className}`}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }
  
  // Plain text - render as-is
  return <p className={className}>{content}</p>;
};

const PreviewPage = ({ standaloneMode = false }) => {
  const { siteId, siteSlug, pageSlug: routePageSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Utility function to parse text with golden highlights
  // Usage: "Text with {{highlighted words}} in gold"
  const parseHighlightedText = (text, goldColor = '#D4AF37') => {
    if (!text) return null;
    const parts = text.split(/(\{\{[^}]+\}\})/g);
    return parts.map((part, i) => {
      if (part.startsWith('{{') && part.endsWith('}}')) {
        const highlightedText = part.slice(2, -2);
        return (
          <span key={i} style={{ color: goldColor, fontWeight: '600' }}>
            {highlightedText}
          </span>
        );
      }
      return part;
    });
  };
  
  // Detect if we're in an iframe (e.g., Constructor preview)
  const isInIframe = typeof window !== 'undefined' && window.self !== window.top;
  
  const [site, setSite] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hoveredMenu, setHoveredMenu] = useState(null);
  // Default to expanded "Nos Prestations" menu (index 1) on mobile
  const [expandedMobileMenu, setExpandedMobileMenu] = useState(1);
  const [expandedMobileChild, setExpandedMobileChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [selectedPrestationForBooking, setSelectedPrestationForBooking] = useState(null);
  const [selectedDurationForBooking, setSelectedDurationForBooking] = useState(null);
  const [selectedFormulaForBooking, setSelectedFormulaForBooking] = useState(null);
  const [bookingStep, setBookingStep] = useState(1);
  const [bookingData, setBookingData] = useState({});
  const [showFloatingMenu, setShowFloatingMenu] = useState(false);
  // Floating menu modals
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [callbackData, setCallbackData] = useState({ name: '', phone: '', preferredTime: '' });
  const [reviewData, setReviewData] = useState({ name: '', rating: 5, comment: '' });
  const [callbackSent, setCallbackSent] = useState(false);
  const [reviewSent, setReviewSent] = useState(false);
  // Client Login Modal
  const [showClientLoginModal, setShowClientLoginModal] = useState(false);
  const [clientLoginData, setClientLoginData] = useState({ email: '', password: '' });
  const [clientLoginError, setClientLoginError] = useState('');
  const [clientLoginLoading, setClientLoginLoading] = useState(false);
  const [isClientLoggedIn, setIsClientLoggedIn] = useState(false);
  const [clientUser, setClientUser] = useState(null);
  // Forgot Password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordResult, setForgotPasswordResult] = useState(null);
  // Deployment info state
  const [deploymentInfo, setDeploymentInfo] = useState({ version: 'V.9', deployed_at: '—' });
  // Photo Preview Feature
  const [showPhotoPreview, setShowPhotoPreview] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [uploadedPhoto, setUploadedPhoto] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [photoDimensions, setPhotoDimensions] = useState(null);
  const [photoQuality, setPhotoQuality] = useState(null);
  // Model Lightbox state
  const [showModelLightbox, setShowModelLightbox] = useState(false);
  const [lightboxModel, setLightboxModel] = useState(null);
  const [lightboxModels, setLightboxModels] = useState([]);
  const [lightboxModelIndex, setLightboxModelIndex] = useState(0);
  // Gallery Lightbox state
  const [showGalleryLightbox, setShowGalleryLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  // Hotspots state
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [hotspotsConfig, setHotspotsConfig] = useState(null); // Will be loaded from DB or use defaults
  // FAQ accordion state
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  
  // Reviews Carousel state
  const [desktopReviewIndex, setDesktopReviewIndex] = useState(0);
  const [mobileReviewIndex, setMobileReviewIndex] = useState(0);
  
  // Partner Logos Carousel state
  const [partnerLogoIndex, setPartnerLogoIndex] = useState(0);
  
  // Top Banner state
  const [topBannerClosed, setTopBannerClosed] = useState(false);
  const [promoCopied, setPromoCopied] = useState(false);
  
  // SEO Pages state
  const [seoPage, setSeoPage] = useState(null);
  const [loadingSeoPage, setLoadingSeoPage] = useState(false);
  const [publishedSeoPages, setPublishedSeoPages] = useState([]); // Pages SEO publiées depuis la DB
  
  // Blog Article state
  const [blogArticle, setBlogArticle] = useState(null);
  const [loadingBlogArticle, setLoadingBlogArticle] = useState(false);
  
  // Google Reviews state - loaded from API
  const [googleReviews, setGoogleReviews] = useState([]);
  const [googleReviewsInfo, setGoogleReviewsInfo] = useState({ total: 0, average: 4.9 });
  const [ratingAnimation, setRatingAnimation] = useState(null);
  
  // Products for dynamic services section
  const [productsForServices, setProductsForServices] = useState([]);
  
  // Template Editor States
  const [editorMode, setEditorMode] = useState('preview'); // 'preview' or 'editor'
  const [templateFormat, setTemplateFormat] = useState('paysage'); // 'bandelette', 'paysage', 'portrait'
  const [editorElements, setEditorElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [editorLogo, setEditorLogo] = useState(null);
  const [editorTexts, setEditorTexts] = useState([]);
  const [editorEventDate, setEditorEventDate] = useState('');
  const [editorEventName, setEditorEventName] = useState('');
  const [isSendingTemplate, setIsSendingTemplate] = useState(false);
  const [templateSent, setTemplateSent] = useState(false);
  const [isBookingOpening, setIsBookingOpening] = useState(false);
  const bookingLockRef = React.useRef(false);

  // Gift Card state
  const [giftCardForm, setGiftCardForm] = useState({
    sender_first_name: '',
    sender_last_name: '',
    sender_email: '',
    sender_phone: '',
    recipient_name: '',
    recipient_email: '',
    prestation: 'photobooth',
    amount: 249,
    message: '',
    event_date: ''
  });
  const [giftCardSubmitting, setGiftCardSubmitting] = useState(false);
  const [giftCardSuccess, setGiftCardSuccess] = useState(false);
  
  // Services Carousel state
  const [currentServiceSlide, setCurrentServiceSlide] = useState(0);

  // Stable booking handler with multiple protections against double opening
  const handleBookingClick = React.useCallback((e, prestation = null, duration = null, formulaInfo = null) => {
    // Prevent default and propagation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Multiple protection layers - exit early if any lock is active
    if (showBooking) {
      return;
    }
    
    if (bookingLockRef.current) {
      console.log('Booking lock active');
      return;
    }
    
    // Set lock IMMEDIATELY before any async operations
    bookingLockRef.current = true;
    
    // Set preselected values FIRST, then open modal
    // Use a batch update approach
    const openBookingWithPreselections = () => {
      setSelectedPrestationForBooking(prestation);
      setSelectedDurationForBooking(duration);
      setSelectedFormulaForBooking(formulaInfo);
      setShowBooking(true);
      setBookingStep(1);
      setBookingData({});
    };
    
    // Small delay to allow any pending state updates to complete
    setTimeout(() => {
      openBookingWithPreselections();
      
      // Release lock after modal is definitely shown
      setTimeout(() => {
        bookingLockRef.current = false;
      }, 500);
    }, 50);
    
  }, [showBooking]);

  // Close booking modal properly
  const handleCloseBooking = () => {
    setShowBooking(false);
    setSelectedPrestationForBooking(null);
    setSelectedDurationForBooking(null);
    setSelectedFormulaForBooking(null);
  };

  // Gift card submission handler
  const handleGiftCardSubmit = async (e) => {
    e.preventDefault();
    setGiftCardSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/gift-cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(giftCardForm)
      });
      if (response.ok) {
        setGiftCardSuccess(true);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setGiftCardSubmitting(false);
    }
  };

  // Gift card prestation options
  const prestationOptions = [
    { value: 'photobooth', label: 'Photobooth', minPrice: 149, maxPrice: 399 },
    { value: 'videobooth', label: 'Vidéobooth 360°', minPrice: 199, maxPrice: 549 },
    { value: 'pack', label: 'Pack Photobooth + Vidéobooth', minPrice: 550, maxPrice: 750 }
  ];

  // Track page view on load
  useEffect(() => {
    const trackPageView = async () => {
      if (!site?.id) return;
      try {
        await siteBuilderAPI.trackPageView?.({
          site_id: site.id,
          page_url: window.location.pathname,
          page_title: document.title,
          referrer: document.referrer,
          session_id: sessionStorage.getItem('visitor_session') || (() => {
            const newSession = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            sessionStorage.setItem('visitor_session', newSession);
            return newSession;
          })()
        });
      } catch (e) {
        // Silent fail for analytics
      }
    };
    trackPageView();
  }, [site?.id]);

  useEffect(() => {
    loadSite();
    loadGoogleReviews();
    loadProductsForServices();
    checkClientAuth();
    loadHotspotsConfig();
    loadPublishedSeoPages();
    loadDeploymentInfo();
    
    // Load rating animation JSON
    fetch(`${API_URL}/api/media/file/27ce19d7-750d-4def-bff6-1b53cbc51ebe/rating-animation.json`)
      .then(res => res.json())
      .then(data => setRatingAnimation(data))
      .catch(err => console.log('Rating animation not loaded'));
    
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
      // Close mega menu only on significant scroll (more than 100px from top)
      if (window.scrollY > 100) {
        setHoveredMenu(null);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [siteId]);
  
  // Load deployment info from API
  const loadDeploymentInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/deployment-info`);
      if (response.ok) {
        const data = await response.json();
        setDeploymentInfo(data);
      }
    } catch (err) {
      console.log('Deployment info not loaded');
    }
  };

  // Auto-advance services carousel - get count from gallery section
  useEffect(() => {
    const gallerySection = currentPage?.sections?.find(s => s.type === 'gallery');
    const servicesCount = gallerySection?.content?.highlightedServices?.length || 3;
    const timer = setInterval(() => {
      setCurrentServiceSlide((prev) => (prev + 1) % servicesCount);
    }, 6000);
    return () => clearInterval(timer);
  }, [currentPage]);

  // Load published SEO pages from database
  const loadPublishedSeoPages = async () => {
    try {
      // Try new unified endpoint first
      let response = await fetch(`${API_URL}/api/seo-pages/published`);
      if (response.ok) {
        const pages = await response.json();
        if (pages.length > 0) {
          setPublishedSeoPages(pages);
          return;
        }
      }
      
      // Fallback to old endpoint
      const effectiveSiteId = standaloneMode ? 'justasmile-main' : (siteId || 'default');
      response = await fetch(`${API_URL}/api/seo-assistant/pages/${effectiveSiteId}?published_only=true`);
      if (response.ok) {
        const pages = await response.json();
        setPublishedSeoPages(pages);
      }
    } catch (error) {
      console.log('Could not load SEO pages:', error);
    }
  };

  // Load hotspots config from database
  const loadHotspotsConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/hotspots-config`);
      if (response.ok) {
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          setHotspotsConfig(data);
        }
      }
    } catch (error) {
      console.log('Using default hotspots config');
    }
  };

  // Handle devis/quote button click - scroll to contact section
  const handleDevisClick = () => {
    // Navigate to contact page for quote request
    navigateToPage('contact');
  };

  // Check if client is already logged in
  const checkClientAuth = () => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        setIsClientLoggedIn(true);
        setClientUser(user);
      } catch (e) {
        // Invalid user data
      }
    }
  };

  // Handle client login
  const handleClientLogin = async (e) => {
    e.preventDefault();
    setClientLoginError('');
    setClientLoginLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clientLoginData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.access_token) {
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setIsClientLoggedIn(true);
        setClientUser(data.user);
        setShowClientLoginModal(false);
        setClientLoginData({ email: '', password: '' });
        // Redirect based on role
        const userRole = data.user?.role;
        if (userRole === 'client') {
          window.location.href = '/mon-compte';
        } else {
          window.location.href = '/dashboard';
        }
      } else {
        setClientLoginError(data.detail || 'Email ou mot de passe incorrect');
      }
    } catch (error) {
      setClientLoginError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setClientLoginLoading(false);
    }
  };

  // Handle client logout
  const handleClientLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsClientLoggedIn(false);
    setClientUser(null);
  };

  // Load Google Reviews from API
  const loadGoogleReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/google-reviews/`);
      if (response.ok) {
        const data = await response.json();
        if (data.reviews && data.reviews.length > 0) {
          // Transform API data to match our display format
          const formattedReviews = data.reviews.map(r => ({
            name: r.author_name,
            avatar: r.author_image || r.author_name?.charAt(0)?.toUpperCase() || 'U',
            rating: r.rating,
            date: r.relative_time,
            text: r.text
          }));
          setGoogleReviews(formattedReviews);
          setGoogleReviewsInfo({
            total: data.total_reviews || formattedReviews.length,
            average: data.average_rating || 4.9,
            lastSync: data.last_sync
          });
        }
      }
    } catch (error) {
      console.log('Using fallback reviews - API not available');
      // Keep fallback reviews if API fails
    }
  };
  
  // Load products for dynamic services section
  const loadProductsForServices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/products`);
      if (response.ok) {
        const data = await response.json();
        const productsList = Array.isArray(data) ? data : (data.products || []);
        // Filter active products, exclude carte-cadeau and pack
        const activeProducts = productsList.filter(p => 
          (p.active === true || p.is_active === true) && 
          !['carte-cadeau', 'pack'].includes(p.category)
        );
        setProductsForServices(activeProducts);
      }
    } catch (error) {
      console.log('Error loading products for services section');
    }
  };

  const loadSite = async () => {
    try {
      let response;
      
      // STANDALONE MODE: Load from /api/public/site for Just A Smile Events
      if (standaloneMode) {
        response = await fetch(`${API_URL}/api/public/site`);
        if (response.ok) {
          const data = await response.json();
          // Transform site_content to expected site format
          const siteData = {
            id: data.id || 'justasmile-main',
            site_id: data.id || 'justasmile-main',
            name: data.site_info?.name || 'Just A Smile Events',
            site_name: data.site_info?.name || 'Just A Smile Events',
            slug: 'justasmile-events',
            domain: 'justasmile-events.com',
            logo_url: data.site_info?.logo_url,
            navigation: {
              logo: data.site_info?.logo_url,
              style: 'transparent'
            },
            pages: data.pages || [],
            menu_structure: data.menu_structure || { items: [] },
            footer_content: data.footer_content || {},
            site_settings: data.site_settings || {},
            contact_info: data.contact_info || {},
            global_styles: data.global_styles || data.site_info?.global_styles || {
              primaryColor: '#e6d28e',
              secondaryColor: '#1a1a2e'
            },
            is_published: true
          };
          setSite(siteData);
          // Find the right page based on URL
          const pathParts = location.pathname.split('/').filter(Boolean);
          const pageSlugFromUrl = pathParts[0] || 'accueil';
          
          // Special pages that don't need to exist in DB
          const specialPages = ['formules', 'nos-formules', 'composer-formule', 'contact'];
          
          let targetPage;
          if (specialPages.includes(pageSlugFromUrl)) {
            // Create virtual page for special pages
            if (pageSlugFromUrl === 'contact') {
              targetPage = { 
                slug: 'contact', 
                title: 'Contact', 
                sections: [{
                  type: 'contact',
                  content: {
                    title: 'Envoyez-nous un message',
                    description: 'Nous sommes à votre écoute pour toutes vos questions.'
                  }
                }]
              };
            } else {
              targetPage = { slug: pageSlugFromUrl, title: 'Nos Formules', sections: [] };
            }
          } else {
            targetPage = siteData.pages?.find(p => p.slug === pageSlugFromUrl) || siteData.pages?.[0];
          }
          setCurrentPage(targetPage);
        }
        setLoading(false);
        return;
      }
      
      // If siteSlug is provided, load by slug (for subdomain simulation)
      if (siteSlug) {
        response = await fetch(`${API_URL}/api/site-builder/site/by-slug/${siteSlug}`);
        if (response.ok) {
          const data = await response.json();
          setSite(data);
          setCurrentPage(data.pages?.[0]);
        } else {
          console.error('Site not found by slug:', siteSlug);
        }
      }
      // If siteId is provided in URL, load by siteId (public preview)
      else if (siteId) {
        response = await fetch(`${API_URL}/api/site-builder/site/${siteId}`);
        if (response.ok) {
          const data = await response.json();
          setSite(data);
          setCurrentPage(data.pages?.[0]);
        } else {
          // Try with authenticated API
          response = await siteBuilderAPI.getMySite();
          setSite(response.data);
          setCurrentPage(response.data.pages?.[0]);
        }
      } else {
        // No siteId/siteSlug - use authenticated endpoint
        response = await siteBuilderAPI.getMySite();
        setSite(response.data);
        setCurrentPage(response.data.pages?.[0]);
      }
    } catch (error) {
      console.error('Error loading site:', error);
    } finally {
      setLoading(false);
    }
  };

  // Navigate to page from route parameter when site is loaded
  useEffect(() => {
    if (!site) return;
    
    // Check if we're on an SEO page URL from the full path
    const pathname = location.pathname;
    const seoMatch = pathname.match(/\/seo\/(.+)$/);
    const blogMatch = pathname.match(/\/blog\/(.+)$/);
    
    // Handle blog article URL
    if (blogMatch && blogMatch[1]) {
      const articleSlug = blogMatch[1];
      console.log('Loading blog article from URL:', articleSlug);
      
      const loadBlogArticle = async () => {
        setLoadingBlogArticle(true);
        setBlogArticle(null);
        setSeoPage(null);
        setCurrentPage(null);
        
        try {
          const response = await fetch(`${API_URL}/api/public/blog/${articleSlug}`);
          if (response.ok) {
            const article = await response.json();
            setBlogArticle(article);
          } else {
            console.error('Blog article not found');
            // Navigate back to blog page
            navigateToPage('blog');
          }
        } catch (error) {
          console.error('Error loading blog article:', error);
        } finally {
          setLoadingBlogArticle(false);
        }
      };
      
      loadBlogArticle();
      return;
    }
    
    if (seoMatch && seoMatch[1]) {
      // Extract SEO slug from URL path and load SEO page directly
      const seoSlug = seoMatch[1];
      console.log('Loading SEO page from URL:', seoSlug);
      
      // Load SEO page directly
      const loadSeoPage = async () => {
        setLoadingSeoPage(true);
        setSeoPage(null);
        setCurrentPage(null);
        setBlogArticle(null);
        
        // In standalone mode, use a fixed site_id
        const effectiveSiteId = standaloneMode ? 'justasmile-main' : (site?.id || 'default');
        
        try {
          // Try to fetch existing SEO page
          const response = await fetch(`${API_URL}/api/seo-assistant/pages/${effectiveSiteId}/${seoSlug}`);
          if (response.ok) {
            const pageData = await response.json();
            setSeoPage(pageData);
          } else {
            // Generate the page on-the-fly if not found
            const [service, ...cityParts] = seoSlug.split('-');
            const city = cityParts.join('-').replace(/-/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            
            const genResponse = await fetch(`${API_URL}/api/seo-assistant/pages/generate-and-store?site_id=${effectiveSiteId}&service=${service}&city=${city}`, {
              method: 'POST'
            });
            
            if (genResponse.ok) {
              const result = await genResponse.json();
              setSeoPage(result.page);
            } else {
              console.error('Failed to generate SEO page');
            }
          }
        } catch (error) {
          console.error('Error loading SEO page:', error);
        } finally {
          setLoadingSeoPage(false);
        }
      };
      
      loadSeoPage();
      return;
    }
    
    // Handle regular page navigation
    // In standalone mode, get page slug from URL path
    const effectivePageSlug = standaloneMode 
      ? (location.pathname.split('/').filter(Boolean)[0] || 'accueil')
      : routePageSlug;
    
    if (effectivePageSlug && effectivePageSlug !== 'accueil') {
      const targetPage = site?.pages?.find(p => 
        p.slug === effectivePageSlug || 
        p.slug?.toLowerCase() === effectivePageSlug?.toLowerCase() ||
        p.name?.toLowerCase().replace(/\s+/g, '-') === effectivePageSlug?.toLowerCase()
      );
      if (targetPage) {
        setCurrentPage(targetPage);
        setSeoPage(null); // Clear SEO page when navigating to regular page
      } else {
        // Page not found in site pages - check if it's an SEO page
        // SEO pages have format: service-city (e.g., photobooth-marseille, videobooth-360-istres)
        const seoPattern = /^(photobooth|videobooth-360|livre-dor-video|livre-dor)-(.+)$/i;
        if (seoPattern.test(effectivePageSlug)) {
          console.log('Loading SEO page from slug:', effectivePageSlug);
          setLoadingSeoPage(true);
          setSeoPage(null);
          setCurrentPage(null);
          setBlogArticle(null);
          
          const effectiveSiteId = standaloneMode ? 'justasmile-main' : (site?.id || 'default');
          
          // Load SEO page
          fetch(`${API_URL}/api/seo-pages/by-slug/${effectivePageSlug}`)
            .then(response => {
              if (response.ok) {
                return response.json();
              }
              throw new Error('SEO page not found');
            })
            .then(pageData => {
              if (pageData.is_published !== false) {
                setSeoPage(pageData);
              } else {
                console.log('SEO page exists but is not published');
                navigateToPage('accueil');
              }
            })
            .catch(error => {
              console.error('Error loading SEO page:', error);
              // Fallback to old endpoint
              fetch(`${API_URL}/api/seo-assistant/pages/${effectiveSiteId}/${effectivePageSlug}`)
                .then(resp => resp.ok ? resp.json() : null)
                .then(data => {
                  if (data && data.is_published !== false) {
                    setSeoPage(data);
                  } else {
                    navigateToPage('accueil');
                  }
                })
                .catch(() => navigateToPage('accueil'));
            })
            .finally(() => setLoadingSeoPage(false));
          return;
        }
      }
    } else if (effectivePageSlug === 'accueil' || !effectivePageSlug) {
      // Navigate to home page
      const homePage = site?.pages?.find(p => p.slug === 'accueil') || site?.pages?.[0];
      if (homePage) {
        setCurrentPage(homePage);
      }
    }
  }, [site, routePageSlug, location.pathname, standaloneMode]);

  // SEO Meta Tags - Direct DOM manipulation (react-helmet-async has issues with React 19)
  useEffect(() => {
    // Only run when we have site data
    if (!site) return;
    
    // Get production URL for SEO
    const productionDomain = 'www.justasmile-events.com';
    const currentPageSlug = currentPage?.slug || 'accueil';
    const canonicalUrl = currentPageSlug === 'accueil' 
      ? `https://${productionDomain}` 
      : `https://${productionDomain}/${currentPageSlug}`;
    
    // SEO metadata
    const seoTitle = currentPage?.seo_title || currentPage?.title || site?.name || 'Just A Smile Events';
    const seoDescription = currentPage?.seo_description || site?.site_settings?.seo_description || 'Location PhotoBooth & VideoBooth 360° pour tous vos événements en région PACA';
    const logoUrl = site?.logo_url || site?.navigation?.logo || 'https://2026.justasmile-events.com/wp-content/uploads/2023/04/JUST-A-SMILE-EVENTS-LOGO-new-2025-sans-fond-1-1024x1024.png';
    const seoImage = currentPage?.og_image || logoUrl;
    
    // Compute robots value - noindex for preview/staging URLs
    const isPreviewUrl = window.location.hostname.includes('emergentagent.com') || 
      window.location.hostname.includes('preview');
    const robotsContent = isPreviewUrl ? 'noindex, nofollow' : 'index, follow';
    
    // Helper to update or create meta tag
    const setMeta = (selector, content) => {
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        if (selector.includes('property=')) {
          const prop = selector.match(/property="([^"]+)"/)?.[1];
          if (prop) meta.setAttribute('property', prop);
        } else {
          const name = selector.match(/name="([^"]+)"/)?.[1];
          if (name) meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Helper to update or create link tag
    const setLink = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Set title
    document.title = seoTitle;
    
    // Set meta tags
    setMeta('meta[name="description"]', seoDescription);
    setMeta('meta[name="robots"]', robotsContent);
    setMeta('meta[property="og:title"]', seoTitle);
    setMeta('meta[property="og:description"]', seoDescription);
    setMeta('meta[property="og:url"]', canonicalUrl);
    setMeta('meta[property="og:type"]', 'website');
    setMeta('meta[property="og:image"]', seoImage);
    setMeta('meta[property="og:site_name"]', site?.name || 'Just A Smile Events');
    setMeta('meta[name="twitter:card"]', 'summary_large_image');
    setMeta('meta[name="twitter:url"]', canonicalUrl);
    setMeta('meta[name="twitter:title"]', seoTitle);
    setMeta('meta[name="twitter:description"]', seoDescription);
    setMeta('meta[name="twitter:image"]', seoImage);
    
    // Set canonical link
    setLink('canonical', canonicalUrl);
  }, [site, currentPage]);

  // Navigate to a different page
  const navigateToPage = async (slug) => {
    // Close menus immediately
    setHoveredMenu(null);
    setMobileMenuOpen(false);
    
    // Handle gift card page - redirect to dedicated page
    if (slug === 'carte-cadeau') {
      if (standaloneMode) {
        navigate('/carte-cadeau');
      } else {
        window.open('/carte-cadeau', '_blank');
      }
      return;
    }
    
    // Check if it's an SEO page (starts with 'seo/')
    if (slug?.startsWith('seo/')) {
      const seoSlug = slug.replace('seo/', '');
      setLoadingSeoPage(true);
      setSeoPage(null);
      setBlogArticle(null);
      setCurrentPage(null);
      
      // Scroll to top immediately
      window.scrollTo(0, 0);
      
      // In standalone mode, use a fixed site_id
      const effectiveSiteId = standaloneMode ? 'justasmile-main' : (site?.id || 'default');
      
      // Update URL immediately for better UX
      if (standaloneMode) {
        navigate(`/seo/${seoSlug}`);
      }
      
      try {
        // Only fetch existing SEO page - no automatic generation
        const response = await fetch(`${API_URL}/api/seo-assistant/pages/${effectiveSiteId}/${seoSlug}`);
        if (response.ok) {
          const pageData = await response.json();
          // Only show if published
          if (pageData.is_published !== false) {
            setSeoPage(pageData);
          } else {
            console.log('SEO page exists but is not published');
            // Redirect to home page
            navigateToPage('accueil');
          }
        } else {
          // Page not found - redirect to home instead of generating
          console.log('SEO page not found:', seoSlug);
          navigateToPage('accueil');
        }
      } catch (error) {
        console.error('Error loading SEO page:', error);
        navigateToPage('accueil');
      } finally {
        setLoadingSeoPage(false);
      }
      return;
    }
    
    // Check if it's a blog article (starts with 'blog/')
    if (slug?.startsWith('blog/')) {
      const articleSlug = slug.replace('blog/', '');
      setLoadingBlogArticle(true);
      setBlogArticle(null);
      setSeoPage(null);
      setCurrentPage(null);
      
      // Scroll to top immediately
      window.scrollTo(0, 0);
      
      // Update URL immediately
      if (standaloneMode) {
        navigate(`/blog/${articleSlug}`);
      }
      
      try {
        const response = await fetch(`${API_URL}/api/public/blog/${articleSlug}`);
        if (response.ok) {
          const article = await response.json();
          setBlogArticle(article);
        } else {
          console.error('Blog article not found');
          // Navigate back to blog page
          navigateToPage('blog');
        }
      } catch (error) {
        console.error('Error loading blog article:', error);
      } finally {
        setLoadingBlogArticle(false);
      }
      return;
    }

    // Regular page navigation - scroll to top first
    window.scrollTo(0, 0);
    
    // Clear previous states
    setSeoPage(null);
    setBlogArticle(null);
    
    // Check if it's a legal page
    const legalPages = ['mentions-legales', 'cgv', 'politique-confidentialite'];
    if (legalPages.includes(slug)) {
      // Update URL immediately
      if (standaloneMode) {
        navigate(`/${slug}`);
      }
      
      try {
        const response = await fetch(`${API_URL}/api/public/legal/${slug}`);
        if (response.ok) {
          const legalData = await response.json();
          // Create a virtual page with legal_content type
          const legalPage = {
            slug: slug,
            name: legalData.title,
            sections: [{
              type: 'legal_content',
              content: legalData
            }]
          };
          setCurrentPage(legalPage);
          // Force scroll to top after state update
          setTimeout(() => window.scrollTo(0, 0), 0);
          return;
        }
      } catch (error) {
        console.error('Error loading legal page:', error);
      }
    }
    
    const targetPage = site?.pages?.find(p => 
      p.slug === slug || 
      p.slug?.toLowerCase() === slug?.toLowerCase() ||
      p.name?.toLowerCase().replace(/\s+/g, '-') === slug?.toLowerCase()
    );
    if (targetPage) {
      setCurrentPage(targetPage);
      // In standalone mode, update URL
      if (standaloneMode) {
        const newPath = slug === 'accueil' ? '/' : `/${slug}`;
        navigate(newPath);
      }
      // Force scroll to top after state update
      setTimeout(() => window.scrollTo(0, 0), 0);
    } else {
      // Page not found in site.pages, but try to navigate anyway (for dynamic pages like modeles)
      console.log('Page not found in site.pages, navigating directly to:', slug);
      if (standaloneMode) {
        const newPath = slug === 'accueil' ? '/' : `/${slug}`;
        navigate(newPath);
        // Create a virtual page for the slug
        setCurrentPage({ slug: slug, sections: [] });
        setTimeout(() => window.scrollTo(0, 0), 0);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!site) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white">Site non trouvé</p>
      </div>
    );
  }

  const menuStructure = site?.menu_structure?.items || site?.navigation?.items || [];
  
  // Check if blog is enabled in site settings (default to false)
  const blogEnabled = site?.site_settings?.blog_enabled === true;
  
  // Filter menu to hide blog if disabled
  const filteredMenuStructure = menuStructure.filter(item => {
    // Always hide "Réservation" as it's handled by CTA button
    if (item.label?.toLowerCase() === 'réservation') return false;
    // Hide blog if disabled
    if (!blogEnabled && (item.label?.toLowerCase() === 'blog' || item.slug === 'blog' || item.link === 'blog')) return false;
    return true;
  });
  
  // Golden color matching the logo - rich metallic gold
  const primaryColor = site?.global_styles?.primaryColor || '#e6d28e';
  
  // Get booking button text from site settings (connected to site editor)
  const bookingButtonText = site?.site_settings?.booking_button_text || 'RÉSERVATION';
  
  // Use logo from site data - check multiple sources
  const logoUrl = site?.logo_url || site?.navigation?.logo || 'https://2026.justasmile-events.com/wp-content/uploads/2023/04/JUST-A-SMILE-EVENTS-LOGO-new-2025-sans-fond-1-1024x1024.png';
  
  // Get current page slug
  const currentPageSlug = currentPage?.slug || 'accueil';

  // Helper function to get product data for a service type
  const getProductForService = (serviceType) => {
    const categoryMapping = {
      'photobooth': 'photobooth',
      'videobooth-360': 'videobooth',
      'livre-dor': 'livre-dor'
    };
    const category = categoryMapping[serviceType] || serviceType;
    
    // Find product matching the service type
    const product = productsForServices.find(p => 
      p.category === category || 
      p.name?.toLowerCase().includes(serviceType.replace('-', ' '))
    );
    
    return product;
  };

  // Service images for mega menu - use product images[0] first, then cover_image, then defaults
  const getMegaMenuImage = (serviceType) => {
    const product = getProductForService(serviceType);
    
    // Priority 1: Use product's main image (images[0]) - same as admin cards
    if (product?.images?.[0]) {
      // Handle relative URLs
      const imageUrl = product.images[0];
      if (imageUrl.startsWith('/api/')) {
        return `${window.location.origin}${imageUrl}`;
      }
      return imageUrl;
    }
    
    // Priority 2: Use cover_image if set
    if (product?.cover_image) return product.cover_image;
    
    // Fallback to site config
    const siteImages = {
      'photobooth': site?.mega_menu_images?.photobooth,
      'videobooth-360': site?.mega_menu_images?.videobooth,
      'livre-dor': site?.mega_menu_images?.livreor
    };
    if (siteImages[serviceType]) return siteImages[serviceType];
    
    // Default fallback images
    const defaults = {
      'photobooth': "https://2026.justasmile-events.com/wp-content/uploads/elementor/thumbs/5A50AEA3-2EC1-4674-ABEA-D92916842EA6-rbjnp9lqbo832awqsaqjzhs4s3lol5gi87ovtnm18w.jpg",
      'videobooth-360': "https://2026.justasmile-events.com/wp-content/uploads/2023/05/videobooth-accueil.png",
      'livre-dor': "https://2026.justasmile-events.com/wp-content/uploads/elementor/thumbs/IMG_8338-scaled-rbjnol5xdzamofw8r0696ny5c2y510rhguq9cgm9qo.jpg"
    };
    return defaults[serviceType] || '';
  };

  // IMAGES object - will be recalculated on each render when productsForServices changes
  const IMAGES = {
    photobooth: getMegaMenuImage('photobooth'),
    videobooth: getMegaMenuImage('videobooth-360'),
    livreor: getMegaMenuImage('livre-dor')
  };

  // Transform product variations to TarifsPage format
  const getProductTarifs = (product, serviceType) => {
    if (!product?.variations?.length) return null;
    
    return product.variations
      .filter(v => v.is_active !== false)
      .map(v => ({
        id: v.id,
        name: v.name,
        duration: v.duration_label || (v.duration_hours ? `${v.duration_hours}h` : ''),
        price: v.price,
        popular: v.is_popular || false,
        subtitle: v.description,
        // Use structured features if available, else parse from description
        features: v.features?.length > 0 ? v.features : [
          v.description || ''
        ].filter(Boolean),
        // Key metrics
        prints_count: v.prints_count,
        prints_unlimited: v.prints_unlimited,
        templates_count: v.templates_count || 1,
        music_count: v.music_count || 0,
        km_offerts: v.km_offerts || false,
        usb_included: v.usb_included || false,
        // Card styling options
        showRibbon: v.showRibbon || false,
        ribbonText: v.ribbonText || 'BEST SELLER',
        ribbonColor: v.ribbonColor || '#DC2626',
        headerColor: v.headerColor || '#1a1a1a',
        backgroundType: v.backgroundType || 'solid',
        backgroundColor: v.backgroundColor || '#ffffff',
        gradientColors: v.gradientColors || ['#D4A574', '#C9A45C', '#B8956F'],
        textColorOverride: v.textColorOverride || ''
      }));
  };

  // Get first section info
  const firstSection = currentPage?.sections?.[0];
  
  // Custom pages that handle their own layout - don't show global hero/header for these
  const customLayoutPages = [
    'photobooth-presentation', 'videobooth-360-presentation', 'livre-dor-presentation',
    'photobooth-modeles', 'photobooth-tarifs',
    'videobooth-360-modeles', 'videobooth-360-tarifs',
    'livre-dor-modeles', 'livre-dor-tarifs',
    'formules', 'nos-formules', 'composer-formule',
    'contact'
  ];
  const isCustomLayoutPage = customLayoutPages.includes(currentPage?.slug);
  
  // Check if current page is the formulas page
  const isFormulasPage = ['formules', 'nos-formules', 'composer-formule'].includes(currentPage?.slug);
  
  // Check if current page is the contact page
  const isContactPage = currentPage?.slug === 'contact';
  
  // Only show global hero if first section is hero AND it's not a custom layout page AND not formulas page
  const isHeroFirst = !isCustomLayoutPage && !isFormulasPage && !isContactPage && (firstSection?.type === 'hero_fullscreen' || firstSection?.type === 'hero_video' || firstSection?.type === 'hero' || firstSection === 'hero');
  const heroContent = firstSection?.content || {};
  // YouTube video - use from CMS or fallback to default
  const defaultVideoId = '_DcxsNgZTx8'; // JAS back video (fallback)
  // Extract video ID from heroContent.videoUrl if available
  const extractYoutubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };
  const heroVideoId = extractYoutubeId(heroContent.videoUrl) || defaultVideoId;
  // Use YouTube thumbnail as fallback background image (high quality)
  const heroVideoThumbnail = `https://i.ytimg.com/vi/${heroVideoId}/maxresdefault.jpg`;
  const heroVideoUrl = `https://www.youtube.com/embed/${heroVideoId}?autoplay=1&mute=1&loop=1&playlist=${heroVideoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`;
  const heroImageUrl = heroContent.backgroundImage || heroVideoThumbnail;

  // TTS Button
  const TTSButton = ({ text }) => {
    const [speaking, setSpeaking] = useState(false);
    const speak = () => {
      if ('speechSynthesis' in window) {
        if (speaking) { window.speechSynthesis.cancel(); setSpeaking(false); }
        else {
          const utterance = new SpeechSynthesisUtterance(text);
          utterance.lang = 'fr-FR'; utterance.rate = 0.9;
          utterance.onend = () => setSpeaking(false);
          window.speechSynthesis.speak(utterance); setSpeaking(true);
        }
      }
    };
    return (
      <button onClick={speak} className={`p-2 rounded-full transition-all hover:scale-110 ${speaking ? 'animate-pulse' : ''}`} style={{ backgroundColor: primaryColor }}>
        <FiVolume2 className="h-4 w-4 text-black" />
      </button>
    );
  };

  // BookingModal is now imported from components/BookingModal.js

  // Callback Modal - Request a callback
  const CallbackModal = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => { setShowCallbackModal(false); setCallbackSent(false); }}>
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-white rounded-3xl max-w-md w-full p-8 relative" onClick={e => e.stopPropagation()}>
        <button onClick={() => { setShowCallbackModal(false); setCallbackSent(false); }} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"><FiX className="h-5 w-5" /></button>
        
        {!callbackSent ? (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                <FiPhone className="h-8 w-8" style={{ color: primaryColor }} />
              </div>
              <h3 className="text-2xl font-bold mb-2">Demande de rappel</h3>
              <p className="text-gray-500 text-sm">Nous vous recontactons dans les plus brefs délais</p>
            </div>
            
            <div className="space-y-4">
              <input 
                type="text" 
                placeholder="Votre nom *" 
                className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-yellow-200 focus:border-yellow-400 outline-none" 
                value={callbackData.name} 
                onChange={e => setCallbackData(prev => ({ ...prev, name: e.target.value }))} 
              />
              <input 
                type="tel" 
                placeholder="Votre téléphone *" 
                className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-yellow-200 focus:border-yellow-400 outline-none" 
                value={callbackData.phone} 
                onChange={e => setCallbackData(prev => ({ ...prev, phone: e.target.value }))} 
              />
              <select 
                className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-yellow-200 focus:border-yellow-400 outline-none text-gray-700"
                value={callbackData.preferredTime}
                onChange={e => setCallbackData(prev => ({ ...prev, preferredTime: e.target.value }))}
              >
                <option value="">Moment préféré pour être rappelé</option>
                <option value="matin">Le matin (9h-12h)</option>
                <option value="apres-midi">L'après-midi (14h-18h)</option>
                <option value="soir">En fin de journée (18h-19h)</option>
              </select>
              
              <button 
                onClick={() => {
                  if (callbackData.name && callbackData.phone) {
                    setCallbackSent(true);
                    setCallbackData({ name: '', phone: '', preferredTime: '' });
                  }
                }}
                disabled={!callbackData.name || !callbackData.phone}
                className="w-full py-3 rounded-xl font-bold text-black disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
              >
                Demander un rappel
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-100">
              <FiCheck className="h-10 w-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Demande envoyée !</h3>
            <p className="text-gray-500">Nous vous rappellerons très prochainement.</p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );

  // Review Modal - Leave a review
  const ReviewModal = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => { setShowReviewModal(false); setReviewSent(false); }}>
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-white rounded-3xl max-w-md w-full p-8 relative" onClick={e => e.stopPropagation()}>
        <button onClick={() => { setShowReviewModal(false); setReviewSent(false); }} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"><FiX className="h-5 w-5" /></button>
        
        {!reviewSent ? (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                <FiStar className="h-8 w-8" style={{ color: primaryColor }} />
              </div>
              <h3 className="text-2xl font-bold mb-2">Laisser un avis</h3>
              <p className="text-gray-500 text-sm">Partagez votre expérience avec nous</p>
            </div>
            
            <div className="space-y-4">
              <input 
                type="text" 
                placeholder="Votre nom *" 
                className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-yellow-200 focus:border-yellow-400 outline-none" 
                value={reviewData.name} 
                onChange={e => setReviewData(prev => ({ ...prev, name: e.target.value }))} 
              />
              
              {/* Star Rating */}
              <div className="flex items-center justify-center gap-2 py-2">
                {[1, 2, 3, 4, 5].map(star => (
                  <button 
                    key={star} 
                    onClick={() => setReviewData(prev => ({ ...prev, rating: star }))}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <FiStar 
                      className={`h-8 w-8 transition-colors ${star <= reviewData.rating ? 'fill-current' : ''}`}
                      style={{ color: star <= reviewData.rating ? primaryColor : '#e5e7eb' }}
                    />
                  </button>
                ))}
              </div>
              
              <textarea 
                placeholder="Votre commentaire *" 
                rows={4}
                className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-yellow-200 focus:border-yellow-400 outline-none resize-none" 
                value={reviewData.comment} 
                onChange={e => setReviewData(prev => ({ ...prev, comment: e.target.value }))} 
              />
              
              <button 
                onClick={() => {
                  if (reviewData.name && reviewData.comment) {
                    setReviewSent(true);
                    setReviewData({ name: '', rating: 5, comment: '' });
                  }
                }}
                disabled={!reviewData.name || !reviewData.comment}
                className="w-full py-3 rounded-xl font-bold text-black disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
              >
                Envoyer mon avis
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-100">
              <FiCheck className="h-10 w-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Merci pour votre avis !</h3>
            <p className="text-gray-500">Votre retour compte beaucoup pour nous.</p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );

  // Photo Templates for Preview - use site config or defaults
  // Using full resolution images for better quality display
  const DEFAULT_TEMPLATES = [
    { id: 'strip-vertical', name: 'Bandelette Verticale', format: 'portrait', ratio: '5x15', image: 'https://2026.justasmile-events.com/wp-content/uploads/2023/11/Clara-Ghais-2.png', overlay: 'Format photomaton classique - 4 poses', photoPosition: { top: '8%', left: '10%', width: '80%', height: '35%' } },
    { id: 'carte-postale', name: 'Carte Postale', format: 'paysage', ratio: '10x15', image: 'https://2026.justasmile-events.com/wp-content/uploads/2023/11/30-ans-Sophie.png', overlay: 'Format carte postale horizontal', photoPosition: { top: '10%', left: '5%', width: '90%', height: '65%' } },
    { id: 'mariage', name: 'Mariage Élégant', format: 'portrait', ratio: '10x15', image: 'https://2026.justasmile-events.com/wp-content/uploads/2023/11/Bapteme-Luna.png', overlay: 'Cadre romantique avec prénoms', photoPosition: { top: '5%', left: '8%', width: '84%', height: '70%' } },
    { id: 'corporate', name: 'Corporate', format: 'paysage', ratio: '10x15', image: 'https://2026.justasmile-events.com/wp-content/uploads/2023/11/GALA-2022.png', overlay: 'Avec logo entreprise', photoPosition: { top: '8%', left: '10%', width: '80%', height: '68%' } },
    { id: 'anniversaire', name: 'Anniversaire', format: 'portrait', ratio: '10x15', image: 'https://2026.justasmile-events.com/wp-content/uploads/2022/07/photobooth-JUSTASMILE-1.png', overlay: 'Design festif personnalisé', photoPosition: { top: '10%', left: '12%', width: '76%', height: '60%' } },
    { id: 'moderne', name: 'Moderne Minimaliste', format: 'carré', ratio: '10x10', image: 'https://2026.justasmile-events.com/wp-content/uploads/2025/08/IMG_8336.jpg', overlay: 'Style épuré et moderne', photoPosition: { top: '5%', left: '5%', width: '90%', height: '75%' } },
  ];
  
  // Use site templates if configured, otherwise use defaults
  const PHOTO_TEMPLATES = site?.photo_templates?.length > 0 
    ? site.photo_templates.map((t, i) => ({
        ...DEFAULT_TEMPLATES[i],
        ...t,
        image: t.image || DEFAULT_TEMPLATES[i]?.image || ''
      }))
    : DEFAULT_TEMPLATES;

  // Pre-loaded test photos for each format type
  const TEST_PHOTOS = {
    portrait: {
      label: 'Portrait couple',
      url: 'https://images.unsplash.com/photo-1529634806980-85c3dd6d34ac?w=600&h=800&fit=crop',
      credit: 'Unsplash'
    },
    paysage: {
      label: 'Groupe amis',
      url: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&h=600&fit=crop',
      credit: 'Unsplash'
    },
    carré: {
      label: 'Selfie sourire',
      url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=600&h=600&fit=crop',
      credit: 'Unsplash'
    }
  };

  // SEO Cities list for footer banner - Load from site settings or use defaults
  const DEFAULT_SEO_CITIES = [
    'Istres', 'Martigues', 'Fos-sur-Mer', 'Miramas', 'Salon-de-Provence',
    'Arles', 'Marseille', 'Aix-en-Provence', 'Vitrolles', 'Marignane',
    'Aubagne', 'La Ciotat', 'Cassis', 'Carry-le-Rouet', 'Sausset-les-Pins',
    'Port-de-Bouc', 'Saint-Mitre-les-Remparts', 'Grans', 'Cornillon-Confoux',
    'Eyguières', 'Lamanon', 'Sénas', 'Mallemort', 'Lambesc', 'Pélissanne'
  ];
  
  // Get SEO cities from site settings or fallback to defaults
  const SEO_CITIES = site?.site_settings?.seo_cities?.length > 0 
    ? site.site_settings.seo_cities 
    : DEFAULT_SEO_CITIES;

  // SEO Services for URL generation
  const SEO_SERVICES = [
    { name: 'PhotoBooth', slug: 'photobooth' },
    { name: 'VideoBooth 360', slug: 'videobooth-360' },
    { name: "Livre d'Or Vidéo", slug: 'livre-dor-video' }
  ];

  // Google Reviews Data - Real reviews from Just A Smile Events
  const GOOGLE_REVIEWS = [
    { 
      name: 'CELINE LAURENT', 
      avatar: 'C', 
      rating: 5, 
      date: 'il y a 2 mois',
      text: "D'un professionalisme absolu ! Je recommande sans hésiter ! Que ce soit, la réservation, l'installation, la dépose, rien à dire mise à part que tout était parfait. A l'écoute sur tous les points, je ne peux que les remercier d'avoir contribué à cette soirée qui a marqué les 18 ans de ma fille."
    },
    { 
      name: 'Quentin Ribot', 
      avatar: 'Q', 
      rating: 5, 
      date: 'il y a 3 mois',
      text: "Très satisfaits ! Nous avions loué la borne pour la journée. Une équipe est venue l'installer le matin même et la récupérer en fin de journée. Très beau rendu des photos, nous avons aussi le lien et la clef USB avec toutes les photos."
    },
    { 
      name: 'sandrine amato', 
      avatar: 'S', 
      rating: 5, 
      date: 'il y a 4 mois',
      text: "Une équipe très professionnelle et à l'écoute. Ma petite fille était ravie des photos de très bonne qualité. Je recommande Just a Smile Events, un très grand merci à toute l'équipe..."
    },
    { 
      name: 'marion galhaut', 
      avatar: 'M', 
      rating: 5, 
      date: 'il y a 6 mois',
      text: "Nous avons fait appel à Just a Smile Events pour notre mariage. Prestation de qualité, livraison et installation, design des cadres photos personnalisés. Nous avons adoré et nos invités également !"
    },
    { 
      name: 'Jorge Ferreira', 
      avatar: 'J', 
      rating: 5, 
      date: 'il y a 6 mois',
      text: "Parfait"
    },
    { 
      name: 'Guillaume Chollet', 
      avatar: 'G', 
      rating: 5, 
      date: 'il y a 6 mois',
      text: "Merci beaucoup à toute l'équipe pour cette mise en place de box photo. Tout était parfait et les photos magnifiques 🤩"
    },
    { 
      name: 'Louane Mumber', 
      avatar: 'L', 
      rating: 5, 
      date: 'il y a 5 mois',
      text: "Une prestation au top des photos très lumineuse et une société qui s'adapte à vos besoins. Encore merci à vous pour votre professionnalisme."
    },
    { 
      name: 'Ludivine Chehami', 
      avatar: 'L', 
      rating: 5, 
      date: 'il y a 5 mois',
      text: "Prestation incroyable, ce sont les meilleurs dans leur domaine. Foncez les yeux fermés 👌🏼"
    },
    { 
      name: 'Ange Duarès', 
      avatar: 'A', 
      rating: 5, 
      date: 'il y a 4 mois',
      text: "Super prestation ! Je recommande fortement Just a Smile. Super serviable, prestation au top. Merci encore à eux."
    },
  ];

  // Interactive Product Hotspots - Points dynamiques sur la machine (basé sur le site de référence)
  // Default hotspots (will be overwritten by database config if available)
  const DEFAULT_PRODUCT_HOTSPOTS = {
    'livre-dor-video': {
      image: 'https://2026.justasmile-events.com/wp-content/uploads/2025/09/IMG_2393-1-683x1024.jpg',
      hotspots: [
        { id: 1, x: 50, y: 8, label: 'Caméra 4K', description: 'Permet un enregistrement de vidéo fluide avec une qualité 4K professionnelle', icon: '📹' },
        { id: 2, x: 30, y: 35, label: 'Micro col de cygne', description: 'Micro professionnel avec réducteur de bruit - notre micro capte uniquement votre voix', icon: '🎤' },
        { id: 3, x: 70, y: 45, label: 'Éclairage LED', description: 'Permet une luminosité adéquate pour un rendu optimal de vos vidéos', icon: '💡' },
        { id: 4, x: 50, y: 70, label: 'Pupitre élégant', description: 'Pour accompagner vos plus beaux messages avec un design soigné', icon: '🎨' },
      ]
    },
    'photobooth': {
      image: 'https://2026.justasmile-events.com/wp-content/uploads/2025/09/image00001-1-683x1024.jpeg',
      hotspots: [
        { id: 1, x: 50, y: 12, label: 'Appareil photo Canon', description: 'Appareil photo professionnel Canon avec un objectif 18-55 pour des photos de qualité studio', icon: '📷' },
        { id: 2, x: 50, y: 30, label: 'Écran tactile', description: 'Pour choisir vos modèles et voir l\'aperçu de vos photos en temps réel', icon: '📱' },
        { id: 3, x: 25, y: 45, label: 'Lumières LED', description: 'Éclairage LED professionnel pour vous mettre en lumière parfaitement', icon: '💡' },
        { id: 4, x: 75, y: 45, label: 'Flash Professionnel', description: 'Permet une luminosité et une qualité optimales, digne d\'un studio photo', icon: '⚡' },
        { id: 5, x: 50, y: 75, label: 'Onduleur intégré', description: 'Permet au PhotoBooth de ne pas s\'éteindre lors d\'un débranchement involontaire ou d\'une coupure de courant', icon: '🔋' },
      ]
    },
    'videobooth-360': {
      image: 'https://2026.justasmile-events.com/wp-content/uploads/2025/09/image00002-1-683x1024.jpeg',
      hotspots: [
        { id: 1, x: 50, y: 10, label: 'Caméra 4K', description: 'Une caméra 4K de haute qualité pour des vidéos spectaculaires', icon: '🎥' },
        { id: 2, x: 65, y: 25, label: 'Ringlight professionnelle', description: 'Une ringlight professionnelle pour un éclairage parfait à 360°', icon: '💡' },
        { id: 3, x: 35, y: 40, label: 'Lumières LED', description: 'Lumières LED multi-color & sans fils pour des effets visuels uniques', icon: '🌈' },
        { id: 4, x: 50, y: 75, label: 'Plateforme rotative', description: 'Une plateforme de 80cm ou 1 mètre de diamètre pouvant accueillir entre 3 et 5 personnes selon la taille choisie', icon: '🔄' },
      ]
    }
  };

  // Use hotspotsConfig from state or fall back to defaults
  const PRODUCT_HOTSPOTS = hotspotsConfig || DEFAULT_PRODUCT_HOTSPOTS;

  // Client logos for scrolling banner - Load from site settings
  const DEFAULT_CLIENT_LOGOS = [];
  
  // Helper to get full URL for media
  const getMediaUrl = (url) => {
    if (!url) return '';
    // If URL starts with /api/, prefix with backend URL
    if (url.startsWith('/api/')) {
      return `${API_URL}${url}`;
    }
    // If already absolute URL, return as-is
    if (url.startsWith('http')) {
      return url;
    }
    return url;
  };
  
  // Get partner logos from site settings and format URLs
  const rawClientLogos = site?.site_settings?.client_logos?.length > 0 
    ? site.site_settings.client_logos 
    : (site?.partner_logos?.length > 0 ? site.partner_logos : DEFAULT_CLIENT_LOGOS);
  
  const CLIENT_LOGOS = rawClientLogos.map(logo => ({
    ...logo,
    url: getMediaUrl(logo.url)
  }));

  // Photo format recommendations
  const FORMAT_RECOMMENDATIONS = {
    portrait: { label: 'Portrait', ratio: '3:4', minWidth: 600, minHeight: 800, icon: '📱', description: 'Idéal pour les bandelettes et cadres verticaux' },
    paysage: { label: 'Paysage', ratio: '4:3', minWidth: 800, minHeight: 600, icon: '🖼️', description: 'Parfait pour les cartes postales' },
    carré: { label: 'Carré', ratio: '1:1', minWidth: 600, minHeight: 600, icon: '⬜', description: 'Format moderne et équilibré' },
  };

  // Interactive Product Showcase Component
  const ProductHotspotShowcase = ({ productSlug }) => {
    const product = PRODUCT_HOTSPOTS[productSlug];
    if (!product) return null;

    // Get active hotspot details
    const activeHotspotData = product.hotspots.find(h => h.id === activeHotspot);

    return (
      <div className="relative max-w-2xl mx-auto my-8 md:my-12 px-4 md:px-0">
        {/* Image container with hotspots */}
        <div className="relative w-full">
          <img 
            src={product.image} 
            alt="Machine" 
            className="w-full h-auto rounded-xl shadow-2xl"
          />
          
          {/* Hotspot Points - visible on all devices */}
          {product.hotspots.map((hotspot) => (
            <button
              key={hotspot.id}
              onClick={() => setActiveHotspot(activeHotspot === hotspot.id ? null : hotspot.id)}
              className={`absolute w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all transform hover:scale-110 z-10 ${
                activeHotspot === hotspot.id 
                  ? 'bg-white text-black scale-125 z-20 ring-4 ring-amber-400 shadow-lg' 
                  : 'bg-black/90 text-white animate-pulse shadow-lg border-2 border-white/50'
              }`}
              style={{ 
                left: `${hotspot.x}%`, 
                top: `${hotspot.y}%`,
                transform: activeHotspot === hotspot.id ? 'translate(-50%, -50%) scale(1.25)' : 'translate(-50%, -50%)'
              }}
              aria-label={hotspot.label}
            >
              <span className="text-sm sm:text-base md:text-lg font-bold">{activeHotspot === hotspot.id ? '×' : '+'}</span>
            </button>
          ))}
        </div>

        {/* Tooltip - Fixed position on mobile, absolute on desktop */}
        <AnimatePresence>
          {activeHotspotData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="mt-4 md:mt-0 md:absolute md:left-1/2 md:top-full md:-translate-x-1/2 z-30 bg-white rounded-xl shadow-2xl p-4 mx-4 md:mx-0 md:w-80"
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">{activeHotspotData.icon}</span>
                <h4 className="font-bold text-slate-900 text-lg">{activeHotspotData.label}</h4>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{activeHotspotData.description}</p>
              <button 
                onClick={() => setActiveHotspot(null)}
                className="mt-3 text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
              >
                <FiX className="h-3 w-3" /> Fermer
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Legend - clickable hotspot buttons */}
        <div className="mt-6 grid grid-cols-2 sm:flex sm:flex-wrap gap-2 md:gap-4 justify-center px-4">
          {product.hotspots.map((hotspot) => (
            <button
              key={hotspot.id}
              onClick={() => setActiveHotspot(activeHotspot === hotspot.id ? null : hotspot.id)}
              className={`flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 rounded-full text-xs md:text-sm transition-all ${
                activeHotspot === hotspot.id
                  ? 'bg-white text-black shadow-lg'
                  : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
            >
              <span>{hotspot.icon}</span>
              <span className="text-left whitespace-pre-line leading-tight text-[10px] sm:text-xs md:text-sm">{hotspot.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Handle test photo selection
  const handleTestPhotoSelect = (format) => {
    const testPhoto = TEST_PHOTOS[format];
    if (testPhoto) {
      setUploadedPhoto(testPhoto.url);
      setPhotoDimensions({ 
        width: format === 'paysage' ? 800 : 600, 
        height: format === 'portrait' ? 800 : 600, 
        orientation: format, 
        ratio: format === 'portrait' ? 0.75 : format === 'paysage' ? 1.33 : 1 
      });
      setPhotoQuality('bonne');
    }
  };

  // Handle photo upload with dimension detection
  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const width = img.width;
          const height = img.height;
          const ratio = width / height;
          let orientation = 'carré';
          if (ratio > 1.2) orientation = 'paysage';
          else if (ratio < 0.8) orientation = 'portrait';
          
          setPhotoDimensions({ width, height, orientation, ratio });
          setPhotoQuality(width >= 600 && height >= 600 ? 'bonne' : 'faible');
        };
        img.src = event.target.result;
        setUploadedPhoto(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Photo Preview Modal Component - IMPROVED VERSION
  const PhotoPreviewModal = () => {
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'single'
    
    return (
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }} 
        className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md flex items-center justify-center p-4" 
        onClick={() => setShowPhotoPreview(false)}
      >
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }} 
          animate={{ scale: 1, opacity: 1 }} 
          exit={{ scale: 0.9, opacity: 0 }} 
          className="bg-white rounded-3xl max-w-6xl w-full max-h-[95vh] overflow-y-auto" 
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white z-10 px-6 md:px-8 py-5 border-b flex justify-between items-center">
            <div>
              <h3 className="text-xl md:text-2xl font-bold flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                  <FiImage className="h-5 w-5" style={{ color: primaryColor }} />
                </span>
                Testez votre photo sur nos modèles
              </h3>
              <p className="text-gray-500 text-sm mt-1">Uploadez une photo et visualisez-la sur tous nos templates</p>
            </div>
            <button onClick={() => setShowPhotoPreview(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <FiX className="h-6 w-6" />
            </button>
          </div>
          
          <div className="p-6 md:p-8">
            {/* Step 1: Photo Upload with Recommendations */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-lg flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: primaryColor }}>1</span>
                  Uploadez votre photo de test
                </h4>
              </div>
              
              {/* Format Recommendations */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 mb-6 border border-blue-100">
                <h5 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <FiInfo className="h-4 w-4" /> Recommandations pour un meilleur rendu
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(FORMAT_RECOMMENDATIONS).map(([key, rec]) => (
                    <div key={key} className="bg-white/80 rounded-xl p-4 border border-blue-100">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{rec.icon}</span>
                        <span className="font-semibold text-gray-800">{rec.label}</span>
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">{rec.ratio}</span>
                      </div>
                      <p className="text-sm text-gray-600">{rec.description}</p>
                      <p className="text-xs text-gray-400 mt-1">Min: {rec.minWidth}×{rec.minHeight}px</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Upload Zone */}
              {!uploadedPhoto ? (
                <div className="space-y-6">
                  {/* Test Photos Selection */}
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl p-5 border border-amber-200">
                    <h5 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                      🎯 Utilisez une photo de test
                    </h5>
                    <p className="text-sm text-amber-700 mb-4">Cliquez sur une photo de démonstration pour voir le rendu sur tous les modèles</p>
                    <div className="grid grid-cols-3 gap-4">
                      {Object.entries(TEST_PHOTOS).map(([format, photo]) => (
                        <button
                          key={format}
                          onClick={() => handleTestPhotoSelect(format)}
                          className="group relative rounded-xl overflow-hidden border-2 border-amber-200 hover:border-amber-400 transition-all hover:shadow-lg"
                        >
                          <img src={photo.url} alt={photo.label} className="w-full aspect-square object-cover group-hover:scale-105 transition-transform" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                          <div className="absolute bottom-0 left-0 right-0 p-2 text-white text-center">
                            <p className="font-semibold text-sm">{photo.label}</p>
                            <p className="text-xs text-white/70 capitalize">{format}</p>
                          </div>
                          <div className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center bg-amber-400 text-black opacity-0 group-hover:opacity-100 transition-opacity">
                            <FiCheck className="h-4 w-4" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Or separator */}
                  <div className="flex items-center gap-4">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-gray-400 text-sm font-medium">OU</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                  
                  {/* Custom Upload */}
                  <label className="block border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-all group">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center bg-gray-100 group-hover:bg-gray-200 transition-colors">
                      <FiUpload className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="text-gray-700 font-semibold mb-2">Uploadez votre propre photo</p>
                    <p className="text-gray-400 text-sm">PNG, JPG • Résolution recommandée : 800×600px minimum</p>
                    <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                  </label>
                </div>
              ) : (
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  {/* Uploaded Photo */}
                  <div className="relative">
                    <img src={uploadedPhoto} alt="Votre photo" className="max-h-48 rounded-xl shadow-lg border-2 border-gray-200" />
                    <button 
                      onClick={() => { setUploadedPhoto(null); setPhotoDimensions(null); setPhotoQuality(null); }} 
                      className="absolute -top-2 -right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-lg"
                    >
                      <FiX className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {/* Photo Analysis */}
                  {photoDimensions && (
                    <div className="flex-1 bg-gray-50 rounded-xl p-4 border">
                      <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <FiCheck className="h-4 w-4 text-green-500" /> Analyse de votre photo
                      </h5>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="bg-white rounded-lg p-3 border">
                          <p className="text-gray-500 text-xs mb-1">Dimensions</p>
                          <p className="font-semibold">{photoDimensions.width} × {photoDimensions.height}px</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border">
                          <p className="text-gray-500 text-xs mb-1">Orientation</p>
                          <p className="font-semibold capitalize flex items-center gap-2">
                            {FORMAT_RECOMMENDATIONS[photoDimensions.orientation]?.icon} {photoDimensions.orientation}
                          </p>
                        </div>
                        <div className="col-span-2 bg-white rounded-lg p-3 border">
                          <p className="text-gray-500 text-xs mb-1">Qualité</p>
                          <p className={`font-semibold flex items-center gap-2 ${photoQuality === 'bonne' ? 'text-green-600' : 'text-orange-500'}`}>
                            {photoQuality === 'bonne' ? <><FiCheck className="h-4 w-4" /> Bonne qualité</> : <><FiAlertCircle className="h-4 w-4" /> Résolution faible</>}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-3">
                        💡 Modèles recommandés : {photoDimensions.orientation === 'portrait' ? 'Bandelette, Mariage' : photoDimensions.orientation === 'paysage' ? 'Carte Postale, Corporate' : 'Tous les modèles'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Step 2: Preview on ALL Templates */}
            {uploadedPhoto && (
              <div className="border-t pt-8">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="font-semibold text-lg flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: primaryColor }}>2</span>
                    Aperçu sur tous les modèles
                  </h4>
                  <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                    <button 
                      onClick={() => setViewMode('grid')} 
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'grid' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <FiGrid className="inline-block mr-1 h-4 w-4" /> Grille
                    </button>
                    <button 
                      onClick={() => setViewMode('single')} 
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'single' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <FiMaximize className="inline-block mr-1 h-4 w-4" /> Grand
                    </button>
                  </div>
                </div>
                
                {/* Grid View - Show all templates with photo */}
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {PHOTO_TEMPLATES.map((template) => (
                      <motion.div
                        key={template.id}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => { setSelectedTemplate(template); setViewMode('single'); }}
                        className={`relative rounded-2xl overflow-hidden cursor-pointer border-2 transition-all group ${
                          selectedTemplate?.id === template.id 
                            ? 'ring-4 ring-yellow-300 border-yellow-400' 
                            : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'
                        }`}
                      >
                        {/* Template with user photo overlay */}
                        <div className="relative aspect-[4/3] bg-gray-100">
                          <img 
                            src={template.image} 
                            alt={template.name} 
                            className="w-full h-full object-cover"
                            style={{ imageRendering: 'crisp-edges' }}
                            loading="lazy"
                          />
                          {/* User photo positioned inside template */}
                          <div 
                            className="absolute overflow-hidden rounded-sm"
                            style={{
                              top: template.photoPosition?.top || '10%',
                              left: template.photoPosition?.left || '10%',
                              width: template.photoPosition?.width || '80%',
                              height: template.photoPosition?.height || '65%',
                            }}
                          >
                            <img 
                              src={uploadedPhoto} 
                              alt="Votre photo" 
                              className="w-full h-full object-cover"
                              style={{ filter: 'brightness(0.95) contrast(1.02)' }}
                            />
                          </div>
                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <span className="opacity-0 group-hover:opacity-100 transition-opacity px-4 py-2 bg-white rounded-full text-sm font-semibold shadow-lg">
                              <FiMaximize className="inline-block mr-1 h-4 w-4" /> Agrandir
                            </span>
                          </div>
                        </div>
                        {/* Template info */}
                        <div className="p-3 bg-white">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-sm text-gray-900">{template.name}</p>
                              <p className="text-xs text-gray-500">{template.overlay}</p>
                            </div>
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">{template.ratio}</span>
                          </div>
                          {/* Format match indicator */}
                          {photoDimensions && (
                            <div className={`mt-2 text-xs flex items-center gap-1 ${
                              photoDimensions.orientation === template.format ? 'text-green-600' : 'text-orange-500'
                            }`}>
                              {photoDimensions.orientation === template.format 
                                ? <><FiCheck className="h-3 w-3" /> Format idéal pour votre photo</>
                                : <><FiAlertCircle className="h-3 w-3" /> Recadrage possible</>
                              }
                            </div>
                          )}
                        </div>
                        {/* Selected indicator */}
                        {selectedTemplate?.id === template.id && (
                          <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: primaryColor }}>
                            <FiCheck className="h-4 w-4 text-black" />
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  /* Single View - Large preview of selected template */
                  <div className="bg-gray-100 rounded-2xl p-6 md:p-8">
                    {selectedTemplate ? (
                      <div className="flex flex-col items-center">
                        <div className="relative max-w-2xl mx-auto w-full">
                          <img src={selectedTemplate.image} alt={selectedTemplate.name} className="w-full rounded-xl shadow-2xl" />
                          {/* User photo positioned inside template */}
                          <div 
                            className="absolute overflow-hidden rounded-md"
                            style={{
                              top: selectedTemplate.photoPosition?.top || '10%',
                              left: selectedTemplate.photoPosition?.left || '10%',
                              width: selectedTemplate.photoPosition?.width || '80%',
                              height: selectedTemplate.photoPosition?.height || '65%',
                            }}
                          >
                            <img 
                              src={uploadedPhoto} 
                              alt="Votre photo" 
                              className="w-full h-full object-cover"
                              style={{ filter: 'brightness(0.95) contrast(1.02)' }}
                            />
                          </div>
                          {/* Badge */}
                          <div 
                            className="absolute bottom-4 right-4 px-4 py-2 rounded-full text-sm font-bold text-black shadow-lg flex items-center gap-2"
                            style={{ backgroundColor: primaryColor }}
                          >
                            <FiImage className="h-4 w-4" />
                            {selectedTemplate.name}
                          </div>
                        </div>
                        
                        {/* Template details */}
                        <div className="mt-6 text-center">
                          <h5 className="text-xl font-bold text-gray-900">{selectedTemplate.name}</h5>
                          <p className="text-gray-500">{selectedTemplate.overlay}</p>
                          <div className="flex items-center justify-center gap-4 mt-4">
                            <span className="px-3 py-1 bg-white rounded-full text-sm border">Format: {selectedTemplate.ratio}</span>
                            <span className="px-3 py-1 bg-white rounded-full text-sm border capitalize">{selectedTemplate.format}</span>
                          </div>
                        </div>
                        
                        {/* Navigation buttons */}
                        <div className="flex flex-wrap justify-center gap-3 mt-6">
                          <button 
                            onClick={() => {
                              const currentIdx = PHOTO_TEMPLATES.findIndex(t => t.id === selectedTemplate.id);
                              const prevIdx = (currentIdx - 1 + PHOTO_TEMPLATES.length) % PHOTO_TEMPLATES.length;
                              setSelectedTemplate(PHOTO_TEMPLATES[prevIdx]);
                            }}
                            className="px-4 py-2 rounded-xl font-medium text-gray-700 hover:bg-gray-200 transition-colors bg-white border border-gray-300"
                          >
                            <FiChevronLeft className="inline-block mr-1 h-4 w-4" /> Précédent
                          </button>
                          <button 
                            onClick={() => setViewMode('grid')}
                            className="px-4 py-2 rounded-xl font-medium text-gray-700 hover:bg-gray-200 transition-colors bg-white border border-gray-300"
                          >
                            <FiGrid className="inline-block mr-1 h-4 w-4" /> Voir tous
                          </button>
                          <button 
                            onClick={() => {
                              const currentIdx = PHOTO_TEMPLATES.findIndex(t => t.id === selectedTemplate.id);
                              const nextIdx = (currentIdx + 1) % PHOTO_TEMPLATES.length;
                              setSelectedTemplate(PHOTO_TEMPLATES[nextIdx]);
                            }}
                            className="px-4 py-2 rounded-xl font-medium text-gray-700 hover:bg-gray-200 transition-colors bg-white border border-gray-300"
                          >
                            Suivant <FiChevronRight className="inline-block ml-1 h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-gray-500">Cliquez sur un modèle dans la grille pour l'agrandir</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Note */}
                <p className="text-center text-gray-500 mt-6 text-sm">
                  💡 Rendu approximatif - Votre photo sera parfaitement intégrée au cadre par nos graphistes professionnels !
                </p>
                
                {/* Action Buttons */}
                <div className="flex flex-wrap justify-center gap-4 mt-6">
                  <button 
                    onClick={handleBookingClick}
                    className="px-8 py-3 rounded-xl font-semibold text-black hover:scale-105 transition-transform shadow-lg"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <FiCalendar className="inline-block mr-2 h-4 w-4" />
                    Réserver maintenant
                  </button>
                  <label className="px-8 py-3 rounded-xl font-semibold text-gray-700 hover:bg-gray-100 transition-colors bg-white border border-gray-300 cursor-pointer">
                    <FiUpload className="inline-block mr-2 h-4 w-4" />
                    Changer de photo
                    <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                  </label>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    );
  };

  // Render section
  const renderSection = (section, index) => {
    const { type, content = {} } = section;
    
    // Skip hero if it's first (rendered separately)
    if (index === 0 && isHeroFirst) return null;
    
    switch(type) {
      case 'hero_fullscreen':
      case 'hero_video':
        // Build YouTube URL with time parameters
        const buildVideoUrl = () => {
          if (content.videoType === 'upload' && content.uploadedVideoUrl) {
            return null; // Use HTML5 video instead
          }
          const videoId = content.videoUrl ? (content.videoUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/) || [])[1] : heroVideoId;
          let url = `https://www.youtube.com/embed/${videoId}?autoplay=1&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`;
          url += content.videoMuted !== false ? '&mute=1' : '';
          url += content.videoLoop !== false ? `&loop=1&playlist=${videoId}` : '';
          if (content.videoStartTime > 0) url += `&start=${content.videoStartTime}`;
          if (content.videoEndTime > 0) url += `&end=${content.videoEndTime}`;
          return url;
        };
        
        const isUploadedVideo = content.videoType === 'upload' && content.uploadedVideoUrl;
        const youtubeUrl = buildVideoUrl();
        
        return (
          <section className="relative h-[85vh] flex items-center justify-center overflow-hidden">
            {/* Video Background */}
            <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
              {isUploadedVideo ? (
                <video
                  autoPlay
                  muted={content.videoMuted !== false}
                  loop={content.videoLoop !== false}
                  playsInline
                  className="absolute top-1/2 left-1/2 min-w-full min-h-full w-auto h-auto -translate-x-1/2 -translate-y-1/2 object-cover"
                  style={{ minWidth: '100vw', minHeight: '85vh' }}
                >
                  <source src={content.uploadedVideoUrl} type="video/mp4" />
                  <source src={content.uploadedVideoUrl} type="video/webm" />
                </video>
              ) : (
                // Only load YouTube iframe if NOT in an iframe (Constructor preview)
                // This prevents nested iframe issues and improves preview performance
                !isInIframe ? (
                  <iframe
                    src={youtubeUrl}
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 scale-150"
                    style={{ 
                      border: 'none', 
                      width: '100vw',
                      height: '100vh',
                      minWidth: '177.78vh',
                      minHeight: '100vh'
                    }}
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    title="Background Video"
                  />
                ) : (
                  // Show a placeholder when in iframe - positioned above everything
                  <div className="absolute inset-0 z-10 bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
                    <div className="text-center text-white px-6">
                      <FiPlay className="w-20 h-20 mx-auto mb-4 text-amber-500/80" />
                      <p className="text-xl font-semibold mb-2">Aperçu vidéo désactivé</p>
                      <p className="text-sm text-white/60 max-w-xs mx-auto">Pour voir la page avec la vidéo, cliquez sur <span className="text-amber-500 font-medium">"Ouvrir"</span> en haut à droite</p>
                    </div>
                  </div>
                )
              )}
            </div>
            {/* Fallback image if video fails */}
            {content.backgroundImage && (
              <div className="absolute inset-0 w-full h-full">
                <img src={content.backgroundImage} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            {/* Overlay */}
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${content.overlayOpacity || 0.5})` }} />
            {/* Content */}
            <div className="relative z-10 text-center px-6 max-w-5xl">
              <motion.h1 
                initial={{ opacity: 0, y: 40 }} 
                animate={{ opacity: 1, y: 0 }} 
                className="text-5xl md:text-7xl font-black mb-8 [&_strong]:font-black" 
                style={{ background: `linear-gradient(135deg, #fff 0%, ${primaryColor} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                dangerouslySetInnerHTML={{ __html: content.title || '' }}
              />
              <motion.p 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                transition={{ delay: 0.3 }} 
                className="text-xl md:text-2xl mb-12 text-white/90 [&_strong]:font-bold"
                dangerouslySetInnerHTML={{ __html: content.subtitle || '' }}
              />
              {content.buttonText && (
                <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} onClick={handleBookingClick} className="px-12 py-5 rounded-full font-bold text-lg text-black shadow-2xl hover:scale-105 transition-transform" style={{ backgroundColor: primaryColor }}>
                  {content.buttonText}
                </motion.button>
              )}
            </div>
            {/* Scroll indicator */}
            {content.showScrollIndicator !== false && (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                transition={{ delay: 1 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2"
              >
                <motion.div 
                  animate={{ y: [0, 10, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                  className="w-8 h-14 rounded-full border-2 border-white/40 flex items-start justify-center pt-2"
                >
                  <div className="w-1 h-3 bg-white/60 rounded-full" />
                </motion.div>
              </motion.div>
            )}
            
            {/* Preview mode indicator - only show in iframe */}
            {isInIframe && !isUploadedVideo && (
              <div className="absolute top-4 right-4 z-50 bg-black/80 backdrop-blur-sm rounded-lg px-4 py-2 border border-amber-500/30">
                <p className="text-amber-500 text-xs font-medium flex items-center gap-2">
                  <FiPlay className="w-4 h-4" />
                  Vidéo désactivée en aperçu
                </p>
              </div>
            )}
          </section>
        );

      case 'services_carousel':
        // Use content.items from database, with fallback to default
        const serviceCards = content.items && content.items.length > 0 
          ? content.items.map(item => ({
              title: item.title || 'Prestation',
              slogan: item.description || item.slogan || '',
              image: item.image || '',
              link: item.link ? (item.link.includes('-presentation') ? item.link : `${item.link}-presentation`) : ''
            }))
          : [
              {
                title: 'Le PhotoBooth',
                slogan: 'Prenez la pose, notre PhotoBooth s\'occupe du reste !',
                image: '/api/media/file/27ce19d7-750d-4def-bff6-1b53cbc51ebe/dd3c593b-1230-4955-ada3-f27cf185802d.png',
                link: 'photobooth-presentation'
              },
              {
                title: 'Le VideoBooth 360',
                slogan: 'Des moments de folie enregistrés en 360° !',
                image: '/api/media/file/27ce19d7-750d-4def-bff6-1b53cbc51ebe/f7f2e15c-e002-4d6b-a8fe-4bb42bd40c00.png',
                link: 'videobooth-360-presentation'
              },
              {
                title: 'Le Livre d\'Or Vidéo',
                slogan: 'Les mots ne s\'écrivent plus, mais se vivent !',
                image: '/api/media/file/27ce19d7-750d-4def-bff6-1b53cbc51ebe/5666488e-b1bc-4e8e-9d7f-32f932e59c31.jpg',
                link: 'livre-dor-presentation'
              }
            ];
        
        // Duplicate cards multiple times for seamless infinite loop
        const infiniteCards = [...serviceCards, ...serviceCards, ...serviceCards, ...serviceCards];
        
        return (
          <section className="bg-black py-16 md:py-20 overflow-hidden">
            <div className="max-w-7xl mx-auto px-6">
              <h2 
                className="text-2xl md:text-5xl font-bold text-white text-center mb-2 md:mb-4 [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold"
                dangerouslySetInnerHTML={{ __html: content.title || 'Nos Prestations' }}
              />
              {content.subtitle && (
                <p 
                  className="text-white/60 text-center mb-8 md:mb-12 text-sm md:text-xl [&_strong]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.subtitle }}
                />
              )}
            </div>
            
            {/* Container with carousel */}
            <div className="relative">
              {/* Navigation arrows */}
              <button 
                onClick={() => {
                  const container = document.getElementById('services-carousel-container');
                  if (container) {
                    container.style.animation = 'none';
                    container.scrollLeft -= 300;
                    setTimeout(() => container.style.animation = 'scrollServices 25s linear infinite', 100);
                  }
                }}
                className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center bg-black/60 backdrop-blur-sm border border-amber-500/50 text-white hover:bg-amber-500/20 transition-all hover:scale-110"
                style={{ borderColor: primaryColor }}
              >
                <FiChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
              </button>
              <button 
                onClick={() => {
                  const container = document.getElementById('services-carousel-container');
                  if (container) {
                    container.style.animation = 'none';
                    container.scrollLeft += 300;
                    setTimeout(() => container.style.animation = 'scrollServices 25s linear infinite', 100);
                  }
                }}
                className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center bg-black/60 backdrop-blur-sm border border-amber-500/50 text-white hover:bg-amber-500/20 transition-all hover:scale-110"
                style={{ borderColor: primaryColor }}
              >
                <FiChevronRight className="w-5 h-5 md:w-6 md:h-6" />
              </button>
              
              {/* Gradient overlays */}
              <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 z-10 pointer-events-none" style={{ background: 'linear-gradient(to right, rgb(0,0,0) 0%, rgba(0,0,0,0.8) 40%, rgba(0,0,0,0) 100%)' }} />
              <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 z-10 pointer-events-none" style={{ background: 'linear-gradient(to left, rgb(0,0,0) 0%, rgba(0,0,0,0.8) 40%, rgba(0,0,0,0) 100%)' }} />
              
              {/* Scrollable container - snap scroll on mobile, auto-scroll on desktop */}
              <div 
                id="services-carousel-container"
                className="flex gap-4 md:gap-6 overflow-x-auto scrollbar-hide px-4 md:px-12 snap-x snap-mandatory md:snap-none"
                style={{
                  width: 'fit-content',
                  scrollBehavior: 'smooth'
                }}
                onMouseEnter={(e) => {
                  if (window.innerWidth >= 768) e.currentTarget.style.animationPlayState = 'paused';
                }}
                onMouseLeave={(e) => {
                  if (window.innerWidth >= 768) e.currentTarget.style.animationPlayState = 'running';
                }}
                ref={(el) => {
                  if (el && window.innerWidth >= 768) {
                    el.style.animation = 'scrollServices 25s linear infinite';
                  }
                }}
              >
                <style>{`
                  @keyframes scrollServices {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(calc(-50%)); }
                  }
                  @media (max-width: 767px) {
                    #services-carousel-container {
                      animation: none !important;
                    }
                  }
                `}</style>
                {infiniteCards.map((card, i) => (
                  <motion.div 
                    key={i}
                    className="flex-shrink-0 w-[85vw] md:w-[350px] snap-center group cursor-pointer"
                    onClick={() => navigateToPage(card.link)}
                    whileHover={{ scale: 1.03, y: -5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="relative h-[280px] md:h-[400px] rounded-xl md:rounded-2xl overflow-hidden bg-gray-900 border border-white/10 hover:border-amber-500/50 transition-all shadow-xl">
                      {/* Image */}
                      <img 
                        src={card.image} 
                        alt={card.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                      
                      {/* Content */}
                      <div className="absolute bottom-0 left-0 right-0 p-3 md:p-6">
                        <h3 className="text-sm md:text-2xl font-bold text-white mb-1 md:mb-2 line-clamp-2">{card.title}</h3>
                        <RichTextContent content={card.slogan} className="text-white/80 italic text-xs md:text-sm mb-2 md:mb-4 hidden md:block" />
                        <span 
                          className="inline-flex items-center gap-1 md:gap-2 text-xs md:text-sm font-semibold group-hover:gap-2 md:group-hover:gap-3 transition-all"
                          style={{ color: primaryColor }}
                        >
                          Découvrir <FiArrowRight className="w-3 h-3 md:w-4 md:h-4" />
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'features_grid':
        const isModelsPage = content.title?.toLowerCase().includes('modèle') || 
                            content.title?.toLowerCase().includes('modele') ||
                            content.title?.toLowerCase().includes('choisissez') ||
                            content.title?.toLowerCase().includes('plateformes') ||
                            currentPage?.slug?.includes('modele');
        return (
          <section className="py-24 px-6 bg-gradient-to-b from-black to-gray-900">
            <div className="max-w-7xl mx-auto">
              <h2 
                className="text-4xl font-bold text-white text-center mb-6 [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold"
                dangerouslySetInnerHTML={{ __html: content.title || '' }}
              />
              {content.subtitle && (
                <p 
                  className="text-white/60 text-center mb-8 [&_strong]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.subtitle }}
                />
              )}
              
              {/* Button to test with photo - shown on models pages */}
              {isModelsPage && (
                <div className="text-center mb-12">
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <button 
                      onClick={() => setShowPhotoPreview(true)}
                      className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg text-black hover:scale-105 transition-transform shadow-2xl"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <FiImage className="h-5 w-5" /> Aperçu des formats
                    </button>
                  </div>
                  <p className="text-white/50 text-sm mt-3">Découvrez les différents formats de photos disponibles</p>
                </div>
              )}
              
              <div className={`grid gap-6 ${isModelsPage ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-2 md:grid-cols-4'}`}>
                {content.items?.map((item, i) => (
                  <motion.div 
                    key={i} 
                    initial={{ opacity: 0, scale: 0.9 }} 
                    whileInView={{ opacity: 1, scale: 1 }} 
                    viewport={{ once: true }} 
                    whileHover={{ y: -10 }} 
                    className={`group relative overflow-hidden cursor-pointer ${isModelsPage ? 'rounded-2xl bg-white/5 border border-white/10 hover:border-white/30' : 'rounded-3xl aspect-square'}`}
                    onClick={() => isModelsPage ? (() => { setSelectedTemplate({ id: i, name: item.title, image: item.image, overlay: item.description }); setShowPhotoPreview(true); })() : navigateToPage(item.link || item.title?.toLowerCase().replace(/\s+/g, '-'))}
                  >
                    <div className={`${isModelsPage ? 'aspect-[4/3]' : 'w-full h-full'} overflow-hidden`}>
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    </div>
                    <div className={`absolute inset-0 bg-gradient-to-t ${isModelsPage ? 'from-black/90 via-black/40' : 'from-black via-black/60'} to-transparent`} />
                    <div className={`absolute bottom-0 left-0 right-0 ${isModelsPage ? 'p-6' : 'p-6'}`}>
                      <h3 className={`font-bold text-white ${isModelsPage ? 'text-xl mb-2' : 'text-xl'}`}>{item.title}</h3>
                      {isModelsPage && item.description && (
                        <RichTextContent content={item.description} className="text-white/60 text-sm mb-4" />
                      )}
                      {isModelsPage && (
                        <button 
                          className="inline-flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all"
                          style={{ color: primaryColor }}
                        >
                          <FiUpload className="h-4 w-4" /> Tester ce modèle
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'gift_card':
        // Gift Card order form section
        return (
          <section className="py-24 px-6 bg-gradient-to-b from-amber-900/20 to-black" id="carte-cadeau">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-12"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 text-amber-400 mb-6">
                  <FiGift className="h-5 w-5" />
                  <span className="font-semibold">Carte Cadeau</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  {content.title || 'Offrez une expérience inoubliable'}
                </h2>
                <p className="text-white/60 text-lg max-w-2xl mx-auto">
                  {content.subtitle || 'Faites plaisir à vos proches avec une carte cadeau personnalisée'}
                </p>
              </motion.div>

              {giftCardSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-500/20 border border-green-500/50 rounded-2xl p-8 text-center"
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500 flex items-center justify-center">
                    <FiCheck className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Demande envoyée !</h3>
                  <p className="text-white/70">Nous vous contacterons rapidement pour finaliser votre carte cadeau.</p>
                </motion.div>
              ) : (
                <motion.form
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  onSubmit={handleGiftCardSubmit}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 space-y-6"
                >
                  {/* Prestation Selection */}
                  <div>
                    <label className="block text-white font-medium mb-3">Choisissez la prestation à offrir</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {prestationOptions.map(option => (
                        <div
                          key={option.value}
                          onClick={() => setGiftCardForm(prev => ({ ...prev, prestation: option.value, amount: option.minPrice }))}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            giftCardForm.prestation === option.value
                              ? 'border-amber-500 bg-amber-500/20'
                              : 'border-white/20 hover:border-white/40'
                          }`}
                        >
                          <div className="font-bold text-white">{option.label}</div>
                          <div className="text-sm text-white/60">À partir de {option.minPrice}€</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-white font-medium mb-2">Montant de la carte</label>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min={prestationOptions.find(o => o.value === giftCardForm.prestation)?.minPrice || 149}
                        max={prestationOptions.find(o => o.value === giftCardForm.prestation)?.maxPrice || 399}
                        step={10}
                        value={giftCardForm.amount}
                        onChange={(e) => setGiftCardForm(prev => ({ ...prev, amount: parseInt(e.target.value) }))}
                        className="flex-1 accent-amber-500"
                      />
                      <div className="text-3xl font-bold text-amber-500 min-w-[100px] text-right">{giftCardForm.amount}€</div>
                    </div>
                  </div>

                  {/* Recipient Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-1">Prénom du destinataire *</label>
                      <input
                        type="text"
                        required
                        value={giftCardForm.recipient_name}
                        onChange={(e) => setGiftCardForm(prev => ({ ...prev, recipient_name: e.target.value }))}
                        className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-amber-500"
                        placeholder="Prénom de la personne qui recevra le cadeau"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-1">Email du destinataire (optionnel)</label>
                      <input
                        type="email"
                        value={giftCardForm.recipient_email}
                        onChange={(e) => setGiftCardForm(prev => ({ ...prev, recipient_email: e.target.value }))}
                        className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-amber-500"
                        placeholder="Pour envoyer directement la carte"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-white/70 text-sm mb-1">Votre message personnalisé</label>
                    <textarea
                      value={giftCardForm.message}
                      onChange={(e) => setGiftCardForm(prev => ({ ...prev, message: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-amber-500 resize-none"
                      placeholder="Ex: Joyeux anniversaire ! Profite bien de cette expérience unique..."
                    />
                  </div>

                  {/* Sender Info */}
                  <div className="pt-4 border-t border-white/10">
                    <h4 className="text-white font-medium mb-4">Vos coordonnées</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white/70 text-sm mb-1">Votre prénom *</label>
                        <input
                          type="text"
                          required
                          value={giftCardForm.sender_first_name}
                          onChange={(e) => setGiftCardForm(prev => ({ ...prev, sender_first_name: e.target.value }))}
                          className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-amber-500"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-1">Votre nom *</label>
                        <input
                          type="text"
                          required
                          value={giftCardForm.sender_last_name}
                          onChange={(e) => setGiftCardForm(prev => ({ ...prev, sender_last_name: e.target.value }))}
                          className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-amber-500"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-1">Votre email *</label>
                        <input
                          type="email"
                          required
                          value={giftCardForm.sender_email}
                          onChange={(e) => setGiftCardForm(prev => ({ ...prev, sender_email: e.target.value }))}
                          className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-amber-500"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-1">Votre téléphone *</label>
                        <input
                          type="tel"
                          required
                          value={giftCardForm.sender_phone}
                          onChange={(e) => setGiftCardForm(prev => ({ ...prev, sender_phone: e.target.value }))}
                          className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-amber-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={giftCardSubmitting}
                    className="w-full py-4 rounded-xl font-bold text-lg text-black hover:scale-[1.02] transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {giftCardSubmitting ? (
                      <>Envoi en cours...</>
                    ) : (
                      <>
                        <FiGift className="h-5 w-5" />
                        Commander la carte cadeau - {giftCardForm.amount}€
                      </>
                    )}
                  </button>
                </motion.form>
              )}
            </div>
          </section>
        );

      case 'pricing':
        // Map pricing item names to BookingModal prestation IDs
        const getPrestationId = (itemName) => {
          const name = (itemName || '').toLowerCase();
          if (name.includes('photobooth') && !name.includes('360')) return 'photobooth';
          if (name.includes('360') && name.includes('80')) return 'videobooth80';
          if (name.includes('360') && name.includes('100')) return 'videobooth100';
          if (name.includes('360')) return 'videobooth80'; // Default to 80cm
          if (name.includes('livre') || name.includes('or')) return 'livreor';
          if (name.includes('formule') || name.includes('combin')) return 'formule';
          return null;
        };
        
        // Extract duration hours from string like "3h" or "3 heures" or "illimitée"
        const getDurationFromItem = (item) => {
          if (!item.duration) return null;
          const duration = item.duration.toLowerCase();
          if (duration.includes('illimit')) return 'illimitée';
          const match = duration.match(/(\d+)/);
          return match ? parseInt(match[1]) : null;
        };
        
        // Support both 'items' and 'plans' property names
        const pricingItems = content.plans || content.items || [];
        
        // Determine grid columns based on item count
        const itemCount = pricingItems.length;
        const gridCols = itemCount === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : itemCount === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' : 'md:grid-cols-3';
        
        return (
          <section className="py-24 px-6 bg-gradient-to-b from-black to-gray-900" id="tarifs">
            <div className="max-w-7xl mx-auto">
              <h2 
                className="text-3xl md:text-4xl font-bold text-white text-center mb-4 [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold"
                dangerouslySetInnerHTML={{ __html: content.title || '' }}
              />
              {content.subtitle && (
                <p 
                  className="text-white/60 text-center mb-12 text-lg [&_strong]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.subtitle }}
                />
              )}
              <div className={`grid ${gridCols} gap-6`}>
                {pricingItems.map((item, i) => {
                  // Couleurs distinctives pour chaque formule
                  const planColors = [
                    { bg: 'from-slate-800/80 to-slate-900/80', border: '#64748b', accent: '#94a3b8' },
                    { bg: 'from-blue-900/50 to-slate-900/80', border: '#3b82f6', accent: '#60a5fa' },
                    { bg: 'from-purple-900/50 to-slate-900/80', border: '#8b5cf6', accent: '#a78bfa' },
                    { bg: 'from-amber-900/50 to-slate-900/80', border: primaryColor, accent: primaryColor }
                  ];
                  const colors = planColors[i % planColors.length];
                  const isHighlighted = item.popular || item.highlighted || i === pricingItems.length - 1;
                  
                  return (
                  <motion.div 
                    key={i} 
                    initial={{ opacity: 0, y: 30 }} 
                    whileInView={{ opacity: 1, y: 0 }} 
                    viewport={{ once: true }} 
                    transition={{ delay: i * 0.1 }}
                    className={`relative rounded-3xl p-6 bg-gradient-to-b ${colors.bg} border-2 hover:scale-[1.02] transition-transform`}
                    style={{ borderColor: isHighlighted ? primaryColor : colors.border }}
                  >
                    {isHighlighted && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-sm font-bold text-black" style={{ backgroundColor: primaryColor }}>
                        ⭐ POPULAIRE
                      </div>
                    )}
                    <h3 className="text-xl font-bold text-white mb-1">{item.name}</h3>
                    {item.description && (
                      <RichTextContent content={item.description} className="text-white/50 text-sm mb-3" />
                    )}
                    {item.duration && (
                      <p className="text-white/50 text-sm mb-3">{item.duration}</p>
                    )}
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-4xl font-black" style={{ color: isHighlighted ? primaryColor : colors.accent }}>{item.price}</span>
                      <span className="text-lg text-white/60">{item.period || '€'}</span>
                      {item.originalPrice && (
                        <span className="text-lg text-white/40 line-through">{item.originalPrice}</span>
                      )}
                    </div>
                    <ul className="space-y-2 mb-6">
                      {item.features?.map((feat, fi) => (
                        <li key={fi} className="flex items-start gap-2 text-sm text-white/80">
                          <FiCheck className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: isHighlighted ? primaryColor : colors.accent }} />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                    <button 
                      onClick={(e) => handleBookingClick(e, item.name, getDurationFromItem(item))} 
                      className={`w-full py-3 rounded-xl font-bold transition-all hover:brightness-110 text-sm ${isHighlighted ? 'text-black' : 'text-white'}`}
                      style={{ backgroundColor: isHighlighted ? primaryColor : colors.accent }}
                    >
                      {item.buttonText || 'Réserver cette formule'}
                    </button>
                  </motion.div>
                  );
                })}
              </div>
            </div>
          </section>
        );

      case 'pricing_tabs':
        // Pricing with tabs (for VideoBooth 80cm/100cm) - Use dedicated component to respect React hooks rules
        return (
          <PricingTabsSection 
            content={content} 
            primaryColor={primaryColor} 
            onBookingClick={handleBookingClick} 
          />
        );

      case 'testimonials':
        // Redirect to Google Reviews section - no duplicate rendering
        return null;

      case 'cta':
        // Fix: Keep the "?" on the same line as the last word
        const ctaTitle = (content.title || '').replace(/(\S+)\s*\?\s*$/g, '<span class="whitespace-nowrap">$1\u00A0?</span>');
        return (
          <section className="py-20 px-6 text-center" style={{ backgroundColor: primaryColor }}>
            <h2 
              className="text-2xl md:text-4xl font-bold text-black mb-4 [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold px-2"
              dangerouslySetInnerHTML={{ __html: ctaTitle }}
            />
            {content.subtitle && (
              <p 
                className="text-black/70 text-lg mb-8 max-w-2xl mx-auto [&_strong]:font-bold"
                dangerouslySetInnerHTML={{ __html: content.subtitle }}
              />
            )}
            <button onClick={handleBookingClick} className="px-10 py-4 bg-black text-white rounded-full font-bold text-lg hover:scale-105 transition-transform">
              {content.buttonText || 'Réserver'}
            </button>
          </section>
        );

      case 'partners':
      case 'partners_logos':
        // Partners logos are now displayed in the scrolling banner at the bottom
        // This case returns null to avoid duplicate display
        return null;

      case 'hero':
        // Simple hero section without video
        const scrollToNextSection = () => {
          window.scrollBy({ top: window.innerHeight * 0.7, behavior: 'smooth' });
        };
        return (
          <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden" style={{ background: `linear-gradient(135deg, #000 0%, #1a1a1a 100%)` }}>
            <div className="absolute inset-0 opacity-30">
              {content.backgroundImage && (
                <img 
                  src={content.backgroundImage} 
                  alt="" 
                  className="w-full h-full object-cover"
                  style={{ objectPosition: `${content.imagePositionX ?? 50}% ${content.imagePositionY ?? 50}%` }}
                />
              )}
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
            <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
              <motion.h1 
                initial={{ opacity: 0, y: 30 }} 
                animate={{ opacity: 1, y: 0 }} 
                className="text-4xl md:text-6xl font-black mb-6 text-white [&_strong]:font-black [&_.gold-shimmer-text]:font-bold"
                dangerouslySetInnerHTML={{ __html: content.title || '' }}
              />
              {content.subtitle && (
                <motion.p 
                  initial={{ opacity: 0 }} 
                  animate={{ opacity: 1 }} 
                  transition={{ delay: 0.2 }} 
                  className="text-lg md:text-xl text-white/80 mb-8 [&_strong]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.subtitle }}
                />
              )}
              {content.buttonText && (
                <motion.button 
                  initial={{ opacity: 0 }} 
                  animate={{ opacity: 1 }} 
                  transition={{ delay: 0.4 }}
                  onClick={handleBookingClick} 
                  className="px-10 py-4 rounded-full font-bold text-lg text-black hover:scale-105 transition-transform" 
                  style={{ backgroundColor: primaryColor }}
                >
                  {content.buttonText}
                </motion.button>
              )}
            </div>
            {/* Scroll indicator - centered and clickable */}
            <div className="absolute bottom-6 left-0 right-0 flex justify-center z-10">
              <motion.div 
                animate={{ y: [0, 10, 0] }} 
                transition={{ repeat: Infinity, duration: 2 }} 
                className="cursor-pointer"
                onClick={scrollToNextSection}
              >
                <div className="w-7 h-12 border-2 border-white/30 rounded-full flex justify-center pt-3 hover:border-white/50 transition-colors">
                  <div className="w-1.5 h-4 rounded-full" style={{ backgroundColor: primaryColor }} />
                </div>
              </motion.div>
            </div>
          </section>
        );

      case 'stats':
        // Statistics section - display key metrics
        const statsItems = content.items || content.stats || [
          { value: '+500', label: 'événements' },
          { value: '100%', label: 'satisfaction' },
          { value: '7/7', label: 'disponible' }
        ];
        return (
          <section className="py-16 px-6 bg-gradient-to-b from-black via-black/95 to-black">
            <div className="max-w-6xl mx-auto">
              {content.title && (
                <h2 className="text-3xl font-bold text-center text-white mb-12" style={{ color: primaryColor }}>
                  {content.title}
                </h2>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {statsItems.map((stat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="text-center p-6 rounded-2xl bg-white/5 backdrop-blur-sm"
                  >
                    <div className="text-4xl font-black mb-2" style={{ color: primaryColor }}>
                      {stat.value}
                    </div>
                    <div className="text-white/60 text-sm uppercase tracking-wider">
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'text_image':
        // Text with image section - alternating layout
        const isReversed = content.imagePosition === 'left' || (index % 2 === 1 && !content.imagePosition);
        const textImageContent = content.description || content.text || '';
        const textImageSrc = content.imageUrl || content.image || '';
        const imageSizeMap = {
          small: 'max-h-[300px]',
          medium: 'max-h-[450px]',
          large: 'max-h-[600px]',
          full: 'max-h-none'
        };
        const imageMaxHeight = imageSizeMap[content.imageSize] || imageSizeMap.large;
        return (
          <section className="py-20 px-6 bg-gradient-to-b from-black to-gray-900">
            <div className={`max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center`}>
              <motion.div 
                initial={{ opacity: 0, x: isReversed ? 50 : -50 }} 
                whileInView={{ opacity: 1, x: 0 }} 
                viewport={{ once: true }}
                className={`${isReversed ? 'md:order-2' : ''}`}
              >
                <h2 
                  className="text-3xl md:text-4xl font-bold text-white mb-6 [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.title || '' }}
                />
                {textImageContent.includes('<') ? (
                  <div 
                    className="text-white/70 space-y-4 text-lg leading-relaxed prose prose-invert prose-p:text-white/70 prose-strong:text-white prose-li:text-white/70 prose-em:text-white/90 prose-a:text-white/90 prose-a:no-underline max-w-none"
                    dangerouslySetInnerHTML={{ __html: textImageContent }}
                  />
                ) : (
                  <div className="text-white/70 space-y-4 text-lg leading-relaxed">
                    {textImageContent.split('\n').map((paragraph, pi) => (
                      <p key={pi}>{paragraph}</p>
                    ))}
                  </div>
                )}
                {content.buttonText && (
                  <button 
                    onClick={() => navigateToPage(content.buttonLink || 'contact')}
                    className="mt-8 inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold text-black hover:scale-105 transition-transform"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {content.buttonText} <FiArrowRight />
                  </button>
                )}
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, x: isReversed ? -50 : 50 }} 
                whileInView={{ opacity: 1, x: 0 }} 
                viewport={{ once: true }}
                className={`${isReversed ? 'md:order-1' : ''}`}
              >
                {textImageSrc && (
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                    <img 
                      src={textImageSrc} 
                      alt={content.title} 
                      className={`w-full h-auto object-cover ${imageMaxHeight}`}
                      style={{ objectPosition: `${content.imagePositionX ?? 50}% ${content.imagePositionY ?? 50}%` }}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  </div>
                )}
              </motion.div>
            </div>
          </section>
        );

      case 'text_block':
        // Full-width text content block
        return (
          <section className="py-20 px-6 bg-black">
            <div className="max-w-4xl mx-auto">
              {content.title && (
                <motion.h2 
                  initial={{ opacity: 0, y: 20 }} 
                  whileInView={{ opacity: 1, y: 0 }} 
                  viewport={{ once: true }}
                  className="text-3xl md:text-4xl font-bold text-white mb-8 text-center italic"
                  style={{ color: primaryColor }}
                >
                  {parseHighlightedText(content.title, primaryColor)}
                </motion.h2>
              )}
              <motion.div 
                initial={{ opacity: 0 }} 
                whileInView={{ opacity: 1 }} 
                viewport={{ once: true }}
                className="prose prose-lg prose-invert max-w-none"
              >
                <div className="text-white/80 space-y-6 text-lg leading-relaxed text-center">
                  {content.text?.split('\n').map((paragraph, pi) => (
                    <p key={pi}>{parseHighlightedText(paragraph, primaryColor)}</p>
                  ))}
                </div>
              </motion.div>
              {content.buttonText && (
                <div className="text-center mt-10">
                  <button 
                    onClick={handleBookingClick}
                    className="px-10 py-4 rounded-full font-bold text-lg text-black hover:scale-105 transition-transform"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {content.buttonText}
                  </button>
                </div>
              )}
            </div>
          </section>
        );

      case 'features_list':
        // List of features with icons/checkmarks
        return (
          <section className="py-20 px-6 bg-gradient-to-b from-gray-900 to-black">
            <div className="max-w-6xl mx-auto">
              {content.title && (
                <motion.h2 
                  initial={{ opacity: 0, y: 20 }} 
                  whileInView={{ opacity: 1, y: 0 }} 
                  viewport={{ once: true }}
                  className="text-3xl md:text-4xl font-bold text-white mb-4 text-center [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.title || '' }}
                />
              )}
              {content.subtitle && (
                <p 
                  className="text-white/60 text-center mb-12 text-lg [&_strong]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.subtitle }}
                />
              )}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {content.items?.map((item, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-colors"
                  >
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${primaryColor}20` }}
                    >
                      <FiCheck className="h-6 w-6" style={{ color: primaryColor }} />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{item.title || item}</h3>
                    {item.description && (
                      <RichTextContent content={item.description} className="text-white/60" />
                    )}
                  </motion.div>
                ))}
              </div>
              {content.buttonText && (
                <div className="text-center mt-12">
                  <button 
                    onClick={handleBookingClick}
                    className="px-10 py-4 rounded-full font-bold text-lg text-black hover:scale-105 transition-transform"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {content.buttonText}
                  </button>
                </div>
              )}
            </div>
          </section>
        );

      case 'features_icons':
        // Features with icons in a grid
        return (
          <section className="py-20 px-6 bg-black">
            <div className="max-w-6xl mx-auto">
              {content.title && (
                <h2 
                  className="text-3xl md:text-4xl font-bold text-white text-center mb-12 [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.title }}
                />
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {content.items?.map((item, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="text-center"
                  >
                    <div 
                      className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                      style={{ backgroundColor: `${primaryColor}20` }}
                    >
                      <FiCheck className="h-8 w-8" style={{ color: primaryColor }} />
                    </div>
                    <h3 className="font-bold text-white mb-2">{item.title || item}</h3>
                    {item.description && <RichTextContent content={item.description} className="text-white/60 text-sm" />}
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'text':
        // Simple text section
        const textContent = content.content || content.text || '';
        return (
          <section className="py-20 px-6 bg-gradient-to-b from-gray-900 to-black">
            <div className="max-w-4xl mx-auto">
              {content.title && (
                <h2 
                  className="text-3xl md:text-4xl font-bold text-white text-center mb-8 [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.title }}
                />
              )}
              {textContent.includes('<') ? (
                <div 
                  className="prose prose-lg prose-invert max-w-none text-center prose-p:text-white/80 prose-strong:text-white prose-li:text-white/70 prose-em:text-white/90 prose-a:text-white/90 prose-a:no-underline"
                  dangerouslySetInnerHTML={{ __html: textContent }}
                />
              ) : (
                <div className="prose prose-lg prose-invert max-w-none text-center">
                  <p className="text-white/80 text-lg leading-relaxed">{textContent}</p>
                </div>
              )}
              {content.buttonText && (
                <div className="text-center mt-10">
                  <button 
                    onClick={handleBookingClick}
                    className="px-10 py-4 rounded-full font-bold text-lg text-black hover:scale-105 transition-transform"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {content.buttonText}
                  </button>
                </div>
              )}
            </div>
          </section>
        );

      case 'contact':
        // Contact form section - use data from content or site.contact_info
        const contactInfo = site?.contact_info || {};
        const phoneNumber = content.phone || contactInfo.phone || site?.footer_content?.phone || '06 74 72 95 12';
        const emailAddress = content.email || contactInfo.email || `contact@${site?.domain}`;
        const address = content.address || contactInfo.address || '13800 ISTRES';
        const hours = content.hours || contactInfo.hours || '24/24 & 7/7';
        return (
          <section className="py-20 px-6 bg-black">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12">
                {/* Contact Info */}
                <div>
                  <h2 className="text-3xl font-bold text-white mb-6">{content.title || 'Contactez-nous'}</h2>
                  <p className="text-white/70 mb-8">{content.description || 'Nous sommes à votre écoute pour toutes vos questions.'}</p>
                  
                  <div className="space-y-4">
                    <a href={`tel:${phoneNumber.replace(/\s/g, '')}`} className="flex items-center gap-4 hover:bg-white/5 rounded-xl p-2 -m-2 transition-colors">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                        <FiPhone className="h-5 w-5" style={{ color: primaryColor }} />
                      </div>
                      <div>
                        <p className="text-white/50 text-sm">Téléphone</p>
                        <p className="text-white font-medium">{phoneNumber}</p>
                      </div>
                    </a>
                    <a href={`mailto:${emailAddress}`} className="flex items-center gap-4 hover:bg-white/5 rounded-xl p-2 -m-2 transition-colors">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                        <FiMail className="h-5 w-5" style={{ color: primaryColor }} />
                      </div>
                      <div>
                        <p className="text-white/50 text-sm">Email</p>
                        <p className="text-white font-medium">{emailAddress}</p>
                      </div>
                    </a>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                        <FiClock className="h-5 w-5" style={{ color: primaryColor }} />
                      </div>
                      <div>
                        <p className="text-white/50 text-sm">Horaires</p>
                        <p className="text-white font-medium">{hours}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                        <FiArrowRight className="h-5 w-5" style={{ color: primaryColor }} />
                      </div>
                      <div>
                        <p className="text-white/50 text-sm">Adresse</p>
                        <p className="text-white font-medium">{address}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Contact Form */}
                <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                  <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleBookingClick(e); }}>
                    <div className="grid grid-cols-2 gap-4">
                      <input type="text" placeholder="Prénom" className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/40 focus:border-white/30 focus:outline-none" />
                      <input type="text" placeholder="Nom" className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/40 focus:border-white/30 focus:outline-none" />
                    </div>
                    <input type="email" placeholder="Email" className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/40 focus:border-white/30 focus:outline-none" />
                    <input type="tel" placeholder="Téléphone" className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/40 focus:border-white/30 focus:outline-none" />
                    <select className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white focus:border-white/30 focus:outline-none">
                      <option value="" className="bg-gray-900">Objet de votre demande</option>
                      <option value="devis" className="bg-gray-900">Demande de devis</option>
                      <option value="partenariat" className="bg-gray-900">Proposition de partenariat</option>
                      <option value="presse" className="bg-gray-900">Presse</option>
                      <option value="autre" className="bg-gray-900">Autre</option>
                    </select>
                    <textarea placeholder="Votre demande" rows={4} className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/40 focus:border-white/30 focus:outline-none resize-none" />
                    <button type="submit" className="w-full py-4 rounded-xl font-bold text-black hover:scale-[1.02] transition-transform" style={{ backgroundColor: primaryColor }}>
                      Envoyer ma demande
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        );

      case 'booking_steps':
        // Booking steps - redirect to booking modal
        return (
          <section className="py-20 px-6 bg-gradient-to-b from-gray-900 to-black">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{content.title || 'Réservez votre prestation'}</h2>
              <p className="text-white/70 text-lg mb-10">{content.description || 'Suivez les étapes pour finaliser votre réservation'}</p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                {[
                  { step: 1, title: 'Choisissez', desc: 'Sélectionnez votre prestation et vos options' },
                  { step: 2, title: 'Personnalisez', desc: 'Ajoutez vos préférences et informations' },
                  { step: 3, title: 'Confirmez', desc: 'Validez et recevez votre confirmation' }
                ].map((item) => (
                  <div key={item.step} className="bg-white/5 rounded-2xl p-6 border border-white/10">
                    <div 
                      className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center font-bold text-xl text-black"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {item.step}
                    </div>
                    <h3 className="font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-white/60 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
              
              <button 
                onClick={handleBookingClick}
                className="px-12 py-5 rounded-full font-bold text-lg text-black hover:scale-105 transition-transform shadow-2xl"
                style={{ backgroundColor: primaryColor }}
              >
                <FiCalendar className="inline-block mr-2 h-5 w-5" />
                Commencer la réservation
              </button>
            </div>
          </section>
        );

      case 'services_carousel':
        // Services carousel section (homepage)
        return (
          <section className="py-20 px-6 bg-black">
            <div className="max-w-6xl mx-auto">
              {content.title && (
                <h2 
                  className="text-3xl md:text-4xl font-bold text-white text-center mb-12 [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.title }}
                />
              )}
              <div className="grid md:grid-cols-3 gap-8">
                {(content.items || [
                  { title: 'PhotoBooth', description: 'Photos illimitées pour vos événements', image: 'https://www.justasmile-events.com/wp-content/uploads/2022/07/photobooth-JUSTASMILE-1.png', link: 'location-photobooth' },
                  { title: 'VideoBooth 360', description: 'Vidéos immersives à 360°', image: 'https://www.justasmile-events.com/wp-content/uploads/2023/05/videobooth-accueil.png', link: 'location-videobooth' },
                  { title: 'Livre d\'Or', description: 'Messages vidéo de vos proches', image: 'https://www.justasmile-events.com/wp-content/uploads/2022/12/bapteme.png', link: 'livre-dor' }
                ]).map((service, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.15 }}
                    onClick={() => navigateToPage(service.link)}
                    className="group cursor-pointer relative"
                  >
                    <div className="relative rounded-3xl overflow-hidden mb-4 border border-white/10 hover:border-white/30 transition-colors">
                      <img src={service.image} alt={service.title} className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
                        <p className="text-white/70 text-sm">{service.description}</p>
                      </div>
                    </div>
                    <button className="flex items-center gap-2 font-medium group-hover:gap-3 transition-all" style={{ color: primaryColor }}>
                      Découvrir <FiArrowRight />
                    </button>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'testimonials':
        // Testimonials/Reviews section
        return (
          <section className="py-20 px-6 bg-black">
            <div className="max-w-6xl mx-auto">
              {content.title && (
                <h2 
                  className="text-3xl md:text-4xl font-bold text-white text-center mb-4 [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.title }}
                />
              )}
              {content.subtitle && (
                <p 
                  className="text-white/60 text-center mb-12 [&_strong]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.subtitle }}
                />
              )}
              <div className="grid md:grid-cols-3 gap-8">
                {(content.items || [
                  { name: 'Marie L.', text: 'Un service exceptionnel ! Nos invités ont adoré le photobooth.', rating: 5, event: 'Mariage' },
                  { name: 'Thomas D.', text: 'Le VideoBooth 360 a fait sensation lors de notre soirée entreprise.', rating: 5, event: 'Événement corporate' },
                  { name: 'Sophie M.', text: 'Équipe professionnelle et photos de qualité. Je recommande !', rating: 5, event: 'Anniversaire' }
                ]).map((testimonial, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white/5 rounded-3xl p-8 border border-white/10"
                  >
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating || 5)].map((_, ri) => (
                        <FiStar key={ri} className="h-5 w-5 fill-current" style={{ color: primaryColor }} />
                      ))}
                    </div>
                    <p className="text-white/80 mb-6 italic">"{testimonial.text}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-black" style={{ backgroundColor: primaryColor }}>
                        {testimonial.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-semibold text-white">{testimonial.name}</p>
                        <p className="text-sm text-white/50">{testimonial.event}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'features_grid':
        // Features in a grid layout (used for models page)
        return (
          <section className="py-20 px-6 bg-gradient-to-b from-gray-900 to-black">
            <div className="max-w-6xl mx-auto">
              {content.title && (
                <h2 
                  className="text-3xl md:text-4xl font-bold text-white text-center mb-4 [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.title }}
                />
              )}
              {content.subtitle && (
                <p 
                  className="text-white/60 text-center mb-12 [&_strong]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.subtitle }}
                />
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {(content.items || []).map((item, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className="group relative rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/30 cursor-pointer"
                    onClick={() => item.link ? navigateToPage(item.link) : null}
                  >
                    {item.image && (
                      <div className="aspect-[4/3] overflow-hidden">
                        <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-bold text-white">{item.title}</h3>
                      {item.description && <p className="text-white/60 text-sm mt-1">{item.description}</p>}
                    </div>
                    {item.badge && (
                      <div className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-bold text-black" style={{ backgroundColor: primaryColor }}>
                        {item.badge}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
              {content.buttonText && (
                <div className="text-center mt-12">
                  <button 
                    onClick={handleBookingClick}
                    className="px-10 py-4 rounded-full font-bold text-lg text-black hover:scale-105 transition-transform"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {content.buttonText}
                  </button>
                </div>
              )}
            </div>
          </section>
        );

      case 'gallery':
        // Gallery with animated text section - data from CMS
        const galleryImagesData = (content.images || []).map(img => {
          if (typeof img === 'string') return { url: img, positionX: 50, positionY: 50 };
          if (img && typeof img === 'object' && img.url) return { 
            url: img.url, 
            positionX: img.positionX ?? 50, 
            positionY: img.positionY ?? 50,
            alt: img.alt || ''
          };
          return null;
        }).filter(img => img && img.url);
        const serviceNames = content.highlightedServices || ['PhotoBooth', 'VideoBooth 360', 'Livre d\'Or Vidéo'];
        const currentServiceName = serviceNames[currentServiceSlide % serviceNames.length];
        const galleryTitle = content.title || 'Just a Smile Events vous propose la location de';
        const galleryTitleSuffix = content.titleSuffix || 'pour immortaliser vos événements !';
        
        return (
          <section className="py-20 px-6 bg-black relative overflow-hidden" id="qui-sommes-nous">
            <div className="max-w-7xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Left: Animated Text */}
                <div className="text-center lg:text-left">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mb-8"
                  >
                    {/* Logo */}
                    <img 
                      src="/api/media/file/27ce19d7-750d-4def-bff6-1b53cbc51ebe/logo-just-a-smile.png"
                      alt="Just A Smile Events"
                      className="w-24 h-24 mx-auto lg:mx-0 mb-6 rounded-full"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </motion.div>
                  
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-3xl md:text-4xl font-medium text-white leading-relaxed mb-6"
                  >
                    <span className="text-white">{galleryTitle} </span>
                    <span 
                      className="font-bold inline-block animate-shimmer"
                      style={{ 
                        background: `linear-gradient(90deg, ${primaryColor} 0%, #fff9e6 50%, ${primaryColor} 100%)`,
                        backgroundSize: '200% 100%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        animation: 'shimmer 3s linear infinite'
                      }}
                    >
                      {currentServiceName}
                    </span>
                    <span className="text-white"> {galleryTitleSuffix}</span>
                  </motion.h2>
                  
                  {/* CSS Shimmer Animation */}
                  <style>{`
                    @keyframes shimmer {
                      0% { background-position: 200% 0; }
                      100% { background-position: -200% 0; }
                    }
                  `}</style>
                </div>
                
                {/* Right: Tilted Photo Gallery Grid */}
                <div className="relative">
                  <motion.div
                    initial={{ opacity: 0, rotate: 0 }}
                    whileInView={{ opacity: 1, rotate: 6 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="grid grid-cols-3 gap-3 transform"
                  >
                    {galleryImagesData.slice(0, 6).map((img, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                        className="aspect-square overflow-hidden rounded-lg cursor-pointer hover:scale-105 transition-transform"
                        onClick={() => {
                          const images = content.images || [];
                          setLightboxImages(images);
                          setLightboxIndex(i);
                          setLightboxImage(img.url);
                          setShowGalleryLightbox(true);
                        }}
                      >
                        <img 
                          src={img.url.startsWith('http') ? img.url : `${API_URL}${img.url}`}
                          alt={img.alt || `Gallery ${i + 1}`}
                          className="w-full h-full object-cover"
                          style={{ objectPosition: `${img.positionX}% ${img.positionY}%` }}
                        />
                      </motion.div>
                    ))}
                  </motion.div>
                </div>
              </div>
            </div>
          </section>
        );

      case 'about':
        // Simple about section - title and text from CMS
        const aboutTitle = content.title || 'Qui sommes-nous ?';
        const aboutText = content.text || content.description || '';
        const aboutImage = content.image || '';
        
        // Check if the text contains HTML tags
        const aboutHasHtml = /<[^>]+>/.test(aboutText);
        
        return (
          <section className="py-20 px-6 bg-black" id="a-propos">
            <div className="max-w-4xl mx-auto text-center">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold mb-6 [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold"
                style={{ color: primaryColor }}
                dangerouslySetInnerHTML={{ __html: aboutTitle }}
              />
              {aboutText && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="text-white/80 text-lg leading-relaxed space-y-4 [&_strong]:font-bold [&_strong]:text-white [&_.gold-shimmer-text]:font-bold [&_p]:mb-4 [&_br]:block"
                  dangerouslySetInnerHTML={{ __html: aboutText }}
                />
              )}
              {aboutImage && (
                <motion.img
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  src={aboutImage.startsWith('http') ? aboutImage : `${API_URL}${aboutImage}`}
                  alt={aboutTitle}
                  className="mt-8 mx-auto max-w-md rounded-lg"
                />
              )}
            </div>
          </section>
        );

      case 'events_marquee':
        // Diagonal scrolling marquee for event types - data from CMS
        const defaultEventTypes = [
          { name: 'Mariage', image: '/api/media/file/27ce19d7-750d-4def-bff6-1b53cbc51ebe/event-mariage.png' },
          { name: 'Anniversaire', image: '/api/media/file/27ce19d7-750d-4def-bff6-1b53cbc51ebe/event-anniversaire.png' },
          { name: 'Baptême', image: '/api/media/file/27ce19d7-750d-4def-bff6-1b53cbc51ebe/event-bapteme.png' },
          { name: 'Entreprise', image: '/api/media/file/27ce19d7-750d-4def-bff6-1b53cbc51ebe/event-entreprise.png' },
          { name: 'EVJF / EVJG', image: '/api/media/file/27ce19d7-750d-4def-bff6-1b53cbc51ebe/event-evjf.png' },
          { name: 'Baby Shower', image: '/api/media/file/27ce19d7-750d-4def-bff6-1b53cbc51ebe/event-babyshower.png' },
          { name: 'Gender Reveal', image: '/api/media/file/27ce19d7-750d-4def-bff6-1b53cbc51ebe/event-genderreveal.png' },
        ];
        
        const eventTypes = (content.items && content.items.length > 0) ? content.items : defaultEventTypes;
        const eventsTitle = content.title || 'Pour tous vos événements';
        const eventsSubtitle = content.subtitle || 'Nos animations s\'adaptent à toutes vos occasions';
        
        // Double the events for seamless loop
        const infiniteEvents = [...eventTypes, ...eventTypes];
        
        return (
          <section className="py-16 bg-black overflow-hidden">
            <div className="text-center mb-12 px-6">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                {eventsTitle}
              </h2>
              <p className="text-white/60 text-lg">
                {eventsSubtitle}
              </p>
            </div>
            
            {/* Diagonal marquee container */}
            <div className="relative transform -rotate-2 -mx-8">
              <div 
                className="flex gap-6"
                style={{
                  width: 'fit-content',
                  animation: 'marquee-diagonal 40s linear infinite'
                }}
              >
                {infiniteEvents.map((event, i) => (
                  <div 
                    key={i}
                    className="flex-shrink-0 w-[200px] h-[200px] relative group cursor-pointer overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all hover:scale-105"
                  >
                    {/* Background Image */}
                    <img 
                      src={event.image?.startsWith('http') ? event.image : `${API_URL}${event.image}`} 
                      alt={event.name}
                      className="w-full h-full object-cover"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                    {/* Event name */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
                      <span className="text-white font-bold text-lg">{event.name}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* CSS Animation for diagonal marquee */}
              <style>{`
                @keyframes marquee-diagonal {
                  0% { transform: translateX(0); }
                  100% { transform: translateX(calc(-200px * ${eventTypes.length} - 24px * ${eventTypes.length})); }
                }
              `}</style>
            </div>
          </section>
        );

      case 'highlight':
        // Highlight/Marketing section
        return (
          <section className="py-24 px-6" style={{ backgroundColor: primaryColor }}>
            <div className="max-w-4xl mx-auto text-center">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-4xl md:text-5xl font-black text-black mb-6"
              >
                {content.title}
              </motion.h2>
              {content.subtitle && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="text-2xl text-black/80 mb-8 font-medium"
                >
                  {content.subtitle}
                </motion.p>
              )}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-lg text-black/70 space-y-4 mb-10"
              >
                {content.text?.split('\n\n').map((paragraph, pi) => (
                  <p key={pi} dangerouslySetInnerHTML={{ 
                    __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-black">$1</strong>')
                  }} />
                ))}
              </motion.div>
              {content.ctaText && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  onClick={() => navigateToPage(content.ctaLink || 'reservation')}
                  className="px-12 py-5 bg-black text-white rounded-full font-bold text-lg hover:scale-105 transition-transform shadow-2xl"
                >
                  {content.ctaText}
                </motion.button>
              )}
            </div>
          </section>
        );

      case 'faq':
        // FAQ Accordion section - using state-based accordion to prevent flickering
        return (
          <section className="py-24 px-6 bg-gradient-to-b from-gray-900 to-black">
            <div className="max-w-4xl mx-auto">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-3xl md:text-4xl font-bold text-white text-center mb-12"
              >
                {content.title}
              </motion.h2>
              <div className="space-y-4">
                {content.items?.map((item, i) => {
                  const isOpen = openFaqIndex === `faq-${index}-${i}`;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden"
                    >
                      <button 
                        onClick={() => setOpenFaqIndex(isOpen ? null : `faq-${index}-${i}`)}
                        className="w-full flex items-center justify-between p-6 cursor-pointer text-left"
                      >
                        <h3 className="text-lg font-bold text-white pr-4">{item.question}</h3>
                        <FiChevronDown 
                          className={`h-5 w-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
                          style={{ color: primaryColor }} 
                        />
                      </button>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 pb-6 text-white/70">
                              {item.answer?.includes('<') || item.answer?.includes('&lt;') ? (
                                <div 
                                  className="prose prose-invert prose-p:text-white/70 prose-strong:text-white prose-strong:font-bold prose-li:text-white/70 prose-em:text-white/90 prose-a:text-white/90 prose-a:no-underline prose-ul:my-3 prose-p:my-3 prose-p:leading-loose max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1"
                                  dangerouslySetInnerHTML={{ 
                                    __html: (item.answer || '')
                                      .replace(/&lt;/g, '<')
                                      .replace(/&gt;/g, '>')
                                      .replace(/&amp;/g, '&')
                                      .replace(/&quot;/g, '"')
                                  }}
                                />
                              ) : (
                                <div className="space-y-4">
                                  {item.answer?.split('\n\n').map((paragraph, pi) => (
                                    <p key={pi} className="leading-loose text-base">{paragraph}</p>
                                  ))}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>
        );

      case 'comparison':
        // Comparison section (e.g., 80cm vs 100cm)
        return (
          <section className="py-24 px-6 bg-gradient-to-b from-black to-gray-900">
            <div className="max-w-5xl mx-auto">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold text-white text-center mb-12"
              >
                {content.title}
              </motion.h2>
              <div className="grid md:grid-cols-2 gap-8">
                {content.items?.map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className={`relative p-8 rounded-3xl border ${item.highlight ? 'bg-gradient-to-b from-amber-900/30 to-transparent border-amber-500/50' : 'bg-white/5 border-white/10'}`}
                  >
                    {item.highlight && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-sm font-bold text-black" style={{ backgroundColor: primaryColor }}>
                        Recommandé
                      </div>
                    )}
                    <h3 className="text-2xl font-bold text-white text-center mb-6">{item.name}</h3>
                    <ul className="space-y-4">
                      {item.features?.map((feature, fi) => (
                        <li key={fi} className="flex items-center gap-3 text-white/80">
                          <FiCheck className="h-5 w-5 flex-shrink-0" style={{ color: primaryColor }} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button 
                      onClick={handleBookingClick}
                      className={`mt-8 w-full py-4 rounded-xl font-bold transition-all ${item.highlight ? 'text-black hover:scale-105' : 'bg-white/10 text-white hover:bg-white/20'}`}
                      style={item.highlight ? { backgroundColor: primaryColor } : {}}
                    >
                      Réserver
                    </button>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'text_content':
        // Text content with blocks/steps
        return (
          <section className="py-24 px-6 bg-gradient-to-b from-gray-900 to-black">
            <div className="max-w-4xl mx-auto">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold text-white text-center mb-12"
              >
                {content.title}
              </motion.h2>
              <div className="space-y-8">
                {content.blocks?.map((block, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex gap-6 items-start"
                  >
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-black font-bold text-xl" style={{ backgroundColor: primaryColor }}>
                      {i + 1}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{block.subtitle}</h3>
                      <p className="text-white/70 leading-relaxed">{block.text}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'products_grid':
        // Products/Services grid for reservation page - DYNAMIC VERSION
        // Use productsForServices if no static products are defined
        const displayProducts = content.products && content.products.length > 0 
          ? content.products 
          : productsForServices.map(p => ({
              name: p.name,
              image: p.images?.[0] || '/placeholder.jpg',
              price: `À partir de ${p.variations?.[0]?.price || 149}€`,
              link: `/${p.slug || p.name?.toLowerCase().replace(/\s+/g, '-')}-presentation`,
              badge: p.category === 'livre-dor' ? 'Nouveauté' : null,
              description: p.short_description || p.description?.substring(0, 100)
            }));
        
        return (
          <section className="py-24 px-6 bg-gradient-to-b from-black to-gray-900">
            <div className="max-w-6xl mx-auto">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold text-white text-center mb-12"
              >
                {content.title || 'Nos Prestations'}
              </motion.h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {displayProducts.map((product, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="group relative bg-white/5 rounded-2xl border border-white/10 hover:border-white/30 overflow-hidden cursor-pointer transition-all"
                    onClick={() => navigateToPage(product.link)}
                  >
                    {product.badge && (
                      <div className="absolute top-4 left-4 z-10 px-3 py-1 rounded-full text-sm font-bold text-black" style={{ backgroundColor: primaryColor }}>
                        {product.badge}
                      </div>
                    )}
                    <div className="aspect-square overflow-hidden">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-white mb-2">{product.name}</h3>
                      <p className="font-semibold mb-4" style={{ color: primaryColor }}>{product.price}</p>
                      <button className="w-full py-3 rounded-xl font-bold text-black hover:scale-[1.02] transition-transform" style={{ backgroundColor: primaryColor }}>
                        Choix des options
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
              {content.note && (
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  className="mt-12 text-center text-white/60 italic space-y-2"
                >
                  {content.note.split('\n\n').map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </motion.div>
              )}
            </div>
          </section>
        );

      case 'faq_full':
        // Complete FAQ with categories - using controlled accordion to prevent flickering
        return (
          <section className="py-24 px-6 bg-gradient-to-b from-black to-gray-900">
            <div className="max-w-5xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-16"
              >
                <h2 
                  className="text-4xl md:text-5xl font-bold text-white mb-4 [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.title || '' }}
                />
                <p className="text-white/60 text-lg">Cliquez sur une question pour voir la réponse</p>
              </motion.div>
              
              {content.categories?.map((category, catIdx) => (
                <div key={catIdx} className="mb-12">
                  <h3 className="text-2xl font-bold mb-6 flex items-center gap-3" style={{ color: primaryColor }}>
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold text-black" style={{ backgroundColor: primaryColor }}>
                      {catIdx + 1}
                    </span>
                    {category.category}
                  </h3>
                  <div className="space-y-3">
                    {category.questions?.map((item, i) => {
                      const faqKey = `faq-full-${index}-${catIdx}-${i}`;
                      const isOpen = openFaqIndex === faqKey;
                      return (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.03 }}
                          className="bg-white/5 rounded-xl border border-white/10 overflow-hidden hover:border-white/20 transition-colors"
                        >
                          <button 
                            onClick={() => setOpenFaqIndex(isOpen ? null : faqKey)}
                            className="w-full flex items-center justify-between p-5 cursor-pointer text-left"
                          >
                            <h4 className="text-white font-medium pr-4">{item.question}</h4>
                            <FiChevronDown className={`h-5 w-5 text-white/40 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
                          </button>
                          <AnimatePresence>
                            {isOpen && (
                              <motion.div 
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="overflow-hidden"
                              >
                                <div 
                                  className="px-5 pb-5 text-white/70 leading-loose border-t border-white/5 pt-4 prose prose-invert prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-ul:list-disc prose-ul:pl-5 prose-ol:my-2 prose-ol:list-decimal prose-ol:pl-5 prose-li:my-1 prose-strong:text-white prose-a:text-[#d4af37]"
                                  dangerouslySetInnerHTML={{ __html: item.answer || '' }}
                                />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );

      case 'blog_grid':
        // Blog articles grid
        return (
          <section className="py-24 px-6 bg-gradient-to-b from-gray-900 to-black">
            <div className="max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 
                  className="text-4xl md:text-5xl font-bold text-white mb-4 [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.title || '' }}
                />
                <p className="text-white/60 text-lg">Conseils, inspirations et actualités pour vos événements</p>
              </motion.div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {content.articles?.map((article, i) => (
                  <motion.article
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="group bg-white/5 rounded-2xl border border-white/10 overflow-hidden hover:border-white/20 transition-all cursor-pointer"
                    onClick={() => navigateToPage(`blog/${article.slug}`)}
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img src={article.image} alt={article.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      <div className="absolute top-3 left-3">
                        <span className="px-3 py-1 rounded-full text-xs font-semibold text-black" style={{ backgroundColor: primaryColor }}>
                          {article.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <p className="text-white/40 text-xs mb-2">{article.date}</p>
                      <h3 className="text-white font-bold mb-2 group-hover:text-white/80 transition-colors line-clamp-2">{article.title}</h3>
                      <p className="text-white/50 text-sm line-clamp-3">{article.excerpt}</p>
                      <button className="mt-4 text-sm font-medium flex items-center gap-2 group-hover:gap-3 transition-all" style={{ color: primaryColor }}>
                        Lire l'article <FiArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </motion.article>
                ))}
              </div>
              
              <div className="text-center mt-12">
                <button 
                  onClick={() => navigateToPage('blog')}
                  className="px-8 py-4 rounded-xl font-semibold text-black hover:scale-105 transition-transform"
                  style={{ backgroundColor: primaryColor }}
                >
                  Voir tous les articles
                </button>
              </div>
            </div>
          </section>
        );

      case 'legal_content':
        // Legal pages (CGV, Mentions légales, etc.)
        return (
          <section className="py-24 px-6 bg-gradient-to-b from-gray-900 to-black">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mb-8 text-center"
              >
                <h1 
                  className="text-3xl md:text-4xl font-bold text-white mb-4 [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.title || '' }}
                />
                <p className="text-white/40 text-sm">Dernière mise à jour : {content.lastUpdated}</p>
              </motion.div>
              
              <div className="space-y-8">
                {content.sections?.map((section, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white/5 rounded-2xl p-6 border border-white/10"
                  >
                    <h3 className="text-xl font-bold text-white mb-4">{section.title}</h3>
                    <div className="text-white/70 text-sm leading-relaxed whitespace-pre-wrap prose prose-invert prose-sm max-w-none">
                      {section.content.split('**').map((part, idx) => 
                        idx % 2 === 1 ? <strong key={idx} className="text-white">{part}</strong> : part
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="mt-12 text-center"
              >
                <button 
                  onClick={() => navigateToPage('accueil')}
                  className="px-6 py-3 rounded-xl text-sm font-medium text-white/60 hover:text-white border border-white/20 hover:border-white/40 transition-all"
                >
                  ← Retour à l'accueil
                </button>
              </motion.div>
            </div>
          </section>
        );

      case 'video_gallery':
        // Video gallery (YouTube videos)
        return (
          <section className="py-24 px-6 bg-gradient-to-b from-gray-900 to-black">
            <div className="max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 
                  className="text-4xl md:text-5xl font-bold text-white mb-4 [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.title || '' }}
                />
                <p 
                  className="text-white/60 text-lg [&_strong]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.subtitle || '' }}
                />
              </motion.div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {content.items?.map((video, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="group relative rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/20 transition-all"
                  >
                    <div className="relative aspect-video">
                      <img 
                        src={video.thumbnail} 
                        alt={video.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                      />
                      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <a 
                          href={`https://www.youtube.com/watch?v=${video.videoId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform"
                          style={{ backgroundColor: primaryColor }}
                        >
                          <FiPlay className="h-6 w-6 text-black ml-1" />
                        </a>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-white font-semibold">{video.title}</h3>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'models_gallery':
        // Modern animated models gallery with marquee bands
        // Group items by category
        const groupedItems = {};
        content.items?.forEach(item => {
          const cat = item.category || 'Autres';
          if (!groupedItems[cat]) groupedItems[cat] = [];
          groupedItems[cat].push(item);
        });

        // Helper function to determine aspect ratio based on format/category
        const getModelAspectRatio = (item) => {
          const title = (item.title || '').toLowerCase();
          const category = (item.category || '').toLowerCase();
          
          // Bandelette format (vertical strip) - typically 2x6 or similar
          if (title.includes('bandelette') || category.includes('bandelette')) {
            return 'aspect-[1/3]';
          }
          // Portrait format
          if (title.includes('portrait') || category.includes('portrait')) {
            return 'aspect-[3/4]';
          }
          // Landscape/Paysage format
          if (title.includes('paysage') || category.includes('paysage') || title.includes('carte postale')) {
            return 'aspect-[4/3]';
          }
          // Default - detect from image if possible, or use 4:3
          return 'aspect-[4/3]';
        };

        // Helper to get container width based on format
        const getModelWidth = (item) => {
          const title = (item.title || '').toLowerCase();
          const category = (item.category || '').toLowerCase();
          
          if (title.includes('bandelette') || category.includes('bandelette')) {
            return 'w-32 md:w-40 lg:w-48'; // Narrower for bandelettes
          }
          return 'w-48 md:w-64 lg:w-72'; // Standard width
        };

        return (
          <section className="py-16 md:py-24 bg-gradient-to-b from-gray-900 via-black to-gray-900 overflow-hidden">
            {/* Header */}
            <div className="max-w-6xl mx-auto px-6 mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
                  {content.title || 'Nos Modèles'}
                </h2>
                <p className="text-white/60 text-lg max-w-2xl mx-auto">
                  {content.subtitle || 'Découvrez notre collection de designs personnalisables'}
                </p>
              </motion.div>
            </div>

            {/* Categories with animated image rows */}
            {Object.entries(groupedItems).map(([category, categoryItems], categoryIndex) => (
              <div key={category} className="mb-12">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="max-w-6xl mx-auto px-6 mb-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-8 rounded-full" style={{ backgroundColor: primaryColor }} />
                    <h3 className="text-2xl font-bold text-white">{category}</h3>
                    <span className="text-white/40 text-sm">({categoryItems.length} modèles)</span>
                  </div>
                </motion.div>
                
                {/* Animated horizontal scroll */}
                <div className="overflow-hidden py-3">
                  <motion.div
                    className="flex gap-4 items-end"
                    animate={{
                      x: categoryIndex % 2 === 0 
                        ? [0, -280 * categoryItems.length] 
                        : [-280 * categoryItems.length, 0]
                    }}
                    transition={{
                      x: {
                        repeat: Infinity,
                        repeatType: "loop",
                        duration: 40 + categoryIndex * 10,
                        ease: "linear"
                      }
                    }}
                  >
                    {/* Triple the items for seamless loop */}
                    {[...categoryItems, ...categoryItems, ...categoryItems].map((item, i) => (
                      <div 
                        key={i}
                        className={`flex-shrink-0 ${getModelWidth(item)} group cursor-pointer`}
                        onClick={() => { 
                          const idx = i % categoryItems.length;
                          setLightboxModels(categoryItems);
                          setLightboxModelIndex(idx);
                          setLightboxModel(item); 
                          setShowModelLightbox(true); 
                        }}
                      >
                        <div className={`relative ${getModelAspectRatio(item)} rounded-xl overflow-hidden bg-white/5 border border-white/10 group-hover:border-white/30 transition-all duration-300`}>
                          <img 
                            src={item.image} 
                            alt={item.title}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                            style={{ imageRendering: 'auto', WebkitBackfaceVisibility: 'hidden' }}
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                            <FiMaximize className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 p-3 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                            <p className="text-white text-sm font-medium truncate">{item.title}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                </div>
              </div>
            ))}

            {/* Nos Réalisations Section */}
            {content.realizations && content.realizations.length > 0 && (
              <div className="mt-20">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="max-w-6xl mx-auto px-6 mb-8 text-center"
                >
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-2">
                    Nos Réalisations
                  </h3>
                  <p className="text-white/60">Quelques exemples de nos créations récentes</p>
                </motion.div>
                
                <div className="max-w-6xl mx-auto px-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {content.realizations.slice(0, 8).map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.05 }}
                        className="group relative aspect-square rounded-xl overflow-hidden bg-white/5 cursor-pointer"
                      >
                        <img 
                          src={item.image} 
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <p className="text-white text-sm font-medium text-center px-2">{item.title}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-6xl mx-auto px-6 mt-16 text-center"
            >
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                <p className="text-white/80 text-lg mb-6">
                  Nous réalisons <strong className="text-white">vos modèles sur mesure</strong>, 
                  qu&apos;ils soient verticaux, horizontaux ou bandelettes
                </p>
                <button 
                  onClick={handleBookingClick}
                  className="px-8 py-4 rounded-xl font-semibold text-black hover:scale-105 transition-transform shadow-lg"
                  style={{ backgroundColor: primaryColor }}
                >
                  Réserver en ligne et obtenir mon design personnalisé
                </button>
              </div>
            </motion.div>
          </section>
        );

      default:
        // Fallback for unknown section types - render basic content if available
        console.log('Unknown section type:', type, content);
        return content.title ? (
          <section className="py-16 px-6 bg-black">
            <div className="max-w-4xl mx-auto text-center">
              <h2 
                className="text-3xl font-bold text-white mb-4 [&_strong]:font-bold [&_.gold-shimmer-text]:font-bold"
                dangerouslySetInnerHTML={{ __html: content.title }}
              />
              {content.subtitle && (
                <p 
                  className="text-white/60 [&_strong]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.subtitle }}
                />
              )}
              {content.text && (
                <p 
                  className="text-white/70 mt-4 [&_strong]:font-bold"
                  dangerouslySetInnerHTML={{ __html: content.text }}
                />
              )}
            </div>
          </section>
        ) : null;
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* SEO Meta Tags are handled via useEffect direct DOM manipulation */}
      
      <style>{`
        html { scroll-behavior: smooth; }
        @keyframes float { 0%, 100% { transform: translate(0, 0); } 50% { transform: translate(20px, -20px); } }
        @keyframes parallaxSlow { 0% { transform: translateY(0); } 100% { transform: translateY(-30px); } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes pulse-glow { 0%, 100% { box-shadow: 0 0 20px ${primaryColor}40; } 50% { box-shadow: 0 0 40px ${primaryColor}80; } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
        .parallax-slow { animation: parallaxSlow 20s ease-in-out infinite alternate; }
        .shimmer-text { background: linear-gradient(90deg, ${primaryColor} 0%, #fff 50%, ${primaryColor} 100%); background-size: 200% 100%; animation: shimmer 3s ease-in-out infinite; -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .glow-button { animation: pulse-glow 2s ease-in-out infinite; }
        .fade-in-up { animation: fadeInUp 0.6s ease-out forwards; }
        .scale-in { animation: scaleIn 0.4s ease-out forwards; }
        /* Smooth scrollbar */
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: #111; }
        ::-webkit-scrollbar-thumb { background: ${primaryColor}; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: ${primaryColor}dd; }
      `}</style>
      
      {/* Top Banner - Promo/Announcement Bar */}
      {site?.site_settings?.top_banner?.enabled && !topBannerClosed && (
        <div 
          className="relative z-[200] py-2 px-4 text-center text-sm font-medium"
          style={{ 
            backgroundColor: site.site_settings.top_banner.background_color || primaryColor,
            color: site.site_settings.top_banner.text_color || '#000000'
          }}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 flex-wrap">
            <span>{site.site_settings.top_banner.text}</span>
            {site.site_settings.top_banner.promo_code && (
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(site.site_settings.top_banner.promo_code);
                    setPromoCopied(true);
                    setTimeout(() => setPromoCopied(false), 2000);
                  } catch (err) {
                    console.log('Clipboard error:', err);
                    setPromoCopied(true);
                    setTimeout(() => setPromoCopied(false), 2000);
                  }
                }}
                className="px-3 py-1 rounded-full bg-black/20 hover:bg-black/30 transition-colors flex items-center gap-1.5 font-bold"
              >
                <span>{site.site_settings.top_banner.promo_code}</span>
                {promoCopied ? (
                  <FiCheck className="h-3.5 w-3.5" />
                ) : (
                  <FiCopy className="h-3.5 w-3.5" />
                )}
              </button>
            )}
            {site.site_settings.top_banner.link && (
              <a 
                href={site.site_settings.top_banner.link}
                className="underline hover:no-underline"
              >
                En savoir plus
              </a>
            )}
          </div>
          {site.site_settings.top_banner.closable && (
            <button
              onClick={() => setTopBannerClosed(true)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/10 hover:bg-black/20 flex items-center justify-center"
            >
              <FiX className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
      
      {/* Hero with Header Overlay - PARALLAX EFFECT - Reduced Height */}
      {isHeroFirst && (
        <div className="relative h-[85vh] overflow-hidden">
          {/* Parallax Background Layers */}
          <div className="absolute inset-0 parallax-slow">
            {/* Background Image Fallback */}
            <div 
              className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${heroImageUrl})` }}
            />
          </div>
          
          {/* YouTube Video Background - on top of image */}
          <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
            <iframe
              src={heroVideoUrl}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 scale-150"
              style={{ 
                border: 'none', 
                width: '100vw',
                height: '100vh',
                minWidth: '177.78vh',
                minHeight: '100vh'
              }}
              allow="autoplay; encrypted-media"
              allowFullScreen
              title="Background Video"
            />
          </div>
          
          {/* Animated Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
          
          {/* Floating Particles Effect */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute w-96 h-96 rounded-full blur-[120px] opacity-30" style={{ background: `radial-gradient(circle, ${primaryColor} 0%, transparent 70%)`, top: '10%', right: '10%', animation: 'float 15s ease-in-out infinite' }} />
            <div className="absolute w-64 h-64 rounded-full blur-[100px] opacity-20" style={{ background: `radial-gradient(circle, ${primaryColor} 0%, transparent 70%)`, bottom: '20%', left: '5%', animation: 'float 12s ease-in-out infinite reverse' }} />
            <div className="absolute w-48 h-48 rounded-full blur-[80px] opacity-25" style={{ background: 'radial-gradient(circle, #fff 0%, transparent 70%)', top: '40%', left: '30%', animation: 'float 18s ease-in-out infinite' }} />
          </div>

          {/* Header - Overlay on video - BOXED with rounded corners and protruding logo */}
          <header className="absolute top-0 left-0 right-0 z-50 pt-2">
            <div className="max-w-7xl mx-auto px-2">
              {/* Top Bar with Contact Info - Rounded top corners */}
              <div className="bg-black/70 backdrop-blur-sm px-4 md:px-6 py-1.5 flex justify-between items-center border-b border-white/10 rounded-t-2xl">
                <div className="flex items-center gap-2 md:gap-6 text-white/80">
                  {/* Horaires - Visible on all screens */}
                  <span className="flex items-center gap-1.5 text-xs">
                    <FiClock className="h-3.5 w-3.5" style={{ color: primaryColor }} /> 
                    <span className="text-[10px] md:text-xs">{site?.contact_info?.hours || '24/24 & 7/7'}</span>
                  </span>
                  {/* Email - Hidden on mobile */}
                  <a href={`mailto:${site?.contact_info?.email || site?.footer_content?.email || `contact@${site?.domain}`}`} className="hidden md:flex items-center gap-2 hover:text-white text-xs transition-colors">
                    <FiMail className="h-3.5 w-3.5" /> {site?.contact_info?.email || site?.footer_content?.email || `contact@${site?.domain}`}
                  </a>
                </div>
                <div className="flex items-center gap-2 md:gap-3">
                  {/* Social Links & Email */}
                  <div className="flex items-center gap-1.5">
                    <a href={`mailto:${site?.footer_content?.email || site?.contact_info?.email || ''}`} className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors" title="Email"><FiMail className="h-4 w-4" /></a>
                    <a href={site?.footer_content?.instagram_url || site?.footer_content?.socialLinks?.instagram || site?.contact_info?.instagram_url || 'https://www.instagram.com/just_a_smile_events/'} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors" title="Instagram"><FiInstagram className="h-4 w-4" /></a>
                    <a href={site?.footer_content?.facebook_url || site?.footer_content?.socialLinks?.facebook || site?.contact_info?.facebook || '#'} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors" title="Facebook"><FiFacebook className="h-4 w-4" /></a>
                    {(site?.footer_content?.socialLinks?.tiktok || site?.contact_info?.tiktok) && (
                      <a href={site?.footer_content?.socialLinks?.tiktok || site?.contact_info?.tiktok} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors" title="TikTok"><FaTiktok className="h-4 w-4" /></a>
                    )}
                    {(site?.footer_content?.socialLinks?.snapchat || site?.contact_info?.snapchat) && (
                      <a href={site?.footer_content?.socialLinks?.snapchat || site?.contact_info?.snapchat} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors" title="Snapchat"><FaSnapchatGhost className="h-4 w-4" /></a>
                    )}
                  </div>
                  {/* Username - Dynamic from Instagram URL - with link and icon */}
                  <a 
                    href={site?.footer_content?.instagram_url || site?.contact_info?.instagram_url || 'https://www.instagram.com/just_a_smile_events/'} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="hidden md:flex items-center gap-1.5 text-white/80 hover:text-white text-xs transition-colors"
                    title="Suivez-nous sur Instagram"
                  >
                    <FiInstagram className="h-3.5 w-3.5" style={{ color: primaryColor }} />
                    <span>@{(site?.footer_content?.instagram_url || site?.contact_info?.instagram_url || 'https://www.instagram.com/just_a_smile_events/').split('/').filter(Boolean).pop() || 'just_a_smile_events'}</span>
                  </a>
                </div>
              </div>
              
              {/* Main Navigation Bar - Rounded bottom corners and protruding logo */}
              <div className="bg-black/70 backdrop-blur-sm px-2 md:px-6 py-0 flex items-center justify-between relative rounded-b-2xl">
                {/* Logo Container - Protruding effect - HIGH z-index to stay above mega menu */}
                <div className="relative flex-shrink-0 -mb-4 md:-mb-8 z-[150]">
                  <div 
                    className="bg-black/90 rounded-b-full px-2 md:px-3 pt-0 pb-2 md:pb-4 cursor-pointer hover:scale-105 transition-transform border-x border-b border-white/10"
                    onClick={() => navigateToPage('accueil')}
                  >
                    <img 
                      src={logoUrl} 
                      alt="Logo" 
                      className="h-14 md:h-24 w-auto object-contain drop-shadow-2xl" 
                    />
                  </div>
                </div>
              
              {/* Desktop Menu - Filter out "Réservation" since we have CTA button */}
              <nav className="desktop-header-nav hidden md:flex items-center gap-1 py-3">
                {filteredMenuStructure.map((item, idx) => (
                  <div 
                    key={idx} 
                    className="relative" 
                    onMouseEnter={() => item.children?.length > 0 && setHoveredMenu(idx)} 
                    onMouseLeave={() => !('ontouchstart' in window) && setHoveredMenu(null)}
                  >
                    <button 
                      onClick={() => {
                        if (item.children?.length > 0) {
                          // Toggle menu on click (especially for touch devices)
                          setHoveredMenu(hoveredMenu === idx ? null : idx);
                        } else {
                          navigateToPage(item.link || item.slug || item.label?.toLowerCase());
                        }
                      }}
                      className="px-4 py-2 text-sm font-medium flex items-center gap-1 transition-colors hover:text-white" 
                      style={{ color: hoveredMenu === idx ? primaryColor : 'rgba(255,255,255,0.85)' }}
                    >
                      {item.label}
                      {item.children?.length > 0 && <FiChevronDown className={`h-3 w-3 transition-transform ${hoveredMenu === idx ? 'rotate-180' : ''}`} />}
                    </button>
                    
                    <AnimatePresence>
                      {item.children?.length > 0 && hoveredMenu === idx && (
                        <>
                          {/* Backdrop overlay - clickable to close */}
                          <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/30 z-[90] cursor-pointer"
                            style={{ top: '90px' }}
                            onClick={() => setHoveredMenu(null)}
                            onMouseEnter={() => setHoveredMenu(null)}
                          />
                          {/* Mega Menu - Centered with max-height for tablets */}
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="fixed left-0 right-0 z-[100] flex justify-center"
                            style={{ top: '90px', maxHeight: 'calc(100vh - 100px)' }}
                          >
                            <div 
                              className="bg-white rounded-3xl shadow-2xl overflow-hidden overflow-y-auto mx-4" 
                              style={{ width: '1100px', maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 120px)' }}
                            >
                              {/* Mega Menu Header */}
                              <div className="bg-gradient-to-r from-amber-50 via-white to-amber-50 px-8 py-4 border-b border-amber-100/50">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h3 className="font-bold text-gray-900 text-lg">Découvrez nos solutions</h3>
                                    <p className="text-sm text-gray-500">Location de PhotoBooth, VideoBooth 360 et Livre d'Or Vidéo</p>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 font-medium">✓ Devis gratuit</span>
                                    <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">⭐ +500 événements</span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Menu Content - Products + CTA Columns */}
                              <div className="flex divide-x divide-gray-100">
                                {/* Products columns - 3/5 of space */}
                                <div className="flex-1 grid grid-cols-3 divide-x divide-gray-100">
                                  {item.children?.slice(0, 3).map((child, cidx) => (
                                    <div key={cidx} className={`group/col hover:bg-gradient-to-b hover:from-amber-50/50 hover:to-white transition-all duration-300 ${child.label?.includes('Cadeau') ? 'bg-gradient-to-b from-amber-50/30 to-white' : ''}`}>
                                      {/* Category Header */}
                                      <div className="p-5 pb-3">
                                        <button
                                          onClick={() => { setHoveredMenu(null); navigateToPage(child.link || child.slug || child.label?.toLowerCase().replace(/\s+/g, '-')); }}
                                          className="w-full text-left group"
                                        >
                                          {/* Image without label overlay - Responsive height */}
                                          <div className="relative mb-3 overflow-hidden rounded-2xl">
                                            {/* New ribbon for Livre d'Or */}
                                            {(child.label?.includes('Livre') || child.label?.includes("d'Or")) && (
                                              <div className="absolute top-0 left-0 z-20 overflow-hidden w-20 h-20 pointer-events-none">
                                                <div className="absolute top-[8px] left-[-28px] w-[100px] bg-red-600 text-white text-[10px] font-bold py-1 text-center transform -rotate-45 shadow-md">
                                                  New
                                                </div>
                                              </div>
                                            )}
                                            <img 
                                              src={child.image || (child.label?.includes('Photo') && !child.label?.includes('Livre') ? IMAGES.photobooth : child.label?.includes('Livre') || child.label?.includes("d'Or") ? IMAGES.livreor : child.label?.includes('Vidéo') || child.label?.includes('360') ? IMAGES.videobooth : IMAGES.livreor)} 
                                              alt={child.label}
                                              className="w-full h-40 lg:h-56 object-cover group-hover:scale-110 transition-transform duration-500" 
                                            />
                                          </div>
                                          <h4 className="font-bold text-gray-900 group-hover:text-amber-600 transition-colors text-sm leading-tight">{child.label}</h4>
                                          {child.description && (
                                            <p className="text-xs text-gray-500 mt-1">{child.description}</p>
                                          )}
                                        </button>
                                      </div>
                                      
                                      {/* Sub-items */}
                                      <div className="px-5 pb-5">
                                        {child.children?.length > 0 ? (
                                          <div className="space-y-1">
                                            {child.children.map((subItem, sidx) => (
                                              <button
                                                key={sidx}
                                                onClick={() => { setHoveredMenu(null); navigateToPage(subItem.link || subItem.slug || subItem.label?.toLowerCase().replace(/\s+/g, '-')); }}
                                                className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-all flex items-center gap-2 group/item"
                                              >
                                                <span className="w-1.5 h-1.5 rounded-full bg-gray-300 group-hover/item:bg-amber-500 transition-colors" />
                                                <span className="group-hover/item:translate-x-1 transition-transform">{subItem.label}</span>
                                              </button>
                                            ))}
                                          </div>
                                        ) : (
                                          <button
                                            onClick={() => { setHoveredMenu(null); navigateToPage(child.link || child.slug || child.label?.toLowerCase().replace(/\s+/g, '-')); }}
                                            className="w-full text-left px-3 py-2 text-sm font-medium text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-all flex items-center gap-2 group/item"
                                          >
                                            <span>En savoir plus</span>
                                            <FiArrowRight className="h-4 w-4 group-hover/item:translate-x-1 transition-transform" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                
                                {/* Right side CTAs - Compact */}
                                <div className="w-80 flex flex-col">
                                  {/* Composer sa formule - With background image */}
                                  <div className="flex-1 relative overflow-hidden group/cta">
                                    <div 
                                      className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover/cta:scale-105"
                                      style={{ backgroundImage: 'url(/api/media/file/27ce19d7-750d-4def-bff6-1b53cbc51ebe/composer-formule-bg.png)' }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/40" />
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setHoveredMenu(null); handleBookingClick(e); }}
                                      className="relative z-10 w-full h-full p-4 lg:p-6 text-left flex flex-col justify-end min-h-[180px] lg:min-h-[280px]"
                                    >
                                      <h4 className="font-bold text-white text-xl mb-2">
                                        Composer votre formule
                                      </h4>
                                      <p className="text-sm text-white/80 mb-4">
                                        Combinez plusieurs machines et bénéficiez de tarifs avantageux
                                      </p>
                                      <span 
                                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm text-black w-fit group-hover/cta:scale-105 transition-transform"
                                        style={{ backgroundColor: primaryColor }}
                                      >
                                        <FiZap className="h-4 w-4" /> Composer ma formule
                                      </span>
                                    </button>
                                  </div>
                                  
                                  {/* Carte Cadeau - Compact inline */}
                                  <div 
                                    className="p-4 flex items-center gap-3 cursor-pointer hover:bg-amber-50/50 transition-colors border-t border-gray-100"
                                    onClick={() => { setHoveredMenu(null); navigateToPage('carte-cadeau'); }}
                                  >
                                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}30` }}>
                                      <FiGift className="h-5 w-5" style={{ color: primaryColor }} />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-gray-900 text-sm">Carte Cadeau</h5>
                                      <p className="text-xs text-gray-500">Offrez une expérience unique</p>
                                    </div>
                                    <FiArrowRight className="h-4 w-4 text-gray-400" />
                                  </div>
                                </div>
                              </div>
                              
                              {/* Mega Menu Footer */}
                              <div className="bg-gray-50 px-8 py-4 flex items-center justify-between border-t border-gray-100">
                                <div className="flex items-center gap-6 text-sm text-gray-500">
                                  <span className="flex items-center gap-2"><FiPhone className="h-4 w-4" /> Besoin d'aide ?</span>
                                  <a href={`tel:${(site?.contact_info?.phone || site?.footer_content?.phone || '').replace(/\s/g, '')}`} className="font-bold hover:text-amber-600 transition-colors" style={{ color: primaryColor }}>{site?.contact_info?.phone || site?.footer_content?.phone}</a>
                                </div>
                                <button 
                                  onClick={(e) => { e.stopPropagation(); setHoveredMenu(null); handleDevisClick(); }}
                                  className="px-6 py-2.5 rounded-full font-bold text-sm text-black hover:scale-105 transition-transform flex items-center gap-2"
                                  style={{ backgroundColor: primaryColor }}
                                >
                                  <FiCalendar className="h-4 w-4" /> Demander un devis gratuit
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        </>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </nav>

              {/* Mobile Header Right Side - Phone + Menu Button */}
              <div className="mobile-header-right md:hidden flex items-center gap-2">
                {/* Phone Number - Mobile Header (visible on main page) */}
                <a 
                  href={`tel:${(site?.contact_info?.phone || site?.footer_content?.phone || '').replace(/\s/g, '')}`}
                  className="flex items-center gap-1.5 p-2 rounded-full text-white hover:bg-white/10 transition-colors"
                >
                  <FiPhone className="h-5 w-5" style={{ color: primaryColor }} />
                </a>
                
                {/* Mobile Menu Button */}
                <button 
                  onClick={(e) => { e.preventDefault(); setMobileMenuOpen(true); }}
                  onTouchEnd={(e) => { e.preventDefault(); setMobileMenuOpen(true); }}
                  className="p-2 text-white active:bg-white/10 rounded-lg touch-manipulation"
                >
                  <FiMenu className="h-6 w-6" />
                </button>
              </div>
              
              {/* Desktop Right Side - Phone + Espace Client + CTA */}
              <div className="desktop-header-right hidden md:flex items-center gap-4">
                {/* Phone Number */}
                <a 
                  href={`tel:${(site?.contact_info?.phone || site?.footer_content?.phone || '').replace(/\s/g, '')}`} 
                  className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity"
                >
                  <FiPhone className="h-4 w-4" style={{ color: primaryColor }} />
                  <span className="font-medium">{site?.contact_info?.phone || site?.footer_content?.phone}</span>
                </a>
                
                {/* Separator */}
                <span className="text-white/30">|</span>
                
                {/* Espace Client */}
                {isClientLoggedIn ? (
                  <div className="relative group">
                    <button 
                      onClick={() => window.location.href = '/mon-compte'} 
                      className="flex items-center gap-2 px-4 py-2 rounded-full text-sm text-black font-medium transition-all hover:scale-105"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <FiUser className="h-4 w-4" />
                      {clientUser?.first_name || 'Mon Compte'}
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={() => setShowClientLoginModal(true)} 
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-sm text-white border border-white/30 hover:bg-white/10 transition-all"
                  >
                    <FiUser className="h-4 w-4" />
                    Espace Client
                  </button>
                )}
                
                {/* Réservation CTA */}
                <button 
                  onClick={handleBookingClick} 
                  className="flex items-center gap-2 px-5 py-2 rounded-full font-bold text-sm text-black hover:scale-105 transition-transform shadow-lg" 
                  style={{ backgroundColor: primaryColor }}
                >
                  {bookingButtonText} <FiChevronDown className="h-3 w-3" />
                </button>
              </div>
              </div>
            </div>
          </header>

          {/* Hero Content - Enhanced with Parallax */}
          <div className="hero-content relative z-10 flex items-center justify-center min-h-[70vh] sm:min-h-[75vh] md:min-h-[80vh] lg:min-h-[85vh] pt-52 sm:pt-28 md:pt-36 lg:pt-40 pb-4 sm:pb-6 px-4 sm:px-6">
            <div className="text-center max-w-5xl">
              {/* Animated Badge */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }} 
                animate={{ opacity: 1, scale: 1 }} 
                transition={{ duration: 0.6 }}
                className="hero-badge hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-4 md:mb-6 lg:mb-8"
              >
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: primaryColor }} />
                <span className="text-xs sm:text-sm text-white/80">{heroContent.badge || 'Location PhotoBooth, VideoBooth 360 & Livre d\'Or Vidéo'}</span>
              </motion.div>
              
              {/* Title with Shimmer Effect */}
              <motion.h1 
                initial={{ opacity: 0, y: 40 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.8, delay: 0.2 }}
                className="hero-title text-xl sm:text-2xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-black mb-2 sm:mb-3 md:mb-5 lg:mb-6 shimmer-text px-2 sm:px-4 leading-tight [&_strong]:font-black"
                style={{ fontStyle: 'normal' }}
                dangerouslySetInnerHTML={{ __html: heroContent.title || 'Bienvenue' }}
              />
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: 0.4 }} 
                className="hero-subtitle text-xs sm:text-sm md:text-lg lg:text-xl xl:text-2xl mb-3 sm:mb-4 md:mb-6 lg:mb-8 text-white/90 px-2 sm:px-4 max-w-3xl mx-auto [&_strong]:font-bold"
                dangerouslySetInnerHTML={{ __html: heroContent.subtitle || '' }}
              />
              
              {heroContent.buttonText && (
                <motion.button 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ delay: 0.6 }} 
                  whileHover={{ scale: 1.05, boxShadow: `0 20px 40px ${primaryColor}40` }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleBookingClick} 
                  className="px-12 py-5 rounded-full font-bold text-lg text-black shadow-2xl glow-button" 
                  style={{ backgroundColor: primaryColor }}
                >
                  {heroContent.buttonText}
                </motion.button>
              )}
              
              {/* Trust Indicators */}
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                transition={{ delay: 0.8 }}
                className="mt-12 sm:mt-10 md:mt-10 flex flex-col sm:flex-row flex-wrap justify-center items-center gap-4 sm:gap-8 text-white/80 text-xs sm:text-sm bg-black/30 backdrop-blur-sm rounded-2xl p-3 sm:p-4 mx-4"
              >
                {/* Stats from CMS - hero stats */}
                {(() => {
                  const statsSection = currentPage?.sections?.find(s => s.type === 'stats');
                  const heroStats = statsSection?.content?.heroStats || [
                    { icon: 'star', value: '+500', label: 'événements' },
                    { icon: 'heart', value: '100%', label: 'satisfaction' },
                    { icon: 'calendar', value: '7j/7', label: 'Disponible' }
                  ];
                  const iconMap = {
                    star: FiStar,
                    heart: FiHeart,
                    calendar: FiCalendar,
                    check: FiCheck,
                    award: FiAward
                  };
                  return heroStats.map((stat, i) => {
                    const IconComponent = iconMap[stat.icon] || FiStar;
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <IconComponent className="h-4 w-4" style={{ color: primaryColor }} />
                        <span>{stat.value} {stat.label}</span>
                      </div>
                    );
                  });
                })()}
              </motion.div>
            </div>
          </div>

          {/* Scroll indicator removed as per user request */}
        </div>
      )}

      {/* Standard Header (when no hero first) */}
      {!isHeroFirst && (
        <>
          {/* Top Banner - also on standard pages */}
          {site?.site_settings?.top_banner?.enabled && !topBannerClosed && (
            <div 
              className="relative z-[200] py-2 px-4 text-center text-sm font-medium"
              style={{ 
                backgroundColor: site.site_settings.top_banner.background_color || primaryColor,
                color: site.site_settings.top_banner.text_color || '#000000'
              }}
            >
              <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 flex-wrap">
                <span>{site.site_settings.top_banner.text}</span>
                {site.site_settings.top_banner.promo_code && (
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(site.site_settings.top_banner.promo_code);
                      } catch (err) { console.log('Clipboard not available'); }
                      setPromoCopied(true);
                      setTimeout(() => setPromoCopied(false), 2000);
                    }}
                    className="px-3 py-1 rounded-full bg-black/20 hover:bg-black/30 transition-colors flex items-center gap-1.5 font-bold"
                  >
                    <span>{site.site_settings.top_banner.promo_code}</span>
                    {promoCopied ? <FiCheck className="h-3.5 w-3.5" /> : <FiCopy className="h-3.5 w-3.5" />}
                  </button>
                )}
              </div>
              {site.site_settings.top_banner.closable && (
                <button onClick={() => setTopBannerClosed(true)} className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/10 hover:bg-black/20 flex items-center justify-center">
                  <FiX className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          
          <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-md">
            <div className="max-w-7xl mx-auto">
              {/* Top Bar with Contact Info */}
              <div className="px-6 py-1.5 flex justify-between items-center border-b border-white/10">
                <div className="flex items-center gap-4 md:gap-6 text-white/80">
                  <a href={`mailto:${site?.contact_info?.email || site?.footer_content?.email || `contact@${site?.domain}`}`} className="hidden md:flex items-center gap-2 hover:text-white text-xs transition-colors">
                    <FiMail className="h-3.5 w-3.5" /> {site?.contact_info?.email || site?.footer_content?.email || `contact@${site?.domain}`}
                  </a>
                  <span className="hidden lg:flex items-center gap-2 text-xs">
                    <FiClock className="h-3.5 w-3.5" style={{ color: primaryColor }} /> 
                    <span>{site?.contact_info?.hours || '24/24 & 7/7'}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="flex items-center gap-1.5">
                    <a href={`mailto:${site?.footer_content?.email || site?.contact_info?.email || ''}`} className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors" title="Email"><FiMail className="h-4 w-4" /></a>
                    <a href={site?.footer_content?.instagram_url || site?.footer_content?.socialLinks?.instagram || site?.contact_info?.instagram_url || 'https://www.instagram.com/just_a_smile_events/'} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors" title="Instagram"><FiInstagram className="h-4 w-4" /></a>
                    <a href={site?.footer_content?.facebook_url || site?.footer_content?.socialLinks?.facebook || site?.contact_info?.facebook || '#'} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors" title="Facebook"><FiFacebook className="h-4 w-4" /></a>
                    {(site?.footer_content?.socialLinks?.tiktok || site?.contact_info?.tiktok) && (
                      <a href={site?.footer_content?.socialLinks?.tiktok || site?.contact_info?.tiktok} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors" title="TikTok"><FaTiktok className="h-4 w-4" /></a>
                    )}
                    {(site?.footer_content?.socialLinks?.snapchat || site?.contact_info?.snapchat) && (
                      <a href={site?.footer_content?.socialLinks?.snapchat || site?.contact_info?.snapchat} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors" title="Snapchat"><FaSnapchatGhost className="h-4 w-4" /></a>
                    )}
                  </div>
                  {(site?.footer_content?.instagram_url || site?.footer_content?.socialLinks?.instagram) && (
                    <span className="hidden md:inline text-white/80 text-xs">
                      @{(site?.footer_content?.instagram_url || site?.footer_content?.socialLinks?.instagram || '').split('/').filter(Boolean).pop() || 'jasmile_events'}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Main Navigation */}
              <div className="px-6 py-0 flex items-center justify-between relative rounded-b-2xl bg-black/70">
                {/* Logo Container - Same protruding effect - HIGH z-index */}
                <div className="relative flex-shrink-0 -mb-6 z-[150]">
                  <div 
                    className="bg-black/90 rounded-b-full px-3 pt-0 pb-3 cursor-pointer hover:scale-105 transition-transform border-x border-b border-white/10"
                    onClick={() => navigateToPage('accueil')}
                  >
                    <img src={logoUrl} alt="Logo" className="h-16 md:h-20 w-auto object-contain drop-shadow-2xl" />
                  </div>
                </div>
                
                {/* Desktop Menu */}
                <nav className="desktop-header-nav hidden md:flex items-center gap-1 py-3">
                  {filteredMenuStructure.map((item, idx) => (
                    <div key={idx} className="relative" onMouseEnter={() => item.children?.length > 0 && setHoveredMenu(idx)} onMouseLeave={() => setHoveredMenu(null)}>
                      <button 
                        onClick={() => !item.children?.length && navigateToPage(item.link || item.slug || item.label?.toLowerCase())}
                        className="px-4 py-2 text-sm font-medium flex items-center gap-1 transition-colors hover:text-white" 
                        style={{ color: hoveredMenu === idx ? primaryColor : 'rgba(255,255,255,0.85)' }}
                      >
                        {item.label}
                        {item.children?.length > 0 && <FiChevronDown className={`h-3 w-3 transition-transform ${hoveredMenu === idx ? 'rotate-180' : ''}`} />}
                      </button>
                      
                      <AnimatePresence>
                        {item.children?.length > 0 && hoveredMenu === idx && (
                          <>
                            {/* Backdrop overlay - clickable to close */}
                            <motion.div 
                              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[90] cursor-pointer" style={{ top: '90px' }}
                              onClick={() => setHoveredMenu(null)}
                              onMouseEnter={() => setHoveredMenu(null)}
                            />
                            <motion.div
                              initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="fixed left-0 right-0 z-[100] flex justify-center" style={{ top: '90px' }}
                            >
                              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mx-4" style={{ width: '1100px', maxWidth: 'calc(100vw - 32px)' }}>
                                {/* Mega Menu Header */}
                                <div className="bg-gradient-to-r from-amber-50 via-white to-amber-50 px-8 py-4 border-b border-amber-100/50">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h3 className="font-bold text-gray-900 text-lg">Découvrez nos solutions</h3>
                                      <p className="text-sm text-gray-500">Location de PhotoBooth, VideoBooth 360 et Livre d'Or Vidéo</p>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                      <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 font-medium">✓ Devis gratuit</span>
                                      <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">⭐ +500 événements</span>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Menu Content - Products + CTA Columns */}
                                <div className="flex divide-x divide-gray-100">
                                  {/* Products columns - 3/5 of space */}
                                  <div className="flex-1 grid grid-cols-3 divide-x divide-gray-100">
                                    {item.children?.slice(0, 3).map((child, cidx) => (
                                      <div key={cidx} className={`group/col hover:bg-gradient-to-b hover:from-amber-50/50 hover:to-white transition-all duration-300 ${child.label?.includes('Cadeau') ? 'bg-gradient-to-b from-amber-50/30 to-white' : ''}`}>
                                        {/* Category Header */}
                                        <div className="p-5 pb-3">
                                          <button
                                            onClick={() => { setHoveredMenu(null); navigateToPage(child.link || child.slug || child.label?.toLowerCase().replace(/\s+/g, '-')); }}
                                            className="w-full text-left group"
                                          >
                                            {/* Image without label overlay */}
                                            <div className="relative mb-3 overflow-hidden rounded-2xl">
                                              {/* New ribbon for Livre d'Or */}
                                              {(child.label?.includes('Livre') || child.label?.includes("d'Or")) && (
                                                <div className="absolute top-0 left-0 z-20 overflow-hidden w-20 h-20 pointer-events-none">
                                                  <div className="absolute top-[8px] left-[-28px] w-[100px] bg-red-600 text-white text-[10px] font-bold py-1 text-center transform -rotate-45 shadow-md">
                                                    New
                                                  </div>
                                                </div>
                                              )}
                                              <img 
                                                src={child.image || (child.label?.includes('Photo') && !child.label?.includes('Livre') ? IMAGES.photobooth : child.label?.includes('Livre') || child.label?.includes("d'Or") ? IMAGES.livreor : child.label?.includes('Vidéo') || child.label?.includes('360') ? IMAGES.videobooth : IMAGES.livreor)} 
                                                alt={child.label}
                                                className="w-full h-32 object-cover group-hover:scale-110 transition-transform duration-500" 
                                              />
                                            </div>
                                            <h4 className="font-bold text-gray-900 group-hover:text-amber-600 transition-colors text-sm leading-tight">{child.label}</h4>
                                            {child.description && (
                                              <p className="text-xs text-gray-500 mt-1">{child.description}</p>
                                            )}
                                          </button>
                                        </div>
                                        
                                        {/* Sub-items */}
                                        <div className="px-5 pb-5">
                                          {child.children?.length > 0 ? (
                                            <div className="space-y-1">
                                              {child.children.map((subItem, sidx) => (
                                                <button
                                                  key={sidx}
                                                  onClick={() => { setHoveredMenu(null); navigateToPage(subItem.link || subItem.slug || subItem.label?.toLowerCase().replace(/\s+/g, '-')); }}
                                                  className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-all flex items-center gap-2 group/item"
                                                >
                                                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 group-hover/item:bg-amber-500 transition-colors" />
                                                  <span className="group-hover/item:translate-x-1 transition-transform">{subItem.label}</span>
                                                </button>
                                              ))}
                                            </div>
                                          ) : (
                                            <button
                                              onClick={() => { setHoveredMenu(null); navigateToPage(child.link || child.slug || child.label?.toLowerCase().replace(/\s+/g, '-')); }}
                                              className="w-full text-left px-3 py-2 text-sm font-medium text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-all flex items-center gap-2 group/item"
                                            >
                                              <span>En savoir plus</span>
                                              <FiArrowRight className="h-4 w-4 group-hover/item:translate-x-1 transition-transform" />
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* Right side CTAs - Action buttons */}
                                  <div className="w-72 flex flex-col divide-y divide-gray-200">
                                    {/* Composer sa formule */}
                                    <div className="flex-1 p-5 bg-gradient-to-br from-stone-900 to-black group/cta hover:from-stone-800 transition-all">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setHoveredMenu(null); handleBookingClick(e); }}
                                        className="w-full text-left h-full flex flex-col"
                                      >
                                        <div className="relative mb-3 overflow-hidden rounded-2xl">
                                          <div className="w-full h-16 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)` }}>
                                            <FiPackage className="h-8 w-8 text-black" />
                                          </div>
                                        </div>
                                        <h4 className="font-bold text-white text-sm mb-2 flex items-center gap-2">
                                          📦 Composer votre formule
                                        </h4>
                                        <p className="text-xs text-white/70 mb-3">
                                          Combinez plusieurs machines et bénéficiez de tarifs avantageux
                                        </p>
                                        <div className="mt-auto flex items-center gap-2 text-sm font-medium" style={{ color: primaryColor }}>
                                          <span>Créer ma formule</span>
                                          <FiArrowRight className="h-4 w-4 group-hover/cta:translate-x-1 transition-transform" />
                                        </div>
                                      </button>
                                    </div>
                                    
                                    {/* Carte Cadeau */}
                                    <div className="flex-1 p-5 group/gift transition-all" style={{ background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}30 100%)` }}>
                                      <button
                                        onClick={() => { setHoveredMenu(null); navigateToPage('carte-cadeau'); }}
                                        className="w-full text-left h-full flex flex-col"
                                      >
                                        <div className="relative mb-3 overflow-hidden rounded-2xl">
                                          <div className="w-full h-16 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)` }}>
                                            <FiGift className="h-8 w-8 text-black" />
                                          </div>
                                        </div>
                                        <h4 className="font-bold text-gray-900 group-hover/gift:opacity-80 transition-colors text-sm mb-2">
                                          🎁 Carte Cadeau
                                        </h4>
                                        <p className="text-xs text-gray-600 mb-3">
                                          Offrez une expérience unique à vos proches
                                        </p>
                                        <div className="mt-auto flex items-center gap-2 text-sm font-medium" style={{ color: primaryColor }}>
                                          <span>Acheter une carte</span>
                                          <FiArrowRight className="h-4 w-4 group-hover/gift:translate-x-1 transition-transform" />
                                        </div>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div className="bg-gray-50 px-8 py-4 flex items-center justify-between border-t border-gray-100">
                                  <div className="flex items-center gap-6 text-sm text-gray-500">
                                    <span className="flex items-center gap-2"><FiPhone className="h-4 w-4" /> Besoin d'aide ?</span>
                                    <a href={`tel:${(site?.contact_info?.phone || site?.footer_content?.phone || '').replace(/\s/g, '')}`} className="font-bold hover:text-amber-600 transition-colors" style={{ color: primaryColor }}>{site?.contact_info?.phone || site?.footer_content?.phone}</a>
                                  </div>
                                  <button onClick={(e) => { e.stopPropagation(); setHoveredMenu(null); handleDevisClick(); }} className="px-6 py-2.5 rounded-full font-bold text-sm text-black hover:scale-105 transition-transform flex items-center gap-2" style={{ backgroundColor: primaryColor }}>
                                    <FiCalendar className="h-4 w-4" /> Demander un devis gratuit
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          </>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </nav>
                
                {/* Mobile Header Right Side - Phone + Menu Button */}
                <div className="md:hidden flex items-center gap-2">
                  {/* Phone Number - Mobile Header */}
                  <a 
                    href={`tel:${(site?.contact_info?.phone || site?.footer_content?.phone || '').replace(/\s/g, '')}`}
                    className="flex items-center gap-1.5 p-2 rounded-full text-white hover:bg-white/10 transition-colors"
                  >
                    <FiPhone className="h-5 w-5" style={{ color: primaryColor }} />
                  </a>
                  
                  {/* Mobile Menu Button */}
                  <button 
                    onClick={(e) => { e.preventDefault(); setMobileMenuOpen(true); }}
                    className="p-2 text-white active:bg-white/10 rounded-lg touch-manipulation"
                  >
                    <FiMenu className="h-6 w-6" />
                  </button>
                </div>
                
                {/* Desktop Right Side - Phone + CTA */}
                <div className="hidden md:flex items-center gap-4">
                  <a href={`tel:${(site?.contact_info?.phone || site?.footer_content?.phone || '').replace(/\s/g, '')}`} className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity">
                    <FiPhone className="h-4 w-4" style={{ color: primaryColor }} />
                    <span className="font-medium">{site?.contact_info?.phone || site?.footer_content?.phone}</span>
                  </a>
                  <span className="text-white/30">|</span>
                  <button onClick={handleBookingClick} className="flex items-center gap-2 px-5 py-2 rounded-full font-bold text-sm text-black hover:scale-105 transition-transform shadow-lg" style={{ backgroundColor: primaryColor }}>
                    {bookingButtonText} <FiChevronDown className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          </header>
        </>
      )}

      {/* Mobile Menu - Fixed and Improved */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop - Simplified for better mobile performance */}
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[140] bg-black/70 cursor-pointer"
              style={{ WebkitBackfaceVisibility: 'hidden', backfaceVisibility: 'hidden' }}
              onClick={(e) => {
                e.stopPropagation();
                setMobileMenuOpen(false);
                // Keep "Nos Prestations" expanded for next open
                setExpandedMobileMenu(1);
                setExpandedMobileChild(null);
              }}
              role="button"
              aria-label="Fermer le menu"
              tabIndex={0}
            />
            {/* Menu Panel - Full width on mobile */}
            <motion.div 
              initial={{ x: '100%' }} 
              animate={{ x: 0 }} 
              exit={{ x: '100%' }} 
              transition={{ type: 'tween', duration: 0.3, ease: 'easeOut' }} 
              className="fixed inset-0 z-[150] bg-black shadow-2xl overflow-hidden"
              style={{ WebkitBackfaceVisibility: 'hidden', backfaceVisibility: 'hidden', transform: 'translateZ(0)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="h-full flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-3 sm:p-4 border-b border-white/10">
                  <img src={logoUrl} alt="Logo" className="h-10 sm:h-14 object-contain max-w-[80px] sm:max-w-[120px]" />
                  
                  {/* Phone Number - Compact on small screens */}
                  <a 
                    href={`tel:${(site?.contact_info?.phone || site?.footer_content?.phone || '').replace(/\s/g, '')}`}
                    className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-full bg-white/10 text-white text-xs sm:text-sm font-medium hover:bg-white/20 transition-colors"
                  >
                    <FiPhone className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" style={{ color: primaryColor }} />
                    <span className="font-semibold whitespace-nowrap">{(site?.contact_info?.phone || site?.footer_content?.phone || '06 74 72 95 12').replace(/\s/g, '')}</span>
                  </a>
                  
                  <div className="flex items-center gap-1">
                    {/* Email */}
                    <a 
                      href={`mailto:${site?.footer_content?.email || site?.contact_info?.email || ''}`}
                      className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/70 active:bg-white/20 hover:bg-white/20"
                    >
                      <FiMail className="h-4 w-4" />
                    </a>
                    {/* Instagram */}
                    <a 
                      href={site?.footer_content?.socialLinks?.instagram || site?.contact_info?.instagram || '#'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/70 active:bg-white/20 hover:bg-white/20"
                    >
                      <FiInstagram className="h-4 w-4" />
                    </a>
                    {/* Facebook */}
                    <a 
                      href={site?.footer_content?.socialLinks?.facebook || site?.contact_info?.facebook || '#'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/70 active:bg-white/20 hover:bg-white/20"
                    >
                      <FiFacebook className="h-4 w-4" />
                    </a>
                    {/* Snapchat */}
                    {(site?.footer_content?.socialLinks?.snapchat || site?.contact_info?.snapchat) && (
                      <a 
                        href={site?.footer_content?.socialLinks?.snapchat || site?.contact_info?.snapchat} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/70 active:bg-white/20 hover:bg-white/20"
                      >
                        <FaSnapchatGhost className="h-4 w-4" />
                      </a>
                    )}
                    <button 
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMobileMenuOpen(false);
                        setExpandedMobileMenu(null);
                        setExpandedMobileChild(null);
                      }}
                      className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white active:bg-white/20 hover:bg-white/30"
                      aria-label="Fermer le menu"
                    >
                      <FiX className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                {/* CTA Button */}
                <div className="p-4 space-y-3">
                  <button 
                    type="button"
                    onClick={(e) => { 
                      e.stopPropagation();
                      setMobileMenuOpen(false);
                      setExpandedMobileMenu(null);
                      setExpandedMobileChild(null);
                      setTimeout(() => handleBookingClick(e), 150);
                    }}
                    className="w-full py-3 px-4 rounded-xl font-bold text-black flex items-center justify-center gap-2 active:scale-95 transition-transform" 
                    style={{ backgroundColor: primaryColor }}
                  >
                    <FiCalendar className="h-5 w-5" /> Réservation en ligne <FiArrowRight className="h-4 w-4" />
                  </button>
                  
                  {/* Espace Client Button for Mobile */}
                  {isClientLoggedIn ? (
                    <button 
                      type="button"
                      onClick={(e) => { 
                        e.stopPropagation();
                        setMobileMenuOpen(false);
                        window.location.href = '/mon-compte';
                      }}
                      className="w-full py-3 px-4 rounded-xl font-bold text-white bg-white/10 flex items-center justify-center gap-2 active:scale-95 transition-transform border border-white/20"
                    >
                      <FiUser className="h-5 w-5" /> {clientUser?.first_name || 'Mon Compte'}
                    </button>
                  ) : (
                    <button 
                      type="button"
                      onClick={(e) => { 
                        e.stopPropagation();
                        setMobileMenuOpen(false);
                        setShowClientLoginModal(true);
                      }}
                      className="w-full py-3 px-4 rounded-xl font-medium text-white bg-white/10 flex items-center justify-center gap-2 active:scale-95 transition-transform border border-white/20"
                    >
                      <FiUser className="h-5 w-5" /> Espace Client
                    </button>
                  )}
                </div>
                
                {/* Menu Items */}
                <div className="flex-1 overflow-y-auto px-4 overscroll-contain">
                  {filteredMenuStructure.map((item, idx) => {
                    const isExpanded = expandedMobileMenu === idx;
                    const hasChildren = item.children?.length > 0;
                    
                    return (
                      <div key={idx} className="mb-2">
                        <button 
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (hasChildren) {
                              setExpandedMobileMenu(isExpanded ? null : idx);
                              setExpandedMobileChild(null);
                            } else {
                              setMobileMenuOpen(false);
                              setExpandedMobileMenu(null);
                              navigateToPage(item.link || item.slug || item.label?.toLowerCase());
                            }
                          }}
                          className="w-full flex items-center justify-between py-3 px-3 rounded-xl text-white active:bg-white/10 hover:bg-white/5 touch-manipulation"
                        >
                          <span className="font-semibold">{item.label}</span>
                          {hasChildren && (
                            <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                              <FiChevronDown className="h-5 w-5" />
                            </motion.div>
                          )}
                        </button>
                        <AnimatePresence>
                          {isExpanded && item.children && (
                            <motion.div 
                              initial={{ height: 0, opacity: 0 }} 
                              animate={{ height: 'auto', opacity: 1 }} 
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="py-2 space-y-2">
                                {item.children.map((child, cidx) => {
                                  const childKey = `${idx}-${cidx}`;
                                  const isChildExpanded = expandedMobileChild === childKey;
                                  const hasChildChildren = child.children?.length > 0;
                                  
                                  return (
                                    <div key={cidx} className="bg-white/5 rounded-xl overflow-hidden">
                                      {/* Child category header */}
                                      <button 
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (hasChildChildren) {
                                            setExpandedMobileChild(isChildExpanded ? null : childKey);
                                          } else {
                                            setMobileMenuOpen(false);
                                            setExpandedMobileMenu(null);
                                            setExpandedMobileChild(null);
                                            navigateToPage(child.link || child.slug || child.label?.toLowerCase().replace(/\s+/g, '-'));
                                          }
                                        }} 
                                        className="w-full flex items-center gap-3 p-3 text-left active:bg-white/10 hover:bg-white/10 touch-manipulation"
                                      >
                                        <img 
                                          src={child.label?.includes('Photo') && !child.label?.includes('Livre') ? IMAGES.photobooth : child.label?.includes('Livre') || child.label?.includes("d'Or") ? IMAGES.livreor : child.label?.includes('Vidéo') || child.label?.includes('360') ? IMAGES.videobooth : child.label?.includes('Formule') ? IMAGES.photobooth : IMAGES.livreor} 
                                          className="w-12 h-12 rounded-lg object-cover flex-shrink-0" 
                                          alt="" 
                                        />
                                        <div className="flex-1 min-w-0 relative">
                                          <span className="text-white font-medium block truncate">{child.label}</span>
                                          {/* "New" ribbon for Livre d'Or on mobile */}
                                          {(child.label?.includes('Livre') || child.label?.includes("d'Or")) && (
                                            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded shadow-md">
                                              New
                                            </span>
                                          )}
                                        </div>
                                        {hasChildChildren && (
                                          <FiChevronRight className={`h-4 w-4 text-white/50 transition-transform flex-shrink-0 ${isChildExpanded ? 'rotate-90' : ''}`} />
                                        )}
                                      </button>
                                      {/* Sub-children */}
                                      <AnimatePresence>
                                        {hasChildChildren && isChildExpanded && (
                                          <motion.div 
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="bg-white/5 border-t border-white/10 overflow-hidden"
                                          >
                                            {child.children.map((subChild, sidx) => (
                                              <button 
                                                type="button"
                                                key={sidx}
                                                onClick={(e) => { 
                                                  e.stopPropagation();
                                                  setMobileMenuOpen(false); 
                                                  setExpandedMobileMenu(null);
                                                  setExpandedMobileChild(null);
                                                  navigateToPage(subChild.link || subChild.slug || subChild.label?.toLowerCase().replace(/\s+/g, '-')); 
                                                }}
                                                className="w-full text-left py-2.5 px-4 pl-16 text-sm text-white/80 active:text-white hover:text-white active:bg-white/10 hover:bg-white/5 flex items-center gap-2 touch-manipulation"
                                              >
                                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: primaryColor }} />
                                                <span className="truncate">{subChild.label}</span>
                                              </button>
                                            ))}
                                          </motion.div>
                                        )}
                                      </AnimatePresence>
                                    </div>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
                
                {/* Footer */}
                <div className="p-4 border-t border-white/10">
                  <a 
                    href={`tel:${(site?.contact_info?.phone || site?.footer_content?.phone || '').replace(/\s/g, '')}`} 
                    className="flex items-center gap-2 text-white/60 text-sm active:text-white hover:text-white"
                  >
                    <FiPhone className="h-4 w-4" /> {site?.contact_info?.phone || site?.footer_content?.phone}
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* SEO Page Content - Version simplifiée ultra-optimisée SEO */}
      {seoPage && (
        <div className="bg-black min-h-screen pt-24 md:pt-32">
          {/* SEO Optimized Content */}
          <div className="max-w-4xl mx-auto px-6 py-12">
            {/* Breadcrumb for SEO */}
            <nav className="text-white/50 text-sm mb-8">
              <span className="hover:text-white cursor-pointer" onClick={() => { setSeoPage(null); navigateToPage('accueil'); }}>Accueil</span>
              <span className="mx-2">/</span>
              <span className="hover:text-white cursor-pointer" onClick={() => { setSeoPage(null); navigateToPage('accueil'); }}>Nos Prestations</span>
              <span className="mx-2">/</span>
              <span style={{ color: primaryColor }}>{seoPage.city}</span>
            </nav>

            {/* Main H1 - SEO Critical */}
            <h1 
              className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {seoPage.h1 || `Location ${(() => {
                const slug = seoPage.slug || '';
                if (slug.includes('photobooth')) return 'Photobooth';
                if (slug.includes('videobooth')) return 'VideoBooth 360';
                if (slug.includes('livre-dor')) return 'Livre d\'Or Vidéo';
                return 'Photobooth';
              })()} à ${seoPage.city}`}
            </h1>

            {/* Meta Description as intro paragraph */}
            <p className="text-xl text-white/80 mb-12 leading-relaxed">
              {seoPage.meta_description || `Découvrez notre service de location de ${(() => {
                const slug = seoPage.slug || '';
                if (slug.includes('photobooth')) return 'photobooth';
                if (slug.includes('videobooth')) return 'videobooth 360';
                if (slug.includes('livre-dor')) return 'livre d\'or vidéo';
                return 'photobooth';
              })()} à ${seoPage.city}. Animation professionnelle pour vos événements : mariages, anniversaires, soirées d'entreprise.`}
            </p>

            {/* CTA Button */}
            <div className="mb-16">
              <button 
                onClick={handleBookingClick}
                className="px-8 py-4 rounded-full font-semibold text-black transition-all hover:scale-105 shadow-lg text-lg"
                style={{ backgroundColor: primaryColor }}
              >
                Réserver à {seoPage.city}
              </button>
            </div>

            {/* SEO Content - Rich Text */}
            <article 
              className="prose prose-invert prose-lg max-w-none
                prose-headings:text-white prose-headings:font-bold
                prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-white/85 prose-p:leading-relaxed prose-p:mb-4
                prose-ul:text-white/85 prose-li:my-1
                prose-strong:text-white
                prose-a:underline"
              style={{ 
                '--tw-prose-body': 'rgba(255,255,255,0.85)',
                '--tw-prose-headings': '#ffffff',
                '--tw-prose-links': primaryColor,
                '--tw-prose-bold': '#ffffff',
                '--tw-prose-bullets': primaryColor
              }}
              dangerouslySetInnerHTML={{ __html: seoPage.content }}
            />

            {/* Final CTA */}
            <div className="mt-16 p-8 rounded-2xl text-center" style={{ backgroundColor: `${primaryColor}10`, border: `1px solid ${primaryColor}30` }}>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                {(() => {
                  const slug = seoPage.slug || '';
                  if (slug.includes('photobooth')) return 'Location Photobooth';
                  if (slug.includes('videobooth')) return 'Location VideoBooth 360';
                  if (slug.includes('livre-dor')) return 'Location Livre d\'Or Vidéo';
                  return 'Location Photobooth';
                })()} à {seoPage.city}
              </h2>
              <p className="text-white/70 mb-6">
                Réservez dès maintenant pour votre événement à {seoPage.city} et en région PACA.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={handleBookingClick}
                  className="px-8 py-4 rounded-full font-semibold text-black transition-all hover:scale-105"
                  style={{ backgroundColor: primaryColor }}
                >
                  Réserver maintenant
                </button>
                <button 
                  onClick={handleDevisClick}
                  className="px-8 py-4 rounded-full font-semibold text-white transition-all hover:scale-105 border-2"
                  style={{ borderColor: primaryColor }}
                >
                  Demander un devis
                </button>
              </div>
            </div>

            {/* Back link */}
            <div className="mt-12 text-center">
              <button 
                onClick={() => { setSeoPage(null); navigateToPage('accueil'); }}
                className="text-white/50 hover:text-white transition-colors"
              >
                ← Retour à l'accueil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading SEO Page */}
      {loadingSeoPage && (
        <div className="min-h-[60vh] flex items-center justify-center bg-black">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: `${primaryColor} transparent ${primaryColor} ${primaryColor}` }} />
            <p className="text-white/60">Chargement de la page...</p>
          </div>
        </div>
      )}

      {/* Blog Article Content */}
      {blogArticle && (
        <div className="bg-black min-h-screen pt-24 md:pt-32">
          {/* Article Header */}
          <div className="max-w-4xl mx-auto px-6">
            {/* Back button */}
            <button 
              onClick={() => { setBlogArticle(null); navigateToPage('blog'); }}
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8"
            >
              <FiArrowLeft className="h-4 w-4" /> Retour au blog
            </button>
            
            {/* Category & Date */}
            <div className="flex items-center gap-4 mb-4">
              {blogArticle.category && (
                <span className="px-3 py-1 text-xs rounded-full text-black font-medium" style={{ backgroundColor: primaryColor }}>
                  {blogArticle.category}
                </span>
              )}
              {blogArticle.date && (
                <span className="text-white/40 text-sm">{blogArticle.date}</span>
              )}
            </div>
            
            {/* Title */}
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {blogArticle.title}
            </h1>
            
            {/* Author */}
            {blogArticle.author && (
              <p className="text-white/60 mb-8">Par {blogArticle.author}</p>
            )}
            
            {/* Featured Image */}
            {blogArticle.image && (
              <div className="aspect-video rounded-2xl overflow-hidden mb-12">
                <img src={blogArticle.image} alt={blogArticle.title} className="w-full h-full object-cover" />
              </div>
            )}
            
            {/* Article Content */}
            <div 
              className="prose prose-invert prose-lg max-w-none seo-content
                prose-headings:text-white prose-headings:font-bold
                prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:text-white
                prose-h3:text-xl prose-h3:text-white
                prose-p:text-white/90 prose-p:leading-relaxed
                prose-ul:text-white/90 prose-li:my-2 prose-li:text-white/90
                prose-ol:text-white/90
                prose-a:no-underline hover:prose-a:underline
                prose-strong:text-white prose-em:text-white/90"
              style={{ 
                '--tw-prose-body': 'rgba(255,255,255,0.9)',
                '--tw-prose-headings': '#ffffff',
                '--tw-prose-links': primaryColor,
                color: 'rgba(255,255,255,0.9)'
              }}
              dangerouslySetInnerHTML={{ __html: blogArticle.content || `<p>${blogArticle.excerpt}</p>` }}
            />
            
            {/* CTA */}
            <div className="mt-16 text-center p-8 rounded-2xl" style={{ backgroundColor: `${primaryColor}15` }}>
              <h3 className="text-2xl font-bold text-white mb-4">Envie de vivre cette expérience ?</h3>
              <p className="text-white/60 mb-6">Réservez votre photobooth ou vidéobooth 360° pour votre prochain événement</p>
              <button 
                onClick={() => { setBlogArticle(null); setShowBooking(true); }}
                className="px-8 py-4 rounded-full font-semibold text-black transition-all hover:scale-105"
                style={{ backgroundColor: primaryColor }}
              >
                Réserver maintenant
              </button>
            </div>
            
            {/* Back to blog */}
            <div className="text-center py-12">
              <button 
                onClick={() => { setBlogArticle(null); navigateToPage('blog'); }}
                className="text-white/60 hover:text-white transition-colors"
              >
                ← Voir tous les articles
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Blog Article */}
      {loadingBlogArticle && (
        <div className="min-h-[60vh] flex items-center justify-center bg-black">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: `${primaryColor} transparent ${primaryColor} ${primaryColor}` }} />
            <p className="text-white/60">Chargement de l'article...</p>
          </div>
        </div>
      )}

      {/* Custom dedicated pages matching WordPress design */}
      
      {/* PhotoBooth Presentation Page - also handle 'photobooth' slug */}
      {(currentPage?.slug === 'photobooth-presentation' || currentPage?.slug === 'photobooth' || currentPage?.slug === 'location-de-photobooth') && (
        <PhotoboothPresentationPage
          pageData={currentPage}
          productData={getProductForService('photobooth')}
          onNavigate={(slug) => navigateToPage(slug)}
          onBookingClick={handleBookingClick}
        />
      )}
      
      {/* VideoBooth 360 Presentation Page - also handle 'videobooth-360' slug */}
      {(currentPage?.slug === 'videobooth-360-presentation' || currentPage?.slug === 'videobooth-360' || currentPage?.slug === 'location-de-videobooth-360') && (
        <VideoBooth360PresentationPage
          pageData={currentPage}
          productData={getProductForService('videobooth-360')}
          onNavigate={(slug) => navigateToPage(slug)}
          onBookingClick={handleBookingClick}
        />
      )}
      
      {/* Livre d'Or Vidéo Presentation Page - also handle 'livre-dor-video' slug */}
      {(currentPage?.slug === 'livre-dor-presentation' || currentPage?.slug === 'livre-dor-video' || currentPage?.slug === 'location-de-livre-dor') && (
        <LivreDorPresentationPage
          pageData={currentPage}
          productData={getProductForService('livre-dor')}
          onNavigate={(slug) => navigateToPage(slug)}
          onBookingClick={handleBookingClick}
        />
      )}

      {/* Formulas Page - Composer votre formule */}
      {(currentPage?.slug === 'formules' || currentPage?.slug === 'nos-formules' || currentPage?.slug === 'composer-formule') && (
        <FormulasPage
          site={site}
          primaryColor={primaryColor}
          onBooking={(selection) => {
            // Set the selected formula for booking
            setSelectedFormulaForBooking(selection);
            setShowBooking(true);
          }}
        />
      )}

      {/* Contact Page - Dedicated contact page */}
      {currentPage?.slug === 'contact' && (
        <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
          {/* Contact Header */}
          <div className="pt-32 pb-16 px-6 text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">Contactez-nous</h1>
            <p className="text-white/60 text-lg max-w-xl mx-auto">
              Nous sommes là pour répondre à toutes vos questions
            </p>
          </div>
          
          {/* Contact Content */}
          <div className="max-w-4xl mx-auto px-6 pb-20">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div className="space-y-8">
                <h2 className="text-2xl font-bold text-white mb-6">Nos coordonnées</h2>
                
                {/* Phone */}
                <a href={`tel:${(site?.contact_info?.phone || site?.footer_content?.phone || '06 74 72 95 12').replace(/\s/g, '')}`} 
                   className="flex items-center gap-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors group">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                    <FiPhone className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Téléphone</p>
                    <p className="text-white font-medium text-lg group-hover:underline">
                      {site?.contact_info?.phone || site?.footer_content?.phone || '06 74 72 95 12'}
                    </p>
                  </div>
                </a>

                {/* Email */}
                <a href={`mailto:${site?.contact_info?.email || site?.footer_content?.email || 'contact@justasmile-events.com'}`}
                   className="flex items-center gap-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors group">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                    <FiMail className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Email</p>
                    <p className="text-white font-medium group-hover:underline">
                      {site?.contact_info?.email || site?.footer_content?.email || 'contact@justasmile-events.com'}
                    </p>
                  </div>
                </a>

                {/* Address */}
                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                    <FiMapPin className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Adresse</p>
                    <p className="text-white font-medium">
                      {site?.footer_content?.address || '23 Av. de la Crau, 13118 Istres'}
                    </p>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                    <FiClock className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Horaires</p>
                    <p className="text-white font-medium">
                      {site?.contact_info?.hours || '24/24 & 7/7'}
                    </p>
                  </div>
                </div>

                {/* Social Links */}
                <div className="flex gap-4 pt-4">
                  {(site?.contact_info?.instagram || site?.footer_content?.socialLinks?.instagram) && (
                    <a href={site?.contact_info?.instagram?.startsWith('http') ? site?.contact_info?.instagram : `https://instagram.com/${site?.contact_info?.instagram || site?.footer_content?.socialLinks?.instagram}`} 
                       target="_blank" rel="noopener noreferrer"
                       className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                      <FiInstagram className="w-5 h-5 text-white" />
                    </a>
                  )}
                  {(site?.contact_info?.facebook || site?.footer_content?.socialLinks?.facebook) && (
                    <a href={site?.contact_info?.facebook?.startsWith('http') ? site?.contact_info?.facebook : `https://facebook.com/${site?.contact_info?.facebook || site?.footer_content?.socialLinks?.facebook}`}
                       target="_blank" rel="noopener noreferrer"
                       className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                      <FiFacebook className="w-5 h-5 text-white" />
                    </a>
                  )}
                  {(site?.contact_info?.snapchat || site?.footer_content?.socialLinks?.snapchat) && (
                    <a href={site?.contact_info?.snapchat || site?.footer_content?.socialLinks?.snapchat}
                       target="_blank" rel="noopener noreferrer"
                       className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                      <FaSnapchatGhost className="w-5 h-5 text-white" />
                    </a>
                  )}
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white/5 rounded-2xl p-6 md:p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Envoyez-nous un message</h2>
                <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); toast.success('Message envoyé !'); }}>
                  <div className="grid grid-cols-2 gap-4">
                    <input type="text" placeholder="Prénom" required
                           className="px-4 py-3 bg-black/50 border border-white/20 rounded-lg text-white placeholder-white/40 focus:border-white/50 focus:outline-none" />
                    <input type="text" placeholder="Nom" required
                           className="px-4 py-3 bg-black/50 border border-white/20 rounded-lg text-white placeholder-white/40 focus:border-white/50 focus:outline-none" />
                  </div>
                  <input type="email" placeholder="Email" required
                         className="w-full px-4 py-3 bg-black/50 border border-white/20 rounded-lg text-white placeholder-white/40 focus:border-white/50 focus:outline-none" />
                  <input type="tel" placeholder="Téléphone"
                         className="w-full px-4 py-3 bg-black/50 border border-white/20 rounded-lg text-white placeholder-white/40 focus:border-white/50 focus:outline-none" />
                  <textarea placeholder="Votre message" rows={4} required
                            className="w-full px-4 py-3 bg-black/50 border border-white/20 rounded-lg text-white placeholder-white/40 focus:border-white/50 focus:outline-none resize-none" />
                  <button type="submit" 
                          className="w-full py-3 rounded-lg font-bold text-black transition-transform hover:scale-[1.02]"
                          style={{ backgroundColor: primaryColor }}>
                    Envoyer le message
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* PhotoBooth Modèles Page */}
      {currentPage?.slug === 'photobooth-modeles' && (
        <ModelesPage
          pageData={currentPage}
          onNavigate={(slug) => navigateToPage(slug)}
          onBookingClick={handleBookingClick}
          serviceType="photobooth"
        />
      )}
      
      {/* PhotoBooth Tarifs Page */}
      {currentPage?.slug === 'photobooth-tarifs' && (
        <TarifsPage
          pageData={{
            ...currentPage,
            content: {
              ...currentPage?.content,
              pricing: {
                plans: getProductTarifs(getProductForService('photobooth'), 'photobooth')
              }
            }
          }}
          product={getProductForService('photobooth')}
          onNavigate={(slug) => navigateToPage(slug)}
          onBookingClick={handleBookingClick}
          serviceType="photobooth"
        />
      )}
      
      {/* VideoBooth 360 Réalisations Page */}
      {currentPage?.slug === 'videobooth-360-modeles' && (
        <RealisationsPage
          serviceType="videobooth-360"
        />
      )}
      
      {/* VideoBooth 360 Tarifs Page */}
      {currentPage?.slug === 'videobooth-360-tarifs' && (
        <TarifsPage
          pageData={{
            ...currentPage,
            content: {
              ...currentPage?.content,
              pricing: {
                plans: getProductTarifs(getProductForService('videobooth-360'), 'videobooth-360')
              }
            }
          }}
          product={getProductForService('videobooth-360')}
          onNavigate={(slug) => navigateToPage(slug)}
          onBookingClick={handleBookingClick}
          serviceType="videobooth-360"
        />
      )}
      
      {/* Livre d'Or Réalisations Page */}
      {currentPage?.slug === 'livre-dor-modeles' && (
        <RealisationsPage
          serviceType="livre-dor"
        />
      )}
      
      {/* Livre d'Or Tarifs Page */}
      {currentPage?.slug === 'livre-dor-tarifs' && (
        <TarifsPage
          pageData={{
            ...currentPage,
            content: {
              ...currentPage?.content,
              pricing: {
                plans: getProductTarifs(getProductForService('livre-dor'), 'livre-dor')
              }
            }
          }}
          product={getProductForService('livre-dor')}
          onNavigate={(slug) => navigateToPage(slug)}
          onBookingClick={handleBookingClick}
          serviceType="livre-dor"
        />
      )}

      {/* Page Content - Skip for custom pages */}
      {!['photobooth-presentation', 'videobooth-360-presentation', 'livre-dor-presentation', 
         'location-de-photobooth', 'location-de-videobooth-360', 'location-de-livre-dor', 
         'photobooth', 'videobooth-360', 'livre-dor-video',
         'photobooth-modeles', 'photobooth-tarifs', 
         'videobooth-360-modeles', 'videobooth-360-tarifs',
         'livre-dor-modeles', 'livre-dor-tarifs',
         'formules', 'nos-formules', 'composer-formule',
         'contact'].includes(currentPage?.slug) && 
       currentPage?.sections?.map((section, index) => (
        <div key={section.id || index}>{renderSection(section, index)}</div>
      ))}
      
      {/* Client Logos Scrolling Banner - Hide on videobooth-360-presentation and product pages */}
      {!['videobooth-360-presentation', 'videobooth-360', 'photobooth', 'photobooth-presentation', 
         'livre-dor-video', 'livre-dor-presentation', 'location-de-photobooth', 'location-de-videobooth-360', 'location-de-livre-dor'].includes(currentPage?.slug) && CLIENT_LOGOS.length > 0 && (
      <div className="bg-white border-t border-gray-200 py-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 uppercase tracking-wider text-center">Ils nous font confiance</h2>
        </div>
        <div className="relative max-w-6xl mx-auto px-4">
          {/* Navigation arrows */}
          <button 
            onClick={() => setPartnerLogoIndex(prev => Math.max(0, prev - 1))}
            disabled={partnerLogoIndex === 0}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all ${
              partnerLogoIndex === 0 
                ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                : 'bg-gray-800 text-white hover:bg-black hover:scale-110'
            }`}
            data-testid="partner-logos-prev"
          >
            <FiChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <button 
            onClick={() => {
              const logosPerView = window.innerWidth < 640 ? 2 : window.innerWidth < 1024 ? 3 : 4;
              const maxIndex = Math.max(0, CLIENT_LOGOS.length - logosPerView);
              setPartnerLogoIndex(prev => Math.min(maxIndex, prev + 1));
            }}
            disabled={partnerLogoIndex >= Math.max(0, CLIENT_LOGOS.length - (window.innerWidth < 640 ? 2 : window.innerWidth < 1024 ? 3 : 4))}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all ${
              partnerLogoIndex >= Math.max(0, CLIENT_LOGOS.length - 4)
                ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                : 'bg-gray-800 text-white hover:bg-black hover:scale-110'
            }`}
            data-testid="partner-logos-next"
          >
            <FiChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          
          {/* Logos container with smooth transition */}
          <div className="overflow-hidden mx-12 sm:mx-14">
            <div 
              className="flex gap-4 sm:gap-6 md:gap-8 transition-transform duration-500 ease-out items-center"
              style={{ transform: `translateX(-${partnerLogoIndex * (window.innerWidth < 640 ? 160 : window.innerWidth < 1024 ? 200 : 260)}px)` }}
            >
              {CLIENT_LOGOS.map((client, i) => (
                <div 
                  key={i} 
                  className="flex-shrink-0 group"
                >
                  {client.url ? (
                    client.link ? (
                      <a href={client.link} target="_blank" rel="noopener noreferrer" className="block">
                        <div className="relative h-16 w-32 sm:h-20 sm:w-44 md:h-24 md:w-56 flex items-center justify-center overflow-hidden bg-gray-50 rounded-lg p-2 hover:bg-gray-100 transition-colors">
                          <img 
                            src={client.url} 
                            alt={client.name || `Partenaire ${i + 1}`}
                            className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-110"
                          />
                        </div>
                      </a>
                    ) : (
                      <div className="relative h-16 w-32 sm:h-20 sm:w-44 md:h-24 md:w-56 flex items-center justify-center overflow-hidden bg-gray-50 rounded-lg p-2 hover:bg-gray-100 transition-colors cursor-pointer">
                        <img 
                          src={client.url} 
                          alt={client.name || `Partenaire ${i + 1}`}
                          className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-110"
                        />
                      </div>
                    )
                  ) : (
                    <span 
                      className="text-lg font-bold tracking-wider border border-gray-300 rounded-lg px-4 py-2 hover:border-gray-400 text-gray-600 hover:text-gray-800 transition-all"
                    >
                      {client.name}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          {/* Dots indicator for mobile */}
          <div className="flex justify-center gap-2 mt-6 sm:hidden">
            {CLIENT_LOGOS.slice(0, Math.ceil(CLIENT_LOGOS.length / 2)).map((_, idx) => (
              <button 
                key={idx}
                onClick={() => setPartnerLogoIndex(idx * 2)}
                className={`w-2 h-2 rounded-full transition-all ${Math.floor(partnerLogoIndex / 2) === idx ? 'bg-gray-800 w-4' : 'bg-gray-300'}`}
              />
            ))}
          </div>
        </div>
      </div>
      )}

      {/* Google Reviews Section - Hide on videobooth-360-presentation as it has its own testimonials */}
      {currentPage?.slug !== 'videobooth-360-presentation' && (
      <div className="bg-black py-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          
          {/* Rating Animation */}
          {ratingAnimation && (
            <div className="flex justify-center mb-2">
              <Lottie 
                animationData={ratingAnimation} 
                loop={true}
                style={{ width: 180, height: 180 }}
              />
            </div>
          )}
          
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Qui de mieux que vous, pour en parler ?
          </h2>
          
          {/* Google Rating Summary */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-12">
            <div className="text-center">
              <p className="text-2xl font-bold text-white mb-2">EXCELLENT</p>
              <div className="flex gap-1 justify-center mb-2">
                {[1,2,3,4,5].map(i => (
                  <FiStar key={i} className="h-6 w-6 fill-current" style={{ color: i <= Math.round(googleReviewsInfo.average) ? primaryColor : '#333' }} />
                ))}
              </div>
              <p className="text-white/60 text-sm">
                {googleReviewsInfo.average > 0 ? `Note: ${googleReviewsInfo.average}/5 - ` : ''}
                Basée sur {googleReviewsInfo.total || 89} avis
              </p>
              <div className="flex items-center justify-center gap-2 mt-2">
                <svg className="h-6 w-6" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-white font-medium">Google</span>
              </div>
            </div>
          </div>

          {/* Reviews Carousel - Horizontal scrolling with manual controls */}
          <div className="overflow-hidden relative" style={{ isolation: 'isolate' }}>
            {/* Desktop: Manual carousel with arrows */}
            <div className="hidden sm:block relative">
              {/* Navigation arrows */}
              <button 
                onClick={() => setDesktopReviewIndex(prev => Math.max(0, prev - 1))}
                disabled={desktopReviewIndex === 0}
                className={`absolute left-2 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                  desktopReviewIndex === 0 
                    ? 'bg-white/5 text-white/30 cursor-not-allowed' 
                    : 'bg-white/10 text-white hover:bg-white/20 hover:scale-110'
                }`}
                data-testid="reviews-prev-btn"
              >
                <FiChevronLeft className="w-6 h-6" />
              </button>
              <button 
                onClick={() => {
                  const reviews = googleReviews.length > 0 ? googleReviews : GOOGLE_REVIEWS;
                  const maxIndex = Math.max(0, reviews.length - 3);
                  setDesktopReviewIndex(prev => Math.min(maxIndex, prev + 1));
                }}
                disabled={desktopReviewIndex >= Math.max(0, (googleReviews.length > 0 ? googleReviews : GOOGLE_REVIEWS).length - 3)}
                className={`absolute right-2 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                  desktopReviewIndex >= Math.max(0, (googleReviews.length > 0 ? googleReviews : GOOGLE_REVIEWS).length - 3)
                    ? 'bg-white/5 text-white/30 cursor-not-allowed' 
                    : 'bg-white/10 text-white hover:bg-white/20 hover:scale-110'
                }`}
                data-testid="reviews-next-btn"
              >
                <FiChevronRight className="w-6 h-6" />
              </button>
              
              {/* Left/Right fade masks */}
              <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-black to-transparent z-20 pointer-events-none"></div>
              <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-black to-transparent z-20 pointer-events-none"></div>
              
              {/* Reviews container with smooth transition */}
              <div className="overflow-hidden py-4 px-16">
                <div 
                  className="flex gap-4 transition-transform duration-500 ease-out"
                  style={{ transform: `translateX(-${desktopReviewIndex * 340}px)` }}
                >
                  {(googleReviews.length > 0 ? googleReviews : GOOGLE_REVIEWS).map((review, idx) => (
                    <div 
                      key={idx}
                      className="bg-zinc-900 rounded-2xl p-6 w-[320px] flex-shrink-0 shadow-lg"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        {review.avatar?.startsWith('http') ? (
                          <img 
                            src={review.avatar} 
                            alt={review.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                            style={{ backgroundColor: primaryColor }}
                          >
                            {typeof review.avatar === 'string' ? review.avatar.charAt(0) : review.name?.charAt(0) || 'U'}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium text-sm truncate">{review.name}</p>
                          <p className="text-white/40 text-xs">{review.date}</p>
                        </div>
                        <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      </div>
                      
                      <div className="flex gap-0.5 mb-3">
                        {[1,2,3,4,5].map(i => (
                          <FiStar 
                            key={i} 
                            className={`h-4 w-4 ${i <= review.rating ? 'fill-current' : ''}`}
                            style={{ color: i <= review.rating ? primaryColor : '#333' }}
                          />
                        ))}
                      </div>
                      
                      <p className="text-white/70 text-sm leading-relaxed line-clamp-3">
                        {review.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Dots indicator */}
              <div className="flex justify-center gap-2 mt-2">
                {Array.from({ length: Math.min((googleReviews.length > 0 ? googleReviews : GOOGLE_REVIEWS).length, Math.max(0, (googleReviews.length > 0 ? googleReviews : GOOGLE_REVIEWS).length - 3) + 1) }).map((_, idx) => (
                  <button 
                    key={idx}
                    onClick={() => setDesktopReviewIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${idx === desktopReviewIndex ? 'bg-white w-6' : 'bg-white/30 hover:bg-white/50'}`}
                    data-testid={`review-dot-${idx}`}
                  />
                ))}
              </div>
            </div>
            
            {/* Mobile: One review at a time with arrows */}
            <div className="sm:hidden py-4">
              <div className="relative px-4">
                {/* Navigation arrows */}
                <button 
                  onClick={() => {
                    const reviews = googleReviews.length > 0 ? googleReviews : GOOGLE_REVIEWS;
                    setMobileReviewIndex(prev => prev === 0 ? reviews.length - 1 : prev - 1);
                  }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-30 w-8 h-8 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20"
                  data-testid="mobile-reviews-prev-btn"
                >
                  <FiChevronLeft className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => {
                    const reviews = googleReviews.length > 0 ? googleReviews : GOOGLE_REVIEWS;
                    setMobileReviewIndex(prev => (prev + 1) % reviews.length);
                  }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-30 w-8 h-8 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20"
                  data-testid="mobile-reviews-next-btn"
                >
                  <FiChevronRight className="w-5 h-5" />
                </button>
                
                {/* Review card */}
                <div className="bg-zinc-900 rounded-2xl p-4 mx-8 shadow-lg">
                  {(() => {
                    const reviews = googleReviews.length > 0 ? googleReviews : GOOGLE_REVIEWS;
                    const review = reviews[mobileReviewIndex] || reviews[0];
                    return (
                      <>
                        <div className="flex items-center gap-2 mb-3">
                          {review?.avatar?.startsWith('http') ? (
                            <img 
                              src={review.avatar} 
                              alt={review.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                              style={{ backgroundColor: primaryColor }}
                            >
                              {typeof review?.avatar === 'string' ? review.avatar.charAt(0) : review?.name?.charAt(0) || 'U'}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium text-sm truncate">{review?.name}</p>
                            <p className="text-white/40 text-xs">{review?.date}</p>
                          </div>
                          <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                        </div>
                        
                        <div className="flex gap-0.5 mb-3">
                          {[1,2,3,4,5].map(i => (
                            <FiStar 
                              key={i} 
                              className={`h-4 w-4 ${i <= (review?.rating || 5) ? 'fill-current' : ''}`}
                              style={{ color: i <= (review?.rating || 5) ? primaryColor : '#333' }}
                            />
                          ))}
                        </div>
                        
                        <p className="text-white/70 text-sm leading-relaxed">
                          {review?.text}
                        </p>
                      </>
                    );
                  })()}
                </div>
                
                {/* Dots indicator */}
                <div className="flex justify-center gap-2 mt-4">
                  {(googleReviews.length > 0 ? googleReviews : GOOGLE_REVIEWS).map((_, idx) => (
                    <button 
                      key={idx}
                      onClick={() => setMobileReviewIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${idx === mobileReviewIndex ? 'bg-white w-4' : 'bg-white/30'}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* View All Reviews Link */}
          <div className="text-center mt-8">
            <a 
              href="https://www.google.com/maps/place/Just+a+Smile+Events/@43.5887493,4.9424857,17z/data=!4m8!3m7!1s0x12b6059b03832cb1:0x867f0441c5fd9763!8m2!3d43.5887493!4d4.9424857!9m1!1b1!16s%2Fg%2F11tbmz_2x4" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white/80 hover:bg-white/5 transition-all text-sm"
            >
              Voir tous les avis sur Google
              <FiArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
      )}

      {/* Footer */}
      <footer className="bg-[#0a0a0a] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-4 gap-12">
            {/* Logo et description */}
            <div className="md:col-span-1">
              <img src={logoUrl} alt="Logo" className="h-16 mb-4" />
              <p className="text-white/60 text-sm leading-relaxed mb-6">{site?.footer_content?.description || 'Spécialiste de la location de PhotoBooth, VideoBooth 360 et Livre d\'Or Vidéo pour tous vos événements.'}</p>
              <div className="flex gap-3">
                {/* Email */}
                {(site?.footer_content?.email || site?.contact_info?.email) && (
                  <a href={`mailto:${site?.footer_content?.email || site?.contact_info?.email}`} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors" title="Email"><FiMail className="h-5 w-5" /></a>
                )}
                {/* Instagram */}
                {(site?.footer_content?.instagram_url || site?.footer_content?.socialLinks?.instagram || site?.contact_info?.instagram) && (
                  <a href={site?.footer_content?.instagram_url || site?.footer_content?.socialLinks?.instagram || site?.contact_info?.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors" title="Instagram"><FiInstagram className="h-5 w-5" /></a>
                )}
                {/* Facebook */}
                {(site?.footer_content?.facebook_url || site?.footer_content?.socialLinks?.facebook || site?.contact_info?.facebook) && (
                  <a href={site?.footer_content?.facebook_url || site?.footer_content?.socialLinks?.facebook || site?.contact_info?.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors" title="Facebook"><FiFacebook className="h-5 w-5" /></a>
                )}
                {/* TikTok */}
                {(site?.footer_content?.socialLinks?.tiktok || site?.contact_info?.tiktok) && (
                  <a href={site?.footer_content?.socialLinks?.tiktok || site?.contact_info?.tiktok} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors" title="TikTok"><FaTiktok className="h-5 w-5" /></a>
                )}
                {/* YouTube */}
                {(site?.footer_content?.socialLinks?.youtube || site?.contact_info?.youtube) && (
                  <a href={site?.footer_content?.socialLinks?.youtube || site?.contact_info?.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors" title="YouTube"><FaYoutube className="h-5 w-5" /></a>
                )}
                {/* Snapchat */}
                {(site?.footer_content?.socialLinks?.snapchat || site?.contact_info?.snapchat) && (
                  <a href={site?.footer_content?.socialLinks?.snapchat || site?.contact_info?.snapchat} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors" title="Snapchat"><FaSnapchatGhost className="h-5 w-5" /></a>
                )}
              </div>
            </div>
            
            {/* Nos Solutions */}
            <div>
              <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Nos Solutions</h4>
              <div className="space-y-2 text-white/60 text-sm">
                <p onClick={() => navigateToPage('photobooth-presentation')} className="hover:text-white cursor-pointer flex items-center gap-2">
                  <span style={{ color: primaryColor }}>›</span> PhotoBooth
                </p>
                <p onClick={() => navigateToPage('videobooth-360-presentation')} className="hover:text-white cursor-pointer flex items-center gap-2">
                  <span style={{ color: primaryColor }}>›</span> VideoBooth 360°
                </p>
                <p onClick={() => navigateToPage('livre-dor-presentation')} className="hover:text-white cursor-pointer flex items-center gap-2">
                  <span style={{ color: primaryColor }}>›</span> Livre d'Or Vidéo
                </p>
                <p onClick={() => navigateToPage('photobooth-modeles')} className="hover:text-white cursor-pointer flex items-center gap-2">
                  <span style={{ color: primaryColor }}>›</span> Nos Modèles
                </p>
              </div>
            </div>
            
            {/* Liens Rapides */}
            <div>
              <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Liens Rapides</h4>
              <div className="space-y-2 text-white/60 text-sm">
                <p onClick={handleBookingClick} className="hover:text-white cursor-pointer flex items-center gap-2">
                  <FiCalendar className="h-3.5 w-3.5" style={{ color: primaryColor }} /> Réserver en ligne
                </p>
                <p onClick={() => navigateToPage('carte-cadeau')} className="hover:text-white cursor-pointer flex items-center gap-2">
                  <FiGift className="h-3.5 w-3.5" style={{ color: primaryColor }} /> Carte Cadeau
                </p>
                <p onClick={() => isClientLoggedIn ? (window.location.href = '/mon-compte') : setShowClientLoginModal(true)} className="hover:text-white cursor-pointer flex items-center gap-2">
                  <FiUser className="h-3.5 w-3.5" style={{ color: primaryColor }} /> Mon Compte
                </p>
                <p onClick={() => navigateToPage('contact')} className="hover:text-white cursor-pointer flex items-center gap-2">
                  <FiMail className="h-3.5 w-3.5" style={{ color: primaryColor }} /> Nous Contacter
                </p>
                <p onClick={() => navigateToPage('avis-clients')} className="hover:text-white cursor-pointer flex items-center gap-2">
                  <FiStar className="h-3.5 w-3.5" style={{ color: primaryColor }} /> Avis Clients
                </p>
              </div>
            </div>
            
            {/* Contact */}
            <div id="contact-section">
              <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Contact</h4>
              <div className="space-y-3 text-white/60 text-sm">
                <a href={`tel:${(site?.footer_content?.phone || site?.contact_info?.phone || '').replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-white"><FiPhone className="h-4 w-4" /> {site?.footer_content?.phone || site?.contact_info?.phone}</a>
                <a href={`mailto:${site?.footer_content?.email || site?.contact_info?.email || ''}`} className="flex items-center gap-2 hover:text-white"><FiMail className="h-4 w-4" /> {site?.footer_content?.email || site?.contact_info?.email}</a>
                {(site?.footer_content?.address || site?.contact_info?.address) && (
                  <p className="flex items-center gap-2"><FiMapPin className="h-4 w-4" /> {site?.footer_content?.address || site?.contact_info?.address}</p>
                )}
              </div>
              <button data-testid="footer-devis-btn" onClick={handleDevisClick} className="mt-4 w-full py-3 rounded-xl font-semibold text-sm text-black hover:scale-105 transition-transform" style={{ backgroundColor: primaryColor }}>Demander un devis</button>
            </div>
          </div>
        </div>
        
        {/* SEO Cities Scrolling Banner - Only show published SEO pages */}
        {publishedSeoPages.length > 0 && (
        <div className="border-t border-white/5 py-4 overflow-hidden bg-black/50">
          <div className="relative">
            <div className="flex animate-scroll-x-slow gap-6 whitespace-nowrap text-xs text-white/30">
              {[...publishedSeoPages, ...publishedSeoPages].map((page, i) => {
                // Handle both old format (service) and new format (product_name)
                let displayText = page.title;
                if (page.product_name && page.city) {
                  displayText = `${page.product_name} ${page.city}`;
                } else if (page.service && page.city) {
                  displayText = `${page.service.charAt(0).toUpperCase() + page.service.slice(1).replace(/-/g, ' ')} ${page.city}`;
                }
                
                return (
                  <button 
                    key={`${page.id}-${i}`}
                    onClick={(e) => {
                      e.preventDefault();
                      // Navigate to SEO page without reload
                      window.history.pushState({}, '', `/seo/${page.slug}`);
                      // Fetch and display SEO page content
                      fetch(`${API_URL}/api/seo-pages/slug/${page.slug}`)
                        .then(res => res.json())
                        .then(data => {
                          setSeoPage(data);
                          window.scrollTo(0, 0);
                        })
                        .catch(err => console.error('Error loading SEO page:', err));
                    }}
                    className="hover:text-white/60 transition-colors cursor-pointer"
                  >
                    {displayText}
                  </button>
                );
              })}
              {publishedSeoPages.length > 0 && <span className="text-white/10">•</span>}
            </div>
          </div>
        </div>
        )}
        
        <div className="border-t border-white/10 py-6 pb-24 md:pb-6">
          <div className="max-w-7xl mx-auto px-6 flex flex-col items-center gap-4 text-center">
            <p className="text-white/40 text-sm">{site?.footer_content?.copyrightText || `© ${new Date().getFullYear()} ${site?.site_name}. Tous droits réservés.`}</p>
            
            {/* Version Info - Dynamic from API */}
            <p className="text-white/20 text-xs">
              {deploymentInfo.version} - Déployé le {deploymentInfo.deployed_at}
            </p>
            
            {/* Legal Links */}
            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4 text-white/40 text-xs">
              {(site?.footer_content?.legalLinks || [
                { label: 'Mentions légales', slug: 'mentions-legales' },
                { label: 'CGV', slug: 'cgv' },
                { label: 'Politique de confidentialité', slug: 'politique-confidentialite' }
              ]).map((link, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="text-white/20 hidden md:inline">|</span>}
                  <button onClick={() => navigateToPage(link.slug)} className="hover:text-white transition-colors">{link.label}</button>
                </React.Fragment>
              ))}
            </div>
            
            {/* Admin Access */}
            <div className="flex items-center gap-4 mt-2">
              {/* In standalone mode, don't show SiteFlash badge */}
              {!standaloneMode && (
                <a 
                  href="/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/40 hover:text-white/70 text-xs transition-colors"
                >
                  <SiteFlashBadge color="muted" />
                </a>
              )}
              
              {/* Crédit Agence MSC */}
              <a 
                href="https://agence-msc.fr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-white/40 hover:text-white/70 text-xs transition-colors"
                title="Développé par Agence MSC"
              >
                <span>Développé par</span>
                <img 
                  src="https://customer-assets.emergentagent.com/job_e9b2c036-3caa-4216-8701-cf2d7a87f3a9/artifacts/8jg5k8rx_Logo-MSC-site-BLANC.png" 
                  alt="Agence MSC" 
                  className="h-5 opacity-60 hover:opacity-100 transition-opacity"
                />
              </a>
              
              {/* Bouton connexion Admin */}
              <button 
                onClick={() => window.location.href = '/admin/login'}
                className="flex items-center gap-1.5 text-white/20 hover:text-white/50 text-xs transition-colors group"
                title="Administration"
              >
                <FiLock className="h-3.5 w-3.5 group-hover:text-amber-400" />
                <span className="hidden md:inline">Admin</span>
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Action Button - Quick Actions Menu */}
      <div className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-40">
        <AnimatePresence>
          {showFloatingMenu && (
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} className="absolute bottom-16 right-0 mb-2 flex flex-col gap-3">
              {[
                { 
                  icon: FiPhone, 
                  label: 'Appeler', 
                  color: '#22c55e',
                  enabled: site?.site_settings?.floating_buttons?.call_enabled !== false, 
                  action: () => {
                    const phone = site?.contact_info?.phone || site?.footer_content?.phone || '';
                    if (phone) {
                      window.location.href = `tel:${phone.replace(/\s/g, '')}`;
                    }
                  }
                },
                { 
                  icon: FiMessageSquare, 
                  label: 'Demande de devis', 
                  color: '#3b82f6',
                  enabled: site?.site_settings?.floating_buttons?.quote_enabled !== false, 
                  action: () => navigateToPage('contact')
                },
                { 
                  icon: FiCalendar, 
                  label: 'Réserver en ligne', 
                  color: primaryColor,
                  enabled: site?.site_settings?.floating_buttons?.booking_enabled !== false, 
                  action: () => handleBookingClick() 
                },
              ].filter(item => item.enabled).map((item, i) => (
                <motion.button 
                  key={i} 
                  initial={{ opacity: 0, x: 20, scale: 0.8 }} 
                  animate={{ opacity: 1, x: 0, scale: 1 }} 
                  transition={{ delay: i * 0.08 }} 
                  onClick={() => { item.action(); setShowFloatingMenu(false); }} 
                  className="flex items-center gap-3 pl-4 pr-5 py-3 bg-white rounded-full shadow-xl text-sm font-semibold whitespace-nowrap hover:scale-105 transition-transform"
                  style={{ boxShadow: `0 4px 20px ${item.color}30` }}
                >
                  <span className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: item.color }}>
                    <item.icon className="h-4 w-4" />
                  </span>
                  <span className="text-gray-800">{item.label}</span>
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
        <motion.button 
          whileHover={{ scale: 1.1 }} 
          whileTap={{ scale: 0.9 }} 
          onClick={() => setShowFloatingMenu(!showFloatingMenu)} 
          className="w-14 h-14 md:w-16 md:h-16 rounded-full shadow-2xl flex items-center justify-center text-black relative"
          style={{ backgroundColor: primaryColor }}
        >
          <motion.div
            animate={{ rotate: showFloatingMenu ? 45 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <FiPlus className="h-7 w-7 md:h-8 md:w-8" />
          </motion.div>
        </motion.button>
      </div>

      {/* Scroll to top */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button 
            initial={{ opacity: 0, scale: 0.8 }} 
            animate={{ opacity: 1, scale: 1 }} 
            exit={{ opacity: 0, scale: 0.8 }} 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} 
            className="fixed bottom-20 md:bottom-6 left-4 md:left-6 z-[100] w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center text-black hover:scale-110 transition-transform shadow-lg"
            style={{ backgroundColor: primaryColor }}
            whileHover={{ y: -3 }}
          >
            <FiArrowUp className="h-5 w-5 md:h-6 md:w-6" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Booking Modal V2 - New simplified version */}
      <BookingModalV2 
        isOpen={showBooking} 
        onClose={handleCloseBooking} 
        primaryColor={primaryColor}
        siteId={site?.id}
        preSelectedProduct={selectedPrestationForBooking}
        preSelectedDuration={selectedDurationForBooking}
        preSelectedFormula={selectedFormulaForBooking}
      />
      
      {/* Callback Modal */}
      <AnimatePresence>{showCallbackModal && <CallbackModal />}</AnimatePresence>
      
      {/* Review Modal */}
      <AnimatePresence>{showReviewModal && <ReviewModal />}</AnimatePresence>
      
      {/* Client Login Modal - Optimized Component */}
      <ClientLoginModal
        isOpen={showClientLoginModal}
        onClose={() => setShowClientLoginModal(false)}
        onLoginSuccess={(user) => {
          setClientUser(user);
          setIsClientLoggedIn(true);
          navigate('/mon-compte');
        }}
        primaryColor={primaryColor}
      />
      
      {/* Photo Preview Modal */}
      <AnimatePresence>{showPhotoPreview && <PhotoPreviewModal />}</AnimatePresence>
      
      {/* Model Lightbox Modal with Navigation */}
      <AnimatePresence>
        {showModelLightbox && lightboxModel && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md flex items-center justify-center p-4" 
            onClick={() => setShowModelLightbox(false)}
          >
            {/* Previous Arrow */}
            {lightboxModels.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = lightboxModelIndex === 0 ? lightboxModels.length - 1 : lightboxModelIndex - 1;
                  setLightboxModelIndex(newIndex);
                  setLightboxModel(lightboxModels[newIndex]);
                }}
                className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 sm:w-14 sm:h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all hover:scale-110"
                data-testid="model-lightbox-prev"
              >
                <FiChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
              </button>
            )}
            
            {/* Next Arrow */}
            {lightboxModels.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = (lightboxModelIndex + 1) % lightboxModels.length;
                  setLightboxModelIndex(newIndex);
                  setLightboxModel(lightboxModels[newIndex]);
                }}
                className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 sm:w-14 sm:h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all hover:scale-110"
                data-testid="model-lightbox-next"
              >
                <FiChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
              </button>
            )}
            
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-5xl max-h-[90vh] w-full flex flex-col items-center"
              onClick={e => e.stopPropagation()}
            >
              {/* Close button */}
              <button 
                onClick={() => setShowModelLightbox(false)}
                className="absolute -top-12 right-0 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
              >
                <FiX className="h-6 w-6" />
              </button>
              
              {/* Image container */}
              <div className="relative w-full h-full flex items-center justify-center">
                <img 
                  src={lightboxModel.image}
                  alt={lightboxModel.title}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-2xl"
                  style={{ 
                    imageRendering: 'auto',
                    WebkitFontSmoothing: 'antialiased'
                  }}
                />
              </div>
              
              {/* Counter */}
              {lightboxModels.length > 1 && (
                <div className="mt-2 text-white/60 text-sm">
                  {lightboxModelIndex + 1} / {lightboxModels.length}
                </div>
              )}
              
              {/* Info panel */}
              <div className="mt-4 text-center">
                <h3 className="text-2xl font-bold text-white mb-2">{lightboxModel.title}</h3>
                {lightboxModel.category && (
                  <span className="inline-block px-4 py-1 rounded-full text-sm bg-white/10 text-white/80 mb-4">
                    {lightboxModel.category}
                  </span>
                )}
                <p className="text-white/60 max-w-lg mx-auto">
                  {lightboxModel.description || 'Modèle personnalisable pour vos événements'}
                </p>
                <button 
                  onClick={() => { setShowModelLightbox(false); handleBookingClick(); }}
                  className="mt-6 px-8 py-3 rounded-xl font-semibold text-black hover:scale-105 transition-transform"
                  style={{ backgroundColor: primaryColor }}
                >
                  Réserver avec ce modèle
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Gallery Lightbox with Navigation */}
      <AnimatePresence>
        {showGalleryLightbox && lightboxImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 p-4"
            onClick={() => setShowGalleryLightbox(false)}
          >
            {/* Previous Arrow */}
            {lightboxImages.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = lightboxIndex === 0 ? lightboxImages.length - 1 : lightboxIndex - 1;
                  setLightboxIndex(newIndex);
                  setLightboxImage(lightboxImages[newIndex]?.url);
                }}
                className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 sm:w-14 sm:h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all hover:scale-110"
                data-testid="lightbox-prev"
              >
                <FiChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
              </button>
            )}
            
            {/* Next Arrow */}
            {lightboxImages.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = (lightboxIndex + 1) % lightboxImages.length;
                  setLightboxIndex(newIndex);
                  setLightboxImage(lightboxImages[newIndex]?.url);
                }}
                className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 sm:w-14 sm:h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all hover:scale-110"
                data-testid="lightbox-next"
              >
                <FiChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
              </button>
            )}
            
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-4xl max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowGalleryLightbox(false)}
                className="absolute -top-12 right-0 text-white/80 hover:text-white p-2"
              >
                <FiX className="w-8 h-8" />
              </button>
              <img
                src={lightboxImage?.startsWith('http') ? lightboxImage : `${API_URL}${lightboxImage}`}
                alt="Gallery"
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
              
              {/* Counter */}
              {lightboxImages.length > 1 && (
                <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 text-white/60 text-sm">
                  {lightboxIndex + 1} / {lightboxImages.length}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Template Editor moved to back office - coming soon */}
    </div>
  );
};

export default PreviewPage;
